/**
 * UpgradeMenuManager - Manages the upgrade menu UI and all its tabs
 *
 * UI Architecture (UI-02):
 * - Uses system wrappers (UpgradeSystem, InventorySystem) for mutations
 * - Uses gameState directly for read-only queries
 * - Handles DOM rendering and user interaction only
 *
 * Systems used:
 * - UpgradeSystem: purchaseUpgrade()
 * - InventorySystem: equipItem(), unequipSlot(), scrapItem()
 *
 * Query methods (direct gameState access):
 * - getMiles(), getUpgradeCost(), getCarTier(), getInventoryItems(), etc.
 *
 * Handles:
 * - Main upgrade menu (showUpgradeMenu)
 * - Upgrades tab (car upgrades, ascension)
 * - Inventory tab (equipped items, all items)
 * - Waves tab (wave packs)
 * - Sub-screens (ascension, prestige, pack opening)
 */

import * as PrestigeSystem from '../game/PrestigeSystem.js';
import * as StatsFormatter from './StatsFormatter.js';
import { getTierStatMultiplier, TIER_STAT_MULTIPLIERS } from '../game/CarTierSystem.js';
import { WaveStateManager } from '../game/WaveStateManager.js';
import { GameState } from '../game/GameState.js';
import { getUIUpdateScheduler, getUpgradeSystem, getInventorySystem } from '../systems/index.js';
import { itemRegistry } from '../registries/ItemRegistry.js';

// Button styling handled by CSS classes in car-menu.css:
// .cf-wave-btn, .cf-wave-scrap-btn, .cf-item-upgrade-btn, etc.
// with .can-afford and .cannot-afford modifiers

export class UpgradeMenuManager {
    constructor(gameUI, gameState) {
        this.gameUI = gameUI;
        this.gameState = gameState;
        this._upgradeMenuUpdateId = 'upgrade-menu-update';
        // Load sort preferences from localStorage, default to 'recent'
        this.inventorySortBy = localStorage.getItem('inventorySortBy') || 'recent';
        this.wavesSortBy = localStorage.getItem('wavesSortBy') || 'recent';
        this.menuMilesDisplay = null; // Reference to miles display in upgrade menu
        this.orientationChangeHandler = null; // Handler for orientation changes

        // Stat calculator subscription for live updates
        this.statCalcUnsubscribe = null;

        // References for surgical UI updates
        this.upgradeStatElements = new Map(); // type -> { valueEl, iconEl }
        this.currentTab = 'upgrades';
        this.upgradesContentEl = null;
        this.overlayEl = null;

        // Tier upgrade modal state
        this.tierModalActive = false;
        this.pendingTierUpgrade = null; // { oldTier, newTier, oldMultiplier, newMultiplier }

        // References for live refresh
        this.wavesContentEl = null;
        this.inventoryContentEl = null;

        // Multi-select for bulk wave scrapping
        this.selectedWaves = new Set(); // Set of milestone numbers
        this.bulkScrapHeaderEl = null;

        // Multi-select for bulk item scrapping
        this.selectedItems = new Set(); // Set of item IDs
        this.itemBulkScrapHeaderEl = null;

        // Wave opening celebration state
        this.waveOpeningInProgress = false;

        // Mile counter animation state
        this.mileAnimator = {
            element: null,
            displayedValue: 0,
            targetValue: 0,
            isAnimating: false,
            animationFrame: null,
            soundLoopId: null
        };

        // Wavedust counter animation state
        this.wavedustAnimator = {
            element: null,
            displayedValue: 0,
            targetValue: 0,
            isAnimating: false,
            animationFrame: null,
            soundLoopId: null
        };
    }

    /**
     * Get app reference (for itemsData, packGenerator, equipmentManager)
     */
    get app() {
        return this.gameUI.app;
    }

    /**
     * Get SFX manager reference
     */
    get sfxManager() {
        return this.gameUI.sfxManager;
    }

    /**
     * Get particles system
     */
    get particles() {
        return this.gameUI.particles;
    }

    /**
     * Get UpgradeSystem singleton (UI-02: use system wrappers for mutations)
     * Lazy initialization via getter - systems initialized in main.js
     * @returns {UpgradeSystem|null}
     */
    get upgradeSystem() {
        return getUpgradeSystem();
    }

    /**
     * Get InventorySystem singleton (UI-02: use system wrappers for mutations)
     * Lazy initialization via getter - systems initialized in main.js
     * @returns {InventorySystem|null}
     */
    get inventorySystem() {
        return getInventorySystem();
    }

    // ========== DOM Helpers ==========

    /**
     * Clear all children from an element (safe alternative to innerHTML = '')
     * @param {HTMLElement} el - Element to clear
     */
    _clearChildren(el) {
        while (el.firstChild) el.removeChild(el.firstChild);
    }

    /**
     * Create an element with optional classes and text content
     * @param {string} tag - HTML tag name
     * @param {string|string[]} [className] - CSS class(es)
     * @param {string} [text] - Text content
     * @returns {HTMLElement}
     */
    _createElement(tag, className, text) {
        const el = document.createElement(tag);
        if (className) {
            if (Array.isArray(className)) {
                el.classList.add(...className);
            } else {
                el.className = className;
            }
        }
        if (text !== undefined) {
            el.textContent = text;
        }
        return el;
    }

    /**
     * Create a DocumentFragment from an array of elements
     * @param {HTMLElement[]} elements - Array of elements to add
     * @returns {DocumentFragment}
     */
    _createFragment(elements) {
        const fragment = document.createDocumentFragment();
        elements.forEach(el => fragment.appendChild(el));
        return fragment;
    }

    // ========== Mile Counter Animation ==========

    /**
     * Start animating mile counter toward target value
     */
    _startMileAnimation() {
        if (this.mileAnimator.isAnimating) return;

        // Don't animate if the upgrade menu isn't visible
        if (!document.getElementById('upgrade-menu')) return;

        this.mileAnimator.isAnimating = true;

        // Stop any existing sound loop first (safety)
        if (this.mileAnimator.soundLoopId) {
            this.sfxManager?.stopSoundLoop(this.mileAnimator.soundLoopId);
            this.mileAnimator.soundLoopId = null;
        }

        // Start pip sound loop via SFXManager (fixed 80ms interval)
        this.mileAnimator.soundLoopId = this.sfxManager?.startSoundLoop('pip', 80, { volume: 0.7 });

        this._animateMileCounter();
    }

    /**
     * Animate mile counter value toward target
     */
    _animateMileCounter() {
        // Safety: stop if menu was closed during animation
        if (!document.getElementById('upgrade-menu')) {
            this._stopMileAnimation();
            return;
        }

        const animator = this.mileAnimator;
        const diff = animator.targetValue - animator.displayedValue;

        if (Math.abs(diff) < 1) {
            // Reached target
            animator.displayedValue = animator.targetValue;
            this._stopMileAnimation();
            this._updateMileDisplay();
            return;
        }

        // Lerp: faster for big differences, slower as approaching
        const step = Math.max(1, Math.ceil(Math.abs(diff) * 0.12));
        animator.displayedValue += diff > 0 ? step : -step;

        this._updateMileDisplay();
        animator.animationFrame = requestAnimationFrame(() => this._animateMileCounter());
    }

    /**
     * Stop mile counter animation
     */
    _stopMileAnimation() {
        const animator = this.mileAnimator;
        animator.isAnimating = false;

        // Stop sound loop via SFXManager
        this.sfxManager?.stopSoundLoop(animator.soundLoopId);
        animator.soundLoopId = null;

        if (animator.animationFrame) {
            cancelAnimationFrame(animator.animationFrame);
            animator.animationFrame = null;
        }
    }

    /**
     * Update the miles display element with current animated value
     */
    _updateMileDisplay() {
        if (this.mileAnimator.element) {
            this.mileAnimator.element.textContent =
                `${Math.floor(this.mileAnimator.displayedValue).toLocaleString()} MI`;
        }
    }

    // ========== Wavedust Counter Animation ==========

    /**
     * Start animating wavedust counter toward target value
     */
    _startWavedustAnimation() {
        if (this.wavedustAnimator.isAnimating) return;

        // Don't animate if the upgrade menu isn't visible
        if (!document.getElementById('upgrade-menu')) return;

        this.wavedustAnimator.isAnimating = true;

        // Stop any existing sound loop first (safety)
        if (this.wavedustAnimator.soundLoopId) {
            this.sfxManager?.stopSoundLoop(this.wavedustAnimator.soundLoopId);
            this.wavedustAnimator.soundLoopId = null;
        }

        // Start pip sound loop via SFXManager (fixed 80ms interval)
        this.wavedustAnimator.soundLoopId = this.sfxManager?.startSoundLoop('pip', 80, { volume: 0.7 });

        this._animateWavedustCounter();
    }

    /**
     * Animate wavedust counter value toward target
     */
    _animateWavedustCounter() {
        // Safety: stop if menu was closed during animation
        if (!document.getElementById('upgrade-menu')) {
            this._stopWavedustAnimation();
            return;
        }

        const animator = this.wavedustAnimator;
        const diff = animator.targetValue - animator.displayedValue;

        if (Math.abs(diff) < 1) {
            // Reached target
            animator.displayedValue = animator.targetValue;
            this._stopWavedustAnimation();
            this._updateWavedustDisplay();
            return;
        }

        // Lerp: faster for big differences, slower as approaching
        const step = Math.max(1, Math.ceil(Math.abs(diff) * 0.12));
        animator.displayedValue += diff > 0 ? step : -step;

        this._updateWavedustDisplay();
        animator.animationFrame = requestAnimationFrame(() => this._animateWavedustCounter());
    }

    /**
     * Stop wavedust counter animation
     */
    _stopWavedustAnimation() {
        const animator = this.wavedustAnimator;
        animator.isAnimating = false;

        // Stop sound loop via SFXManager
        this.sfxManager?.stopSoundLoop(animator.soundLoopId);
        animator.soundLoopId = null;

        if (animator.animationFrame) {
            cancelAnimationFrame(animator.animationFrame);
            animator.animationFrame = null;
        }
    }

    /**
     * Update the wavedust display element with current animated value
     */
    _updateWavedustDisplay() {
        if (this.wavedustAnimator.element) {
            this.wavedustAnimator.element.textContent =
                `${Math.floor(this.wavedustAnimator.displayedValue).toLocaleString()} ⚙`;
        }
    }

    /**
     * Show the main upgrade menu
     * @param {number} restoreScrollPos - Scroll position to restore
     */
    show(restoreScrollPos = 0) {
        // Create upgrade menu overlay
        if (document.getElementById('upgrade-menu')) {
            return;
        }

        // Warm up particle system to avoid first-use stutter
        this.particles?.warmUp();

        // Reset current tab to upgrades (default tab)
        this.currentTab = 'upgrades';

        // Detect if this is a phone screen (for full-screen takeover)
        const isPhone = this.gameUI.isPhoneScreen();

        const overlay = document.createElement('div');
        overlay.id = 'upgrade-menu';
        overlay.className = `cf-ui cf-car-overlay${isPhone ? ' cf-phone' : ''}`;

        const menu = document.createElement('div');
        menu.className = 'cf-car-modal';

        // Build header with tabs
        const { header, tabBar, upgradesTab, inventoryTab, wavesTab, collectionTab, milesDisplay, wavedustDisplay } =
            this._buildHeader(overlay);
        menu.appendChild(header);

        // Store display references for particle effects
        this.menuMilesDisplay = milesDisplay;
        this.menuWavedustDisplay = wavedustDisplay;

        // Initialize mile animator with current value
        const currentMiles = Math.floor(this.gameState.getMiles());
        this.mileAnimator.element = milesDisplay;
        this.mileAnimator.displayedValue = currentMiles;
        this.mileAnimator.targetValue = currentMiles;

        // Initialize wavedust animator with current value
        const currentWavedust = this.gameState.getWavedust();
        this.wavedustAnimator.element = wavedustDisplay;
        this.wavedustAnimator.displayedValue = currentWavedust;
        this.wavedustAnimator.targetValue = currentWavedust;

        // Create upgrades wrapper (contains car info banner + scrollable upgrades list)
        const upgradesWrapper = document.createElement('div');
        upgradesWrapper.id = 'upgrades-wrapper';
        upgradesWrapper.className = 'cf-car-content';

        // Build car info banner (prominent tier progress display)
        const carInfoBanner = this._buildCarInfoBanner();
        carInfoBanner.id = 'car-info-banner';
        upgradesWrapper.appendChild(carInfoBanner);

        // Upgrades scrollable content (fills remaining space in wrapper)
        const upgradesContent = document.createElement('div');
        upgradesContent.id = 'upgrades-content';
        upgradesContent.className = 'cf-car-scrollable';
        upgradesWrapper.appendChild(upgradesContent);

        // Inventory/Dash container
        const inventoryContent = document.createElement('div');
        inventoryContent.id = 'inventory-content';
        inventoryContent.className = 'cf-car-content';
        inventoryContent.style.display = 'none';

        // Waves container
        const wavesContent = document.createElement('div');
        wavesContent.id = 'waves-content';
        wavesContent.className = 'cf-car-scrollable';
        wavesContent.style.display = 'none';

        // Collection container
        const collectionContent = document.createElement('div');
        collectionContent.id = 'collection-content';
        collectionContent.className = 'cf-car-scrollable';
        collectionContent.style.display = 'none';

        // Tab switching logic
        const switchTab = (activeTab) => {
            // Reset all tabs to inactive state
            [upgradesTab, inventoryTab, wavesTab, collectionTab].forEach(tab => {
                tab.classList.remove('active');
            });

            // Hide all content wrappers
            upgradesWrapper.style.display = 'none';
            inventoryContent.style.display = 'none';
            wavesContent.style.display = 'none';
            collectionContent.style.display = 'none';

            // Hide any lingering tooltips from collection page
            document.querySelectorAll('.cf-collection-tooltip').forEach(t => t.style.display = 'none');
            document.querySelectorAll('.cf-milestone-tooltip').forEach(t => t.style.display = 'none');

            // Activate selected tab
            const activeTabEl = activeTab === 'upgrades' ? upgradesTab :
                               activeTab === 'inventory' ? inventoryTab :
                               activeTab === 'waves' ? wavesTab : collectionTab;
            const activeContent = activeTab === 'upgrades' ? upgradesWrapper :
                                 activeTab === 'inventory' ? inventoryContent :
                                 activeTab === 'waves' ? wavesContent : collectionContent;

            activeTabEl.classList.add('active');
            // Flash animation on tab switch
            activeTabEl.classList.add('cf-flash');
            setTimeout(() => activeTabEl.classList.remove('cf-flash'), 300);
            // All tabs use flex layout for consistent height
            activeContent.style.display = 'flex';
        };

        upgradesTab.addEventListener('click', () => switchTab('upgrades'));
        inventoryTab.addEventListener('click', () => switchTab('inventory'));
        wavesTab.addEventListener('click', () => switchTab('waves'));
        collectionTab.addEventListener('click', () => switchTab('collection'));

        // Store references for stat updates and live refresh
        this.overlayEl = overlay;
        this.upgradesContentEl = upgradesContent;
        this.wavesContentEl = wavesContent;
        this.inventoryContentEl = inventoryContent;
        this.collectionContentEl = collectionContent;
        this.upgradeStatElements.clear();

        // Build upgrades content
        const upgradeButtons = this._buildUpgradesTab(upgradesContent, overlay, milesDisplay);

        // Build inventory content
        this._buildInventoryContent(inventoryContent, overlay);

        // Build waves content
        this._buildWavesContent(wavesContent, overlay);

        // Build collection content
        this._buildCollectionContent(collectionContent, overlay);

        // Hide collection tooltips on click outside items/milestones (added once, persists across rebuilds)
        collectionContent.addEventListener('click', (e) => {
            const isItem = e.target.closest('.cf-collection-item');
            const isMilestone = e.target.closest('.cf-milestone-marker');
            const isTooltip = e.target.closest('.cf-collection-tooltip, .cf-milestone-tooltip');
            if (!isItem && !isMilestone && !isTooltip) {
                document.querySelectorAll('.cf-collection-tooltip').forEach(t => t.style.display = 'none');
                document.querySelectorAll('.cf-milestone-tooltip').forEach(t => t.style.display = 'none');
            }
        });

        // Track current tab for updates
        const originalSwitchTab = switchTab;
        const trackingSwitchTab = (tab) => {
            // When leaving waves tab, mark waves as seen and hide badges
            if (this.currentTab === 'waves' && tab !== 'waves') {
                this.gameState.markAllWavesSeen();
                this._hideWaveBadges();
                // Re-enable badge updates now that we've left waves tab
                this.gameUI.suppressWaveBadgeUpdates = false;
            }

            // When leaving inventory tab, mark items as seen and rebuild for next visit
            if (this.currentTab === 'inventory' && tab !== 'inventory') {
                this.gameState.markAllItemsSeen();
                this._hideDashBadges();
                // Re-enable badge updates now that we've left inventory tab
                this.gameUI.suppressItemBadgeUpdates = false;
                // Rebuild inventory without NEW badges (for when user returns)
                if (this.inventoryContentEl && this.overlayEl) {
                    this._clearChildren(this.inventoryContentEl);
                    this._buildInventoryContent(this.inventoryContentEl, this.overlayEl);
                }
            }

            // When leaving collection tab, mark discoveries as seen and rebuild for next visit
            if (this.currentTab === 'collection' && tab !== 'collection') {
                this.gameState.markAllCollectionSeen();
                this._hideCollectionBadges();
                // Rebuild collection without NEW badges (for when user returns)
                if (this.collectionContentEl && this.overlayEl) {
                    this._clearChildren(this.collectionContentEl);
                    this._buildCollectionContent(this.collectionContentEl, this.overlayEl);
                }
            }

            this.currentTab = tab;
            originalSwitchTab(tab);

            // When entering inventory tab, just hide the tab badge and suppress updates
            // Don't mark items as seen yet - user should see NEW badges while viewing
            if (tab === 'inventory') {
                this._checkInventoryRebuild?.();
                // Hide tab badge when entering inventory (user is now viewing items)
                this._hideDashTabBadge();
                // Suppress badge updates while viewing inventory tab
                this.gameUI.suppressItemBadgeUpdates = true;
            }

            // When entering waves tab, hide the tab/menu badges but keep individual wave badges visible
            if (tab === 'waves') {
                this._hideWaveTabBadges();
                // Suppress badge updates while viewing waves tab
                this.gameUI.suppressWaveBadgeUpdates = true;
            }

            // When entering collection tab, hide badge and rebuild content to refresh found state
            if (tab === 'collection') {
                this._hideCollectionTabBadge();
                if (this.collectionContentEl && this.overlayEl) {
                    this._clearChildren(this.collectionContentEl);
                    this._buildCollectionContent(this.collectionContentEl, this.overlayEl);
                }
            }
        };
        upgradesTab.onclick = () => trackingSwitchTab('upgrades');
        inventoryTab.onclick = () => trackingSwitchTab('inventory');
        wavesTab.onclick = () => trackingSwitchTab('waves');
        collectionTab.onclick = () => trackingSwitchTab('collection');

        // Subscribe to stat calculator changes for live updates
        if (this.gameState.statCalculator) {
            this.statCalcUnsubscribe = this.gameState.statCalculator.subscribe(() => {
                this._onStatsInvalidated(upgradesContent, inventoryContent, overlay, milesDisplay);
            });
        }

        // Add tab contents to menu
        menu.appendChild(upgradesWrapper);
        menu.appendChild(inventoryContent);
        menu.appendChild(wavesContent);
        menu.appendChild(collectionContent);

        // Orientation change handler - rebuild inventory on rotation
        let lastIsLandscape = this.gameUI.isLandscape();
        let inventoryNeedsRebuild = false;
        let resizeTimeout = null;
        this.orientationChangeHandler = () => {
            // Debounce and only rebuild if orientation actually changed
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                const currentIsLandscape = this.gameUI.isLandscape();
                if (currentIsLandscape !== lastIsLandscape) {
                    lastIsLandscape = currentIsLandscape;
                    // Rebuild inventory content if visible, otherwise flag for rebuild on tab switch
                    // Use currentTab check since display style can be unreliable
                    if (this.currentTab === 'inventory') {
                        this._rebuildInventory(inventoryContent, overlay);
                    } else {
                        inventoryNeedsRebuild = true;
                    }
                }
            }, 150);
        };
        window.addEventListener('resize', this.orientationChangeHandler);

        // Store rebuild flag checker for tab switching
        this._checkInventoryRebuild = () => {
            if (inventoryNeedsRebuild) {
                inventoryNeedsRebuild = false;
                this._rebuildInventory(inventoryContent, overlay);
            }
        };

        // Close button
        const closeButton = this._createCloseButton(() => {
            this._cleanupMenu(overlay);
        });
        menu.appendChild(closeButton);

        overlay.appendChild(menu);
        document.body.appendChild(overlay);

        // Restore scroll position if provided
        if (restoreScrollPos > 0) {
            upgradesContent.scrollTop = restoreScrollPos;
        }

        // Close on overlay click
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                this._cleanupMenu(overlay);
            }
        });

        // Set up dynamic updates
        this._setupDynamicUpdates(milesDisplay, upgradeButtons);
    }

    /**
     * Clean up menu resources when closing
     */
    _cleanupMenu(overlay) {
        // If closing while on waves tab, mark waves as seen
        if (this.currentTab === 'waves') {
            this.gameState.markAllWavesSeen();
        }
        // If closing while on inventory tab, mark items as seen
        if (this.currentTab === 'inventory') {
            this.gameState.markAllItemsSeen();
        }

        // Re-enable badge updates
        this.gameUI.suppressWaveBadgeUpdates = false;
        this.gameUI.suppressItemBadgeUpdates = false;

        // Unregister from UIUpdateScheduler (Phase 6 timer consolidation)
        const scheduler = getUIUpdateScheduler();
        scheduler.unregister(this._upgradeMenuUpdateId);

        // Stop mile counter animation
        this._stopMileAnimation();
        this.mileAnimator.element = null;

        // Stop wavedust counter animation
        this._stopWavedustAnimation();
        this.wavedustAnimator.element = null;

        if (this.orientationChangeHandler) {
            window.removeEventListener('resize', this.orientationChangeHandler);
            this.orientationChangeHandler = null;
        }
        if (this.statCalcUnsubscribe) {
            this.statCalcUnsubscribe();
            this.statCalcUnsubscribe = null;
        }
        if (this.sortDropdownClickHandler) {
            document.removeEventListener('click', this.sortDropdownClickHandler);
            this.sortDropdownClickHandler = null;
        }
        if (this.wavesSortDropdownClickHandler) {
            document.removeEventListener('click', this.wavesSortDropdownClickHandler);
            this.wavesSortDropdownClickHandler = null;
        }
        this.menuMilesDisplay = null;
        this.upgradeStatElements.clear();
        this.overlayEl = null;
        this.upgradesContentEl = null;
        this.wavesContentEl = null;
        this.inventoryContentEl = null;
        this.currentTab = null;
        this.selectedWaves.clear();
        this.bulkScrapHeaderEl = null;
        this._bulkScrapDomCache = null; // Clear DOM cache
        if (this._pendingHeaderUpdate) {
            cancelAnimationFrame(this._pendingHeaderUpdate);
            this._pendingHeaderUpdate = null;
        }

        // Update menu badge to reflect cleared unseen items
        this._updateMenuBadge();

        document.querySelectorAll('.upgrade-tooltip').forEach(t => t.remove());
        document.querySelectorAll('.cf-car-desc-tooltip').forEach(t => t.remove());
        document.querySelectorAll('.cf-item-tooltip').forEach(t => t.remove());
        document.querySelectorAll('.cf-collection-tooltip').forEach(t => t.remove());
        document.querySelectorAll('.cf-milestone-tooltip').forEach(t => t.remove());

        // Clean up any running particle animations
        if (this.particles) {
            this.particles.cleanup();
        }

        overlay.remove();
    }

    /**
     * Force close the upgrade menu if it's open
     * Called when entering offline simulation mode to ensure clean state
     * @returns {boolean} True if menu was open and closed, false otherwise
     */
    forceClose() {
        if (this.overlayEl) {
            console.log('[UpgradeMenuManager] Force closing menu');
            this._cleanupMenu(this.overlayEl);
            return true;
        }
        return false;
    }

    /**
     * Check if the upgrade menu is currently open
     * @returns {boolean}
     */
    isOpen() {
        return this.overlayEl !== null;
    }

    /**
     * Hide waves tab badge and update menu button badge
     */
    _hideWaveTabBadges() {
        // Hide waves tab badge
        const wavesBadge = document.getElementById('waves-badge');
        if (wavesBadge) {
            wavesBadge.style.display = 'none';
        }

        // Update main menu button badge (stays visible if there are unseen items)
        this._updateMenuBadge();
    }

    /**
     * Hide all wave notification badges (waves tab, menu button, individual waves)
     */
    _hideWaveBadges() {
        this._hideWaveTabBadges();

        // Hide individual wave badges
        document.querySelectorAll('.wave-new-badge').forEach(badge => {
            badge.style.display = 'none';
        });
    }

    /**
     * Hide DASH tab badge only
     */
    _hideDashTabBadge() {
        const dashBadge = document.getElementById('dash-badge');
        if (dashBadge) {
            dashBadge.style.display = 'none';
        }
    }

    /**
     * Update main CAR menu button badge based on unseen waves, items, and collection
     */
    _updateMenuBadge() {
        if (!this.gameUI?.elements?.packNotificationBadge) return;

        const hasUnseenWaves = this.gameState.getUnseenWaveCount() > 0;
        const hasUnseenItems = this.gameState.getUnseenItemCount() > 0;
        const hasUnseenCollection = this.gameState.getUnseenCollectionCount() > 0;
        const hasNewContent = hasUnseenWaves || hasUnseenItems || hasUnseenCollection;

        this.gameUI.elements.packNotificationBadge.style.display = hasNewContent ? 'block' : 'none';
    }

    /**
     * Hide all dash notification badges (tab and menu button)
     */
    _hideDashBadges() {
        this._hideDashTabBadge();
        // Update menu button badge (checks waves, items, and collection)
        this._updateMenuBadge();
    }

    /**
     * Hide COLLECTION tab badge only
     */
    _hideCollectionTabBadge() {
        const collectionBadge = document.getElementById('collection-badge');
        if (collectionBadge) {
            collectionBadge.style.display = 'none';
        }
    }

    /**
     * Hide all collection notification badges (tab and menu button)
     */
    _hideCollectionBadges() {
        this._hideCollectionTabBadge();
        // Hide individual item badges
        document.querySelectorAll('.collection-new-badge').forEach(badge => {
            badge.style.display = 'none';
        });
        // Update menu button badge (checks waves, items, and collection)
        this._updateMenuBadge();
    }

    /**
     * Refresh waves content when new waves appear while menu is open
     */
    refreshWavesContent() {
        // Only refresh if menu is open and we have references
        if (!this.wavesContentEl || !this.overlayEl) return;
        if (!document.getElementById('upgrade-menu')) return;

        // Clear DOM cache since we're rebuilding
        this._bulkScrapDomCache = null;

        // Clean up selections for waves that no longer exist
        const availableMilestones = new Set(
            this.gameState.getAvailablePacks().map(p => p.milestone)
        );
        for (const milestone of this.selectedWaves) {
            if (!availableMilestones.has(milestone)) {
                this.selectedWaves.delete(milestone);
            }
        }

        // Rebuild waves content
        const scrollPos = this.wavesContentEl.scrollTop;
        this._clearChildren(this.wavesContentEl);
        this._buildWavesContent(this.wavesContentEl, this.overlayEl);
        this.wavesContentEl.scrollTop = scrollPos;

        // Update waves tab badge
        const wavesBadge = document.getElementById('waves-badge');
        if (wavesBadge) {
            const hasUnseenWaves = this.gameState.getUnseenWaveCount() > 0;
            wavesBadge.style.display = hasUnseenWaves ? 'block' : 'none';
        }
    }

    /**
     * Refresh inventory content when new items appear while menu is open
     */
    refreshInventoryContent() {
        // Only refresh if menu is open and we have references
        if (!this.inventoryContentEl || !this.overlayEl) return;
        if (!document.getElementById('upgrade-menu')) return;

        // Rebuild inventory content
        const scrollPos = this.inventoryContentEl.scrollTop;
        this._clearChildren(this.inventoryContentEl);
        this._buildInventoryContent(this.inventoryContentEl, this.overlayEl);
        this.inventoryContentEl.scrollTop = scrollPos;

        // Update dash tab badge (only if not suppressed)
        if (!this.gameUI.suppressItemBadgeUpdates) {
            const dashBadge = document.getElementById('dash-badge');
            if (dashBadge) {
                const hasUnseenItems = this.gameState.getUnseenItemCount() > 0;
                dashBadge.style.display = hasUnseenItems ? 'block' : 'none';
            }
        }
    }

    /**
     * Handle stat calculator invalidation - refresh relevant UI
     */
    _onStatsInvalidated(upgradesContent, inventoryContent, overlay, milesDisplay) {
        // Only refresh if menu is still open
        if (!document.getElementById('upgrade-menu')) {
            return;
        }

        // Skip rebuild during particle animation to avoid flicker
        if (this._animationInProgress) {
            return;
        }

        // Refresh upgrades tab content (rebuild to update stat values and info icons)
        if (upgradesContent) {
            const scrollPos = upgradesContent.scrollTop;
            this._clearChildren(upgradesContent);
            document.querySelectorAll('.upgrade-tooltip').forEach(t => t.remove());
            const upgradeButtons = this._buildUpgradesTab(upgradesContent, overlay, milesDisplay);
            upgradesContent.scrollTop = scrollPos;

            // Re-setup dynamic updates with new buttons (unregister old first)
            const scheduler = getUIUpdateScheduler();
            scheduler.unregister(this._upgradeMenuUpdateId);
            this._setupDynamicUpdates(milesDisplay, upgradeButtons);
        }

        // Refresh inventory tab if visible (to update item effect displays)
        if (inventoryContent && inventoryContent.style.display !== 'none') {
            this._rebuildInventory(inventoryContent, overlay);
        }
    }

    /**
     * Build header section with tabs and car info
     */
    _buildHeader(overlay) {
        const header = document.createElement('div');
        header.className = 'cf-car-header';

        // Tab bar with dot separators
        const tabBar = document.createElement('nav');
        tabBar.className = 'cf-car-tabs';

        const upgradesTab = document.createElement('button');
        upgradesTab.textContent = 'UPGRADES';
        upgradesTab.className = 'cf-car-tab active';

        const dot1 = document.createElement('span');
        dot1.className = 'cf-car-tab-dot';

        const inventoryTab = document.createElement('button');
        inventoryTab.textContent = 'DASH';
        inventoryTab.className = 'cf-car-tab';
        inventoryTab.style.position = 'relative';

        // NEW badge on dash tab (only for unseen items)
        const hasUnseenItems = this.gameState.getUnseenItemCount() > 0;
        const dashBadge = document.createElement('div');
        dashBadge.id = 'dash-badge';
        dashBadge.className = 'cf-new-badge cf-new-badge--tab';
        dashBadge.textContent = 'NEW';
        dashBadge.style.display = hasUnseenItems ? 'block' : 'none';
        inventoryTab.appendChild(dashBadge);

        const dot2 = document.createElement('span');
        dot2.className = 'cf-car-tab-dot';

        const wavesTab = document.createElement('button');
        wavesTab.textContent = 'PICKUPS';
        wavesTab.className = 'cf-car-tab';
        wavesTab.style.position = 'relative';

        // NEW badge on waves tab (only for unseen waves)
        const hasUnseenWaves = this.gameState.getUnseenWaveCount() > 0;
        const wavesBadge = document.createElement('div');
        wavesBadge.id = 'waves-badge';
        wavesBadge.className = 'cf-new-badge cf-new-badge--tab';
        wavesBadge.textContent = 'NEW';
        wavesBadge.style.display = hasUnseenWaves ? 'block' : 'none';
        wavesTab.appendChild(wavesBadge);

        const dot3 = document.createElement('span');
        dot3.className = 'cf-car-tab-dot';

        const collectionTab = document.createElement('button');
        collectionTab.textContent = 'COLLECTION';
        collectionTab.className = 'cf-car-tab';
        collectionTab.style.position = 'relative';

        // NEW badge on collection tab (only for unseen discoveries)
        const hasUnseenCollection = this.gameState.getUnseenCollectionCount() > 0;
        const collectionBadge = document.createElement('div');
        collectionBadge.id = 'collection-badge';
        collectionBadge.className = 'cf-new-badge cf-new-badge--tab';
        collectionBadge.textContent = 'NEW';
        collectionBadge.style.display = hasUnseenCollection ? 'block' : 'none';
        collectionTab.appendChild(collectionBadge);

        tabBar.appendChild(upgradesTab);
        tabBar.appendChild(dot1);
        tabBar.appendChild(inventoryTab);
        tabBar.appendChild(dot2);
        tabBar.appendChild(wavesTab);
        tabBar.appendChild(dot3);
        tabBar.appendChild(collectionTab);
        header.appendChild(tabBar);

        // Right header section
        const rightHeader = this._buildRightHeader();
        header.appendChild(rightHeader.container);

        return {
            header,
            tabBar,
            upgradesTab,
            inventoryTab,
            wavesTab,
            collectionTab,
            milesDisplay: rightHeader.milesDisplay,
            wavedustDisplay: rightHeader.wavedustDisplay
        };
    }

    /**
     * Build right side of header (miles display + wavedust + total miles)
     */
    _buildRightHeader() {
        const rightHeader = document.createElement('div');
        rightHeader.className = 'cf-car-miles';

        // Miles display (current spendable)
        const milesDisplay = document.createElement('div');
        milesDisplay.className = 'cf-car-miles-current';
        milesDisplay.textContent = `${Math.floor(this.gameState.getMiles()).toLocaleString()} MI`;
        rightHeader.appendChild(milesDisplay);

        // Wavedust display (for quality fusion)
        const wavedust = this.gameState.getWavedust();
        const wavedustDisplay = document.createElement('div');
        wavedustDisplay.className = 'cf-car-wavedust';
        wavedustDisplay.textContent = `${wavedust.toLocaleString()} ⚙`;
        wavedustDisplay.style.cssText = `
            font-size: 14px;
            color: #a78bfa;
            text-align: right;
            margin-top: 2px;
        `;
        rightHeader.appendChild(wavedustDisplay);

        // Lifetime miles (small, underneath) - never resets, used for leaderboards
        const lifetimeMiles = this.gameState.getLifetimeMiles();
        const totalDisplay = document.createElement('div');
        totalDisplay.className = 'cf-car-miles-total';
        totalDisplay.textContent = `TOTAL: ${Math.floor(lifetimeMiles).toLocaleString()} MI`;
        rightHeader.appendChild(totalDisplay);

        return { container: rightHeader, milesDisplay, wavedustDisplay };
    }

    /**
     * Build compact car info banner with tier progress
     * Responsive: horizontal on desktop, stacked on mobile
     */
    _buildCarInfoBanner() {
        const currentCarId = this.gameState.state.currentCarId;
        const carConfig = this.gameState.carsConfig[currentCarId];
        const carTier = this.gameState.getCarTier();
        const tierColor = this.gameState.getTierColor();
        const tierProgress = this.gameState.getCarTierProgress();
        const prestigeInfo = this.gameState.getPrestigeInfo();

        // Main banner container
        const banner = document.createElement('div');
        banner.className = 'cf-car-banner';

        // Top row: Car name (left) and Prestige (right)
        const topRow = document.createElement('div');
        topRow.className = 'cf-car-banner-top';

        const carTitle = document.createElement('div');
        carTitle.className = 'cf-car-name';
        carTitle.textContent = carConfig.name.toUpperCase();
        topRow.appendChild(carTitle);

        // Prestige badge (if applicable) - with tooltip showing bonuses
        if (prestigeInfo.level > 0) {
            const prestigeTierColor = PrestigeSystem.getPrestigeTierColor(prestigeInfo.level);
            const prestigeBadge = document.createElement('div');
            prestigeBadge.className = 'cf-car-prestige';
            prestigeBadge.textContent = `PRESTIGE ${prestigeInfo.level}`;
            prestigeBadge.style.color = prestigeTierColor;
            prestigeBadge.style.borderColor = prestigeTierColor;
            prestigeBadge.style.userSelect = 'none';
            prestigeBadge.style.webkitUserSelect = 'none';
            topRow.appendChild(prestigeBadge);

            // Add tooltip showing prestige bonuses
            this._createPrestigeTooltip(prestigeBadge, prestigeInfo);
        }

        banner.appendChild(topRow);

        // Tier progress meter (segmented style)
        const tierMeter = document.createElement('div');
        tierMeter.id = 'tier-progress-container';
        tierMeter.className = 'cf-car-tier-meter';
        tierMeter.dataset.tierColor = tierColor; // Store for animation use

        // Create 10 segments with proper fill calculation
        const progress = tierProgress.progress; // 0.0 to 1.0
        for (let i = 0; i < 10; i++) {
            const segment = document.createElement('div');
            segment.className = 'cf-car-tier-segment';

            const segmentStart = i * 0.1;
            const segmentEnd = (i + 1) * 0.1;

            if (progress >= segmentEnd) {
                // Fully filled segment
                segment.classList.add('filled');
                segment.style.background = tierColor;
            } else if (progress > segmentStart) {
                // Partially filled (active/leading segment)
                segment.classList.add('filled', 'active');
                const segmentFill = (progress - segmentStart) / 0.1;
                const opacity = 0.15 + (segmentFill * 0.85); // 15% to 100%
                segment.style.background = tierColor;
                segment.style.opacity = opacity;
            }
            // else: empty segment uses default CSS background

            tierMeter.appendChild(segment);
        }

        banner.appendChild(tierMeter);

        // Tier labels row
        const tierLabels = document.createElement('div');
        tierLabels.className = 'cf-car-tier-labels';

        // Current tier badge
        const currentTierBadge = document.createElement('span');
        currentTierBadge.id = 'car-tier-badge';
        currentTierBadge.className = 'cf-car-tier-current';
        currentTierBadge.textContent = carTier.toUpperCase();
        currentTierBadge.style.color = tierColor;
        tierLabels.appendChild(currentTierBadge);

        // Miles remaining text (center)
        const progressText = document.createElement('div');
        progressText.id = 'tier-progress-text';
        progressText.className = 'cf-car-tier-progress';
        const nextTierName = tierProgress.nextTier
            ? tierProgress.nextTier.charAt(0).toUpperCase() + tierProgress.nextTier.slice(1)
            : null;
        progressText.textContent = nextTierName
            ? `${Math.round(tierProgress.milesNeeded).toLocaleString()} MI TO ${nextTierName.toUpperCase()}`
            : 'MAX TIER';
        tierLabels.appendChild(progressText);

        // Next tier label
        const nextTierLabel = document.createElement('span');
        nextTierLabel.id = 'next-tier-label';
        nextTierLabel.className = 'cf-car-tier-next';
        nextTierLabel.textContent = tierProgress.nextTier
            ? tierProgress.nextTier.toUpperCase()
            : 'MAX';
        const nextTierColor = tierProgress.nextTier
            ? this.gameState.getTierColorByName(tierProgress.nextTier)
            : tierColor;
        nextTierLabel.style.color = nextTierColor;
        tierLabels.appendChild(nextTierLabel);

        banner.appendChild(tierLabels);

        return banner;
    }

    /**
     * Build upgrades tab content
     */
    _buildUpgradesTab(container, overlay, milesDisplay) {
        const listContainer = document.createElement('div');
        listContainer.className = 'cf-upgrade-list';

        const upgradeTypes = ['maxSpeed', 'baseSpeed', 'momentum', 'momentumRate', 'acceleration', 'cruiseDuration', 'cruiseCooldown'];
        const upgradeButtons = [];
        const maxLevel = this.gameState.getTierUpgradeCap();

        upgradeTypes.forEach(type => {
            const { upgradeItem, purchaseButton } = this._buildUpgradeItem(
                type, maxLevel, overlay, milesDisplay, container
            );
            listContainer.appendChild(upgradeItem);
            if (purchaseButton) {
                upgradeButtons.push(purchaseButton);
            }
        });

        // Next car / prestige option (before driver skills)
        const nextCar = this.gameState.getNextCar();
        if (nextCar) {
            const { item, button } = this._buildAscendItem(nextCar, overlay);
            listContainer.appendChild(item);
            if (button) {
                upgradeButtons.push(button);
            }
        } else {
            listContainer.appendChild(this._buildPrestigeItem(overlay));
        }

        // Driver skills section header
        listContainer.appendChild(this._buildDriverHeader());

        // Add Fuel purchase item first (consumable resource, above Fuel Tank)
        const { fuelItem, fuelButton } = this._buildFuelPurchaseItem(overlay, milesDisplay, container);
        listContainer.appendChild(fuelItem);
        if (fuelButton) {
            upgradeButtons.push({ button: fuelButton, type: 'fuel', cost: this.gameState.getRefuelCost() });
        }

        // Driver skills (global upgrades that apply across all cars)
        const driverSkills = [
            { type: 'fuelTank', name: 'FUEL TANK', getValue: () => this.gameState.getFuelTankCapacity(), formatValue: this._formatDuration.bind(this), getCost: () => this.gameState.getFuelTankCost(), upgrade: () => this.gameState.upgradeFuelTank(), getLevel: () => this.gameState.state.driver?.fuelTank?.level || 0, tooltipTitle: 'Fuel tank capacity', tooltipDesc: 'Maximum fuel capacity for offline driving' },
            { type: 'scrapBonus', name: 'SCRAP BONUS', getValue: () => this.gameState.getScrapBonusMultiplier(), formatValue: (v) => `+${Math.round((v - 1) * 100)}%`, getCost: () => this.gameState.getScrapBonusCost(), upgrade: () => this.gameState.upgradeScrapBonus(), getLevel: () => this.gameState.state.driver?.scrapBonus?.level || 0, tooltipTitle: 'Scrap value bonus', tooltipDesc: 'Bonus Salvage when scrapping Lots and items' },
            { type: 'itemUpgradeBonus', name: 'ITEM FUSION BONUS', getValue: () => this.gameState.getItemUpgradeBonusMultiplier(), formatValue: (v) => `${v.toFixed(2)}x`, getCost: () => this.gameState.getItemUpgradeBonusCost(), upgrade: () => this.gameState.upgradeItemUpgradeBonus(), getLevel: () => this.gameState.state.driver?.itemUpgradeBonus?.level || 0, tooltipTitle: 'Item fusion bonus', tooltipDesc: 'Increases the stat boost from quality fusion' },
            { type: 'waveLuck', name: 'DISCOVERY BONUS', getValue: () => this.gameState.getWaveLuckMultiplier(), formatValue: (v) => `${v.toFixed(2)}x`, getCost: () => this.gameState.getWaveLuckCost(), upgrade: () => this.gameState.upgradeWaveLuck(), getLevel: () => this.gameState.state.driver?.waveLuck?.level || 0, tooltipTitle: 'Lot quality multiplier', tooltipDesc: 'Multiplies the quality of discovered Lots' }
        ];

        driverSkills.forEach(skill => {
            const { upgradeItem, purchaseButton } = this._buildDriverSkillItem(skill, overlay, milesDisplay, container);
            listContainer.appendChild(upgradeItem);
            if (purchaseButton) {
                // Wrap button in expected format for dynamic updates
                upgradeButtons.push({ button: purchaseButton, type: skill.type, cost: skill.getCost() });
            }
        });

        container.appendChild(listContainer);
        return upgradeButtons;
    }

    /**
     * Build Driver section header
     */
    _buildDriverHeader() {
        const header = document.createElement('div');
        header.className = 'cf-driver-header';
        const label = document.createElement('span');
        label.className = 'cf-driver-label';
        label.textContent = 'DRIVER';
        header.appendChild(label);
        return header;
    }

    /**
     * Build a driver skill item row (generic builder for all driver skills)
     */
    _buildDriverSkillItem(skill, overlay, milesDisplay, upgradesContent) {
        const level = skill.getLevel();
        const cost = skill.getCost();
        const canAfford = cost !== null && this.gameState.state.miles >= cost;
        const atMaxLevel = level >= 10;
        const value = skill.getValue();

        const upgradeItem = document.createElement('div');
        upgradeItem.className = `cf-car-upgrade${atMaxLevel ? ' maxed' : ''}`;

        // Left side - info with tooltip
        const infoSection = document.createElement('div');
        infoSection.className = 'cf-car-upgrade-info';

        const nameRow = document.createElement('div');
        nameRow.className = 'cf-car-upgrade-name-row';

        const nameSpan = document.createElement('span');
        nameSpan.className = 'cf-car-upgrade-name';
        nameSpan.textContent = skill.name;
        nameSpan.style.cursor = 'pointer';
        nameRow.appendChild(nameSpan);

        // Info button with tooltip (matches car stat pattern)
        const { icon: infoBtn, showTooltip, hideTooltip } = this._createDriverSkillTooltip(skill);
        nameRow.appendChild(infoBtn);

        // Also trigger tooltip on name click/touch
        nameSpan.addEventListener('click', (e) => {
            e.stopPropagation();
            showTooltip(nameSpan);
        });
        nameSpan.addEventListener('mouseenter', () => showTooltip(nameSpan));
        nameSpan.addEventListener('mouseleave', hideTooltip);

        infoSection.appendChild(nameRow);
        upgradeItem.appendChild(infoSection);

        // Level display
        const levelDiv = document.createElement('div');
        levelDiv.className = 'cf-car-upgrade-level';
        levelDiv.textContent = `LV ${level}/10`;
        upgradeItem.appendChild(levelDiv);

        // Value display
        const statDiv = document.createElement('div');
        statDiv.className = 'cf-car-upgrade-stat';
        const valueSpan = document.createElement('span');
        valueSpan.className = 'cf-car-upgrade-value';
        valueSpan.textContent = skill.formatValue(value);
        statDiv.appendChild(valueSpan);
        upgradeItem.appendChild(statDiv);

        // Purchase button
        const button = document.createElement('button');
        if (atMaxLevel) {
            button.textContent = 'MAX';
            button.className = 'cf-car-upgrade-btn maxed';
        } else {
            button.textContent = `${cost.toLocaleString()} MI`;
            button.className = `cf-car-upgrade-btn ${canAfford ? 'can-afford' : 'cannot-afford'}`;
        }

        button.onclick = () => {
            if (atMaxLevel || !canAfford) return;

            // Capture positions BEFORE purchase
            const buttonRect = button.getBoundingClientRect();
            const oldValue = valueSpan.textContent;

            // Capture old tank capacity before upgrade (for fuel meter animation)
            const oldTankCapacity = skill.type === 'fuelTank' ? this.gameState.getFuelTankCapacity() : 0;

            const success = skill.upgrade();
            if (success) {
                // Play upgrade sound
                this.sfxManager?.playSound('upgrade');

                // Update fuel meter if this was a fuel tank upgrade
                if (skill.type === 'fuelTank') {
                    const fuelMeter = document.getElementById('fuel-meter');
                    if (fuelMeter) {
                        const segments = Array.from(fuelMeter.children);
                        const currentFuel = this.gameState.getFuel();
                        const newTankCapacity = this.gameState.getFuelTankCapacity();
                        const fuelColor = '#C4A535';
                        const fuelColorDim = 'rgba(196, 165, 53, 0.3)';
                        // Animate from old progress to new (lower) progress
                        this._animateFuelFill(segments, currentFuel / oldTankCapacity, currentFuel / newTankCapacity, fuelColor, fuelColorDim);

                        // Update FILL button - tank now has room for more fuel
                        const fillButton = document.getElementById('fuel-purchase-btn');
                        if (fillButton) {
                            const maxAffordable = this.gameState.getMaxAffordableFuel();
                            const currentFuel = this.gameState.getFuel();
                            const canAffordAny = maxAffordable > currentFuel;
                            const affordableCost = this.gameState.getPartialRefuelCost(maxAffordable);

                            if (affordableCost !== null && canAffordAny) {
                                fillButton.textContent = `FILL ${affordableCost.toLocaleString()} MI`;
                                fillButton.disabled = false;
                                fillButton.style.background = 'rgba(196, 165, 53, 0.15)';
                                fillButton.style.borderColor = `rgba(196, 165, 53, 0.5)`;
                                fillButton.style.color = fuelColor;
                                fillButton.style.cursor = 'pointer';
                            } else {
                                const fullCost = this.gameState.getRefuelCost();
                                if (fullCost !== null) {
                                    fillButton.textContent = `FILL ${fullCost.toLocaleString()} MI`;
                                } else {
                                    fillButton.textContent = 'FULL';
                                }
                                fillButton.style.color = 'rgba(255,255,255,0.4)';
                                fillButton.style.borderColor = 'rgba(255,255,255,0.15)';
                                fillButton.style.background = 'rgba(255,255,255,0.05)';
                            }
                        }

                        // Update + button
                        const plusBtn = document.querySelector('.cf-fuel-plus-btn');
                        if (plusBtn) {
                            plusBtn.disabled = false;
                            plusBtn.style.background = 'transparent';
                            plusBtn.style.borderColor = `rgba(196, 165, 53, 0.5)`;
                            plusBtn.style.color = fuelColor;
                            plusBtn.style.cursor = 'pointer';
                        }
                    }
                }

                // Update UI
                const newLevel = skill.getLevel();
                const newCost = skill.getCost();
                const newValue = skill.getValue();
                const newCanAfford = newCost !== null && this.gameState.state.miles >= newCost;
                const newAtMaxLevel = newLevel >= 10;

                levelDiv.textContent = `LV ${newLevel}/10`;

                // Keep old value for animation, will update when particles arrive
                valueSpan.textContent = oldValue;
                const newValueText = skill.formatValue(newValue);

                if (newAtMaxLevel) {
                    button.textContent = 'MAX';
                    button.className = 'cf-car-upgrade-btn maxed';
                    upgradeItem.classList.add('maxed');
                } else {
                    button.textContent = `${newCost.toLocaleString()} MI`;
                    button.className = `cf-car-upgrade-btn ${newCanAfford ? 'can-afford' : 'cannot-afford'}`;
                }

                // Update miles display via animator (triggers pip sounds)
                this.mileAnimator.targetValue = Math.floor(this.gameState.state.miles);
                this._startMileAnimation();

                // Create particles flying to the stat value
                if (this.particles) {
                    this.particles.createParticles({
                        startX: buttonRect.left + buttonRect.width / 2,
                        startY: buttonRect.top + buttonRect.height / 2,
                        target: valueSpan,
                        newValue: newValueText,
                        color: '#64B4FF',
                        colorGradient: ['#fff', '#64B4FF', '#3388DD'],
                        particleCount: 10,
                        particleSizeMin: 5,
                        particleSizeMax: 10,
                        initialSpeedMin: 120,
                        initialSpeedMax: 200,
                        initialLift: 40,
                        gravity: 60,
                        flightDuration: 500,
                        homeStartMin: 150,
                        homeStartMax: 250,
                        shakeDuration: 300,
                        shakeIntensity: 1.0,
                        glowIntensity: 8,
                        playSound: false
                    });
                } else {
                    // Fallback: update value immediately if no particles
                    valueSpan.textContent = newValueText;
                }
            }
        };

        upgradeItem.appendChild(button);

        return { upgradeItem, purchaseButton: button };
    }

    /**
     * Create driver skill tooltip (matches car stat tooltip pattern)
     */
    _createDriverSkillTooltip(skill) {
        const icon = document.createElement('button');
        icon.className = 'cf-car-info-btn';
        icon.textContent = 'i';

        const tooltip = document.createElement('div');
        tooltip.className = 'upgrade-tooltip cf-car-tooltip';
        tooltip.style.display = 'none';

        // Build tooltip content
        const buildTooltipContent = () => {
            const level = skill.getLevel();
            const maxLevel = 10;
            const skillDetails = this._getDriverSkillDetails(skill.type, level);

            // Description goes in header (matches car stat tooltip pattern)
            let html = `<div class="cf-car-tooltip-title">${skillDetails.description.toUpperCase()}</div>`;

            // Current value (skill only)
            html += `<div class="cf-car-tooltip-row"><span class="cf-car-tooltip-label">Skill (LV${level})</span><span class="cf-car-tooltip-value">${skillDetails.currentDisplay}</span></div>`;

            // Item bonus for waveLuck (if applicable)
            if (skill.type === 'waveLuck' && this.gameState.statCalculator) {
                const itemEffects = this.gameState.statCalculator.calculateActiveItemEffects().waveLuck;
                const collectionBonus = this.gameState.statCalculator.getCollectionBonus('waveLuck');
                const hasItemBonus = itemEffects && itemEffects.add > 0;
                const hasCollectionBonus = collectionBonus > 0;

                if (hasItemBonus) {
                    const itemMultiplier = 1 + itemEffects.add;
                    html += `<div class="cf-car-tooltip-row"><span class="cf-car-tooltip-label">Items</span><span class="cf-car-tooltip-value positive">${itemMultiplier.toFixed(2)}x</span></div>`;
                }

                if (hasCollectionBonus) {
                    const collectionMultiplier = 1 + collectionBonus;
                    html += `<div class="cf-car-tooltip-row"><span class="cf-car-tooltip-label">Collection</span><span class="cf-car-tooltip-value positive">${collectionMultiplier.toFixed(2)}x</span></div>`;
                }

                if (hasItemBonus || hasCollectionBonus) {
                    // Show total
                    const totalMultiplier = this.gameState.getWaveLuckMultiplier();
                    html += `<div class="cf-car-tooltip-row" style="margin-top: var(--cf-space-sm); padding-top: var(--cf-space-sm); border-top: 1px solid var(--cf-divider);"><span class="cf-car-tooltip-label">Total</span><span class="cf-car-tooltip-value" style="color: #4ade80;">${totalMultiplier.toFixed(2)}x</span></div>`;
                }
            }

            // Item and collection bonus for scrapBonus (salvageBonus)
            if (skill.type === 'scrapBonus' && this.gameState.statCalculator) {
                const itemEffects = this.gameState.statCalculator.calculateActiveItemEffects().salvageBonus;
                const collectionBonus = this.gameState.statCalculator.getCollectionBonus('salvageBonus');
                const hasItemBonus = itemEffects && itemEffects.add > 0;
                const hasCollectionBonus = collectionBonus > 0;

                if (hasItemBonus) {
                    const itemBonusPercent = Math.round(itemEffects.add * 100);
                    html += `<div class="cf-car-tooltip-row"><span class="cf-car-tooltip-label">Items</span><span class="cf-car-tooltip-value positive">+${itemBonusPercent}%</span></div>`;
                }

                if (hasCollectionBonus) {
                    const collectionPercent = Math.round(collectionBonus * 100);
                    html += `<div class="cf-car-tooltip-row"><span class="cf-car-tooltip-label">Collection</span><span class="cf-car-tooltip-value positive">+${collectionPercent}%</span></div>`;
                }

                if (hasItemBonus || hasCollectionBonus) {
                    // Show total
                    const totalMultiplier = this.gameState.getScrapBonusMultiplier(false);
                    const totalPercent = Math.round((totalMultiplier - 1) * 100);
                    html += `<div class="cf-car-tooltip-row" style="margin-top: var(--cf-space-sm); padding-top: var(--cf-space-sm); border-top: 1px solid var(--cf-divider);"><span class="cf-car-tooltip-label">Total</span><span class="cf-car-tooltip-value" style="color: #4ade80;">+${totalPercent}%</span></div>`;
                }
            }

            // Item and collection bonus for fuelTank
            if (skill.type === 'fuelTank' && this.gameState.statCalculator) {
                const itemEffects = this.gameState.statCalculator.calculateActiveItemEffects().fuelTank;
                const collectionBonus = this.gameState.statCalculator.getCollectionBonus('fuelTank');
                const hasItemBonus = itemEffects && itemEffects.add > 0;
                const hasCollectionBonus = collectionBonus > 0;

                if (hasItemBonus) {
                    html += `<div class="cf-car-tooltip-row"><span class="cf-car-tooltip-label">Items</span><span class="cf-car-tooltip-value positive">+${StatsFormatter.formatDurationValue(itemEffects.add)}</span></div>`;
                }

                if (hasCollectionBonus) {
                    html += `<div class="cf-car-tooltip-row"><span class="cf-car-tooltip-label">Collection</span><span class="cf-car-tooltip-value positive">+${StatsFormatter.formatDurationValue(collectionBonus)}</span></div>`;
                }

                if (hasItemBonus || hasCollectionBonus) {
                    // Show total
                    const totalCapacity = this.gameState.getFuelTankCapacity();
                    html += `<div class="cf-car-tooltip-row" style="margin-top: var(--cf-space-sm); padding-top: var(--cf-space-sm); border-top: 1px solid var(--cf-divider);"><span class="cf-car-tooltip-label">Total</span><span class="cf-car-tooltip-value" style="color: #4ade80;">${this._formatDuration(totalCapacity)}</span></div>`;
                }
            }

            // Item bonus for itemUpgradeBonus
            if (skill.type === 'itemUpgradeBonus' && this.gameState.statCalculator) {
                const itemEffects = this.gameState.statCalculator.calculateActiveItemEffects().itemUpgradeBonus;
                const collectionBonus = this.gameState.statCalculator.getCollectionBonus('itemUpgradeBonus');
                const hasItemBonus = itemEffects && itemEffects.add > 0;
                const hasCollectionBonus = collectionBonus > 0;

                if (hasItemBonus) {
                    const itemMultiplier = 1 + itemEffects.add;
                    html += `<div class="cf-car-tooltip-row"><span class="cf-car-tooltip-label">Items</span><span class="cf-car-tooltip-value positive">${itemMultiplier.toFixed(2)}x</span></div>`;
                }

                if (hasCollectionBonus) {
                    const collectionMultiplier = 1 + collectionBonus;
                    html += `<div class="cf-car-tooltip-row"><span class="cf-car-tooltip-label">Collection</span><span class="cf-car-tooltip-value positive">${collectionMultiplier.toFixed(2)}x</span></div>`;
                }

                if (hasItemBonus || hasCollectionBonus) {
                    // Show total
                    const totalMultiplier = this.gameState.getItemUpgradeBonusMultiplier();
                    html += `<div class="cf-car-tooltip-row" style="margin-top: var(--cf-space-sm); padding-top: var(--cf-space-sm); border-top: 1px solid var(--cf-divider);"><span class="cf-car-tooltip-label">Total</span><span class="cf-car-tooltip-value" style="color: #4ade80;">${totalMultiplier.toFixed(2)}x</span></div>`;
                }
            }

            // Next level preview
            if (level < maxLevel) {
                const nextLevel = level + 1;
                const nextDetails = this._getDriverSkillDetails(skill.type, nextLevel);
                html += `<div class="cf-car-tooltip-row" style="margin-top: var(--cf-space-sm); padding-top: var(--cf-space-sm); border-top: 1px solid var(--cf-divider);"><span class="cf-car-tooltip-label">Next Level</span><span class="cf-car-tooltip-value positive">${nextDetails.improvement}</span></div>`;
            } else {
                html += `<div class="cf-car-tooltip-row" style="margin-top: var(--cf-space-sm); padding-top: var(--cf-space-sm); border-top: 1px solid var(--cf-divider);"><span class="cf-car-tooltip-label">Status</span><span class="cf-car-tooltip-value" style="color: #ffd700;">MAX LEVEL</span></div>`;
            }

            return html;
        };

        tooltip.innerHTML = buildTooltipContent();
        document.body.appendChild(tooltip);

        // Helper to show tooltip
        const showTooltip = (anchor) => {
            // Rebuild content to get current values
            tooltip.innerHTML = buildTooltipContent();
            document.querySelectorAll('.upgrade-tooltip').forEach(t => t.style.display = 'none');
            tooltip.style.display = 'block';
            this.gameUI.positionTooltip(tooltip, anchor);
        };

        // Helper to hide tooltip
        const hideTooltip = () => {
            tooltip.style.display = 'none';
        };

        // Icon hover events
        icon.addEventListener('mouseenter', () => showTooltip(icon));
        icon.addEventListener('mouseleave', hideTooltip);

        // Icon touch/click support
        icon.addEventListener('click', (e) => {
            e.stopPropagation();
            const isVisible = tooltip.style.display === 'block';
            if (isVisible) {
                hideTooltip();
            } else {
                showTooltip(icon);
            }
        });

        return { icon, tooltip, showTooltip, hideTooltip };
    }

    /**
     * Build the Fuel purchase item (consumable resource, not a skill)
     */
    _buildFuelPurchaseItem(overlay, milesDisplay, upgradesContent) {
        const currentFuel = this.gameState.getFuel();
        const tankCapacity = this.gameState.getFuelTankCapacity();
        const isFull = currentFuel >= tankCapacity;

        // Calculate max affordable fuel and its cost
        const maxAffordableFuel = this.gameState.getMaxAffordableFuel();
        const cost = this.gameState.getPartialRefuelCost(maxAffordableFuel);
        const canAfford = maxAffordableFuel > currentFuel;

        // Dirty yellow color for fuel
        const fuelColor = '#C4A535';
        const fuelColorDim = 'rgba(196, 165, 53, 0.3)';

        const fuelItem = document.createElement('div');
        fuelItem.className = 'cf-car-upgrade cf-fuel-item';
        fuelItem.id = 'fuel-purchase-item';

        // Left side - info with tooltip
        const infoSection = document.createElement('div');
        infoSection.className = 'cf-car-upgrade-info';
        infoSection.style.flex = '1';

        const nameRow = document.createElement('div');
        nameRow.className = 'cf-car-upgrade-name-row';

        const name = document.createElement('span');
        name.className = 'cf-car-upgrade-name';
        name.textContent = 'FUEL';
        name.style.cursor = 'pointer';
        nameRow.appendChild(name);

        // Info icon with tooltip (matches driver skill tooltip pattern)
        const icon = document.createElement('button');
        icon.className = 'cf-car-info-btn';
        icon.textContent = 'i';

        const tooltip = document.createElement('div');
        tooltip.className = 'upgrade-tooltip cf-car-tooltip';
        tooltip.style.display = 'none';

        const buildTooltipContent = () => {
            const fuelNow = this.gameState.getFuel();
            const tankCap = this.gameState.getFuelTankCapacity();
            const full = fuelNow >= tankCap;

            let html = `<div class="cf-car-tooltip-title">PURCHASE FUEL TO DRIVE WHILE AWAY. FUEL IS CONSUMED DURING OFFLINE SIMULATION.</div>`;
            html += `<div class="cf-car-tooltip-row"><span class="cf-car-tooltip-label">Current</span><span class="cf-car-tooltip-value">${this._formatDuration(fuelNow)} / ${this._formatDuration(tankCap)}</span></div>`;
            html += `<div class="cf-car-tooltip-row" style="margin-top: var(--cf-space-sm); padding-top: var(--cf-space-sm); border-top: 1px solid var(--cf-divider);"><span class="cf-car-tooltip-label">Refuel</span><span class="cf-car-tooltip-value ${full ? '' : 'positive'}">${full ? 'TANK FULL' : `+${this._formatDuration(tankCap - fuelNow)}`}</span></div>`;
            return html;
        };

        tooltip.innerHTML = buildTooltipContent();
        document.body.appendChild(tooltip);

        const showTooltip = (anchor) => {
            tooltip.innerHTML = buildTooltipContent();
            document.querySelectorAll('.upgrade-tooltip').forEach(t => t.style.display = 'none');
            tooltip.style.display = 'block';
            this.gameUI.positionTooltip(tooltip, anchor);
        };

        const hideTooltip = () => {
            tooltip.style.display = 'none';
        };

        icon.addEventListener('mouseenter', () => showTooltip(icon));
        icon.addEventListener('mouseleave', hideTooltip);
        icon.addEventListener('click', (e) => {
            e.stopPropagation();
            tooltip.style.display === 'block' ? hideTooltip() : showTooltip(icon);
        });

        name.addEventListener('mouseenter', () => showTooltip(name));
        name.addEventListener('mouseleave', hideTooltip);
        name.addEventListener('click', (e) => {
            e.stopPropagation();
            showTooltip(name);
        });

        nameRow.appendChild(icon);
        infoSection.appendChild(nameRow);

        // Segmented fuel gauge (10 segments)
        const fuelMeter = document.createElement('div');
        fuelMeter.className = 'cf-fuel-meter';
        fuelMeter.id = 'fuel-meter';
        fuelMeter.style.cssText = `
            display: flex;
            gap: 2px;
            margin-top: 4px;
            height: 8px;
            overflow: hidden;
        `;

        const fuelProgress = currentFuel / tankCapacity;
        const segments = [];

        for (let i = 0; i < 10; i++) {
            const segment = document.createElement('div');
            segment.className = 'cf-fuel-segment';
            segment.style.cssText = `
                flex: 1;
                height: 100%;
                border-radius: 1px;
                transition: background 0.15s ease, opacity 0.15s ease;
            `;

            const segmentStart = i * 0.1;
            const segmentEnd = (i + 1) * 0.1;

            if (fuelProgress >= segmentEnd) {
                segment.style.background = fuelColor;
                segment.classList.add('filled');
            } else if (fuelProgress > segmentStart) {
                const segmentFill = (fuelProgress - segmentStart) / 0.1;
                segment.style.background = fuelColor;
                segment.style.opacity = (0.3 + segmentFill * 0.7).toString();
                segment.classList.add('filled', 'active');
            } else {
                segment.style.background = fuelColorDim;
            }

            segments.push(segment);
            fuelMeter.appendChild(segment);
        }

        infoSection.appendChild(fuelMeter);
        fuelItem.appendChild(infoSection);

        // Spacer to align with grid columns 2 and 3 (level and stat columns)
        const spacer = document.createElement('div');
        spacer.style.cssText = `grid-column: span 2;`;
        fuelItem.appendChild(spacer);

        // Right side - Button container with + and FILL buttons (in grid column 4)
        const buttonContainer = document.createElement('div');
        buttonContainer.style.cssText = `
            display: flex;
            gap: 6px;
            align-items: center;
            justify-content: flex-end;
        `;

        // + button for partial fill modal (square button)
        const plusButton = document.createElement('button');
        plusButton.className = 'cf-fuel-plus-btn';
        plusButton.textContent = '+';
        plusButton.style.cssText = `
            font-family: var(--cf-font-mono, 'IBM Plex Mono', monospace);
            font-size: 16px;
            font-weight: 500;
            width: 32px;
            height: 32px;
            border: 1px solid ${isFull ? 'rgba(255,255,255,0.1)' : 'rgba(196, 165, 53, 0.5)'};
            border-radius: 2px;
            cursor: ${isFull ? 'default' : 'pointer'};
            transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
            outline: none;
            background: ${isFull ? 'rgba(255,255,255,0.05)' : 'rgba(196, 165, 53, 0.1)'};
            color: ${isFull ? 'rgba(255,255,255,0.3)' : fuelColor};
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
        `;
        plusButton.disabled = isFull;

        if (!isFull) {
            plusButton.addEventListener('mouseenter', () => {
                plusButton.style.background = 'rgba(196, 165, 53, 0.25)';
                plusButton.style.borderColor = fuelColor;
                plusButton.style.boxShadow = `0 0 8px rgba(196, 165, 53, 0.3)`;
            });
            plusButton.addEventListener('mouseleave', () => {
                plusButton.style.background = 'rgba(196, 165, 53, 0.1)';
                plusButton.style.borderColor = 'rgba(196, 165, 53, 0.5)';
                plusButton.style.boxShadow = 'none';
            });
            plusButton.addEventListener('click', () => {
                plusButton.blur();
                this._openFuelFillModal(overlay, milesDisplay, segments, fuelColor, fuelColorDim);
            });
        }

        buttonContainer.appendChild(plusButton);

        // FILL button (cassette future style)
        const purchaseButton = document.createElement('button');
        purchaseButton.className = 'cf-fuel-btn';
        purchaseButton.id = 'fuel-purchase-btn';
        purchaseButton.style.cssText = `
            font-family: var(--cf-font-mono, 'IBM Plex Mono', monospace);
            font-size: 10px;
            font-weight: 500;
            letter-spacing: 0.5px;
            padding: 8px 12px;
            min-width: 90px;
            border: 1px solid ${isFull ? 'rgba(255,255,255,0.1)' : canAfford ? 'rgba(196, 165, 53, 0.5)' : 'rgba(255,255,255,0.15)'};
            border-radius: 2px;
            cursor: ${isFull ? 'default' : 'pointer'};
            transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
            text-transform: uppercase;
            white-space: nowrap;
            outline: none;
            background: ${isFull ? 'rgba(255,255,255,0.05)' : canAfford ? 'rgba(196, 165, 53, 0.15)' : 'rgba(255,255,255,0.05)'};
            color: ${isFull ? 'rgba(255,255,255,0.3)' : canAfford ? fuelColor : 'rgba(255,255,255,0.4)'};
        `;

        if (isFull) {
            purchaseButton.textContent = 'FULL';
            purchaseButton.disabled = true;
        } else {
            // Use partial refuel cost if available, otherwise show full refuel cost
            const displayCost = cost ?? this.gameState.getRefuelCost() ?? 0;
            purchaseButton.textContent = `FILL ${displayCost.toLocaleString()} MI`;

            // Hover effects
            purchaseButton.addEventListener('mouseenter', () => {
                const nowMaxAffordable = this.gameState.getMaxAffordableFuel();
                const nowCanAfford = nowMaxAffordable > this.gameState.getFuel();
                if (nowCanAfford) {
                    purchaseButton.style.background = 'rgba(196, 165, 53, 0.25)';
                    purchaseButton.style.borderColor = fuelColor;
                    purchaseButton.style.boxShadow = `0 0 10px rgba(196, 165, 53, 0.3)`;
                }
            });
            purchaseButton.addEventListener('mouseleave', () => {
                const nowMaxAffordable = this.gameState.getMaxAffordableFuel();
                const nowCanAfford = nowMaxAffordable > this.gameState.getFuel();
                purchaseButton.style.background = nowCanAfford ? 'rgba(196, 165, 53, 0.15)' : 'rgba(255,255,255,0.05)';
                purchaseButton.style.borderColor = nowCanAfford ? 'rgba(196, 165, 53, 0.5)' : 'rgba(255,255,255,0.15)';
                purchaseButton.style.boxShadow = 'none';
            });

            purchaseButton.addEventListener('click', (e) => {
                // Calculate max affordable at time of click
                const maxAffordableNow = this.gameState.getMaxAffordableFuel();
                const currentFuelNow = this.gameState.getFuel();
                if (maxAffordableNow <= currentFuelNow) return;

                // Blur button to prevent focus-related scroll behavior
                purchaseButton.blur();

                // Prevent scroll adjustment by capturing scroll position
                const scrollContainer = upgradesContent.closest('.cf-car-scrollable');
                const scrollTop = scrollContainer?.scrollTop || 0;

                const oldFuel = currentFuelNow;
                const success = this.gameState.partialRefuel(maxAffordableNow);
                if (success) {
                    // Play upgrade sound
                    this.sfxManager?.playSound('upgrade');

                    // Update miles display via animator (triggers pip sounds)
                    this.mileAnimator.targetValue = Math.floor(this.gameState.state.miles);
                    this._startMileAnimation();

                    // Restore scroll position to prevent jolt
                    if (scrollContainer) {
                        scrollContainer.scrollTop = scrollTop;
                    }

                    // Animate fuel meter filling up
                    const newFuel = this.gameState.getFuel();
                    const newCapacity = this.gameState.getFuelTankCapacity();
                    this._animateFuelFill(segments, oldFuel / newCapacity, newFuel / newCapacity, fuelColor, fuelColorDim);

                    // Update button state after animation
                    setTimeout(() => {
                        const updatedFuel = this.gameState.getFuel();
                        const updatedCapacity = this.gameState.getFuelTankCapacity();
                        const tankIsFull = updatedFuel >= updatedCapacity;
                        const updatedMaxAffordable = this.gameState.getMaxAffordableFuel();
                        const canStillAfford = updatedMaxAffordable > updatedFuel;
                        const updatedCost = this.gameState.getPartialRefuelCost(updatedMaxAffordable);

                        if (tankIsFull) {
                            purchaseButton.textContent = 'FULL';
                            purchaseButton.disabled = true;
                            purchaseButton.style.background = 'rgba(255,255,255,0.05)';
                            purchaseButton.style.borderColor = 'rgba(255,255,255,0.1)';
                            purchaseButton.style.color = 'rgba(255,255,255,0.3)';
                            purchaseButton.style.cursor = 'default';

                            // Also disable + button
                            plusButton.disabled = true;
                            plusButton.style.background = 'rgba(255,255,255,0.05)';
                            plusButton.style.borderColor = 'rgba(255,255,255,0.1)';
                            plusButton.style.color = 'rgba(255,255,255,0.3)';
                            plusButton.style.cursor = 'default';
                        } else if (canStillAfford && updatedCost !== null) {
                            // Can still afford more - update button with new cost
                            purchaseButton.textContent = `FILL ${updatedCost.toLocaleString()} MI`;
                            purchaseButton.style.color = fuelColor;
                            purchaseButton.style.borderColor = 'rgba(196, 165, 53, 0.5)';
                            purchaseButton.style.background = 'rgba(196, 165, 53, 0.15)';
                        } else {
                            // Can't afford any more
                            const fullCost = this.gameState.getRefuelCost();
                            if (fullCost !== null) {
                                purchaseButton.textContent = `FILL ${fullCost.toLocaleString()} MI`;
                            } else {
                                purchaseButton.textContent = 'FULL';
                            }
                            purchaseButton.style.color = 'rgba(255,255,255,0.4)';
                            purchaseButton.style.borderColor = 'rgba(255,255,255,0.15)';
                            purchaseButton.style.background = 'rgba(255,255,255,0.05)';
                        }
                    }, 600);
                }
            });
        }

        buttonContainer.appendChild(purchaseButton);
        fuelItem.appendChild(buttonContainer);

        return { fuelItem, fuelButton: isFull ? null : purchaseButton, plusButton: isFull ? null : plusButton };
    }

    /**
     * Animate fuel meter segments filling up
     */
    _animateFuelFill(segments, startProgress, endProgress, fuelColor, fuelColorDim) {
        const duration = 500;
        const startTime = performance.now();

        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const t = Math.min(elapsed / duration, 1);
            // Ease out cubic for smooth deceleration
            const easeT = 1 - Math.pow(1 - t, 3);
            const currentProgress = startProgress + (endProgress - startProgress) * easeT;

            segments.forEach((segment, i) => {
                const segmentStart = i * 0.1;
                const segmentEnd = (i + 1) * 0.1;

                if (currentProgress >= segmentEnd) {
                    segment.style.background = fuelColor;
                    segment.style.opacity = '1';
                    segment.classList.add('filled');
                    segment.classList.remove('active');
                } else if (currentProgress > segmentStart) {
                    const segmentFill = (currentProgress - segmentStart) / 0.1;
                    segment.style.background = fuelColor;
                    segment.style.opacity = (0.3 + segmentFill * 0.7).toString();
                    segment.classList.add('filled', 'active');
                } else {
                    segment.style.background = fuelColorDim;
                    segment.style.opacity = '1';
                    segment.classList.remove('filled', 'active');
                }
            });

            if (t < 1) {
                requestAnimationFrame(animate);
            } else {
                // Final state - set segments to match endProgress exactly
                segments.forEach((segment, i) => {
                    const segmentStart = i * 0.1;
                    const segmentEnd = (i + 1) * 0.1;

                    if (endProgress >= segmentEnd) {
                        segment.style.background = fuelColor;
                        segment.style.opacity = '1';
                        segment.classList.add('filled');
                        segment.classList.remove('active');
                    } else if (endProgress > segmentStart) {
                        const segmentFill = (endProgress - segmentStart) / 0.1;
                        segment.style.background = fuelColor;
                        segment.style.opacity = (0.3 + segmentFill * 0.7).toString();
                        segment.classList.add('filled', 'active');
                    } else {
                        segment.style.background = fuelColorDim;
                        segment.style.opacity = '1';
                        segment.classList.remove('filled', 'active');
                    }

                    // Staggered celebration pulse on filled segments only
                    if (endProgress >= segmentStart) {
                        setTimeout(() => {
                            segment.style.boxShadow = `0 0 8px ${fuelColor}`;
                            setTimeout(() => {
                                segment.style.boxShadow = 'none';
                            }, 200);
                        }, i * 30);
                    }
                });
            }
        };

        requestAnimationFrame(animate);
    }

    /**
     * Open the fuel fill modal with slider
     */
    _openFuelFillModal(overlay, milesDisplay, segments, fuelColor, fuelColorDim) {
        const currentFuel = this.gameState.getFuel();
        const tankCapacity = this.gameState.getFuelTankCapacity();
        const playerMiles = this.gameState.getMiles();

        // Use ModalManager to create the modal
        const modal = this.gameUI.modals.create({
            className: 'cf-fuel-modal-overlay',
            maxWidth: '380px',
            padding: '24px',
            closeOnBackdrop: true
        });

        // Style the container for cassette future theme
        modal.container.style.cssText += `
            background: var(--cf-bg-secondary, #1a1a2e);
            border: 1px solid var(--cf-border, rgba(255,255,255,0.15));
            border-radius: 4px;
            font-family: var(--cf-font-mono, 'IBM Plex Mono', monospace);
        `;

        // Selected amount display (serves as header)
        const amountDisplay = document.createElement('div');
        amountDisplay.style.cssText = `
            text-align: center;
            margin-bottom: 20px;
        `;
        const amountValue = document.createElement('div');
        amountValue.style.cssText = `
            font-family: var(--cf-font-mono, 'IBM Plex Mono', monospace);
            font-size: 28px;
            font-weight: 500;
            color: ${fuelColor};
            letter-spacing: 1px;
        `;
        amountDisplay.appendChild(amountValue);
        modal.container.appendChild(amountDisplay);

        // Fuel meter preview
        const meterPreview = document.createElement('div');
        meterPreview.style.cssText = `
            display: flex;
            gap: 2px;
            height: 12px;
            margin-bottom: 8px;
            overflow: hidden;
        `;
        for (let i = 0; i < 10; i++) {
            const seg = document.createElement('div');
            seg.className = 'cf-fuel-preview-segment';
            seg.style.cssText = `
                flex: 1;
                height: 100%;
                border-radius: 1px;
                transition: background 0.15s ease;
            `;
            meterPreview.appendChild(seg);
        }
        modal.container.appendChild(meterPreview);

        // Meter legend
        const meterLegend = document.createElement('div');
        meterLegend.style.cssText = `
            display: flex;
            justify-content: center;
            gap: 16px;
            font-family: var(--cf-font-mono, 'IBM Plex Mono', monospace);
            font-size: 9px;
            color: rgba(255,255,255,0.5);
            margin-bottom: 16px;
        `;
        // Build meter legend using DOM APIs
        const currentLegend = document.createElement('span');
        currentLegend.style.cssText = 'display: flex; align-items: center; gap: 4px;';
        const currentSwatch = document.createElement('span');
        currentSwatch.style.cssText = `width: 10px; height: 10px; background: ${fuelColor}; opacity: 0.4; border-radius: 1px;`;
        currentLegend.appendChild(currentSwatch);
        currentLegend.appendChild(document.createTextNode('CURRENT'));
        const afterLegend = document.createElement('span');
        afterLegend.style.cssText = 'display: flex; align-items: center; gap: 4px;';
        const afterSwatch = document.createElement('span');
        afterSwatch.style.cssText = `width: 10px; height: 10px; background: ${fuelColor}; border-radius: 1px;`;
        afterLegend.appendChild(afterSwatch);
        afterLegend.appendChild(document.createTextNode('AFTER FILL'));
        meterLegend.appendChild(currentLegend);
        meterLegend.appendChild(afterLegend);
        modal.container.appendChild(meterLegend);

        // Slider container (moved below meter so thumbs don't block info)
        const sliderContainer = document.createElement('div');
        sliderContainer.style.cssText = `margin-bottom: 20px;`;

        // Calculate max affordable fuel level (binary search)
        const calculateMaxAffordableFuel = () => {
            let low = currentFuel;
            let high = tankCapacity;
            let maxAffordable = currentFuel; // Default to current (can't afford any)

            while (low <= high) {
                const mid = Math.floor((low + high) / 2);
                const cost = this.gameState.getPartialRefuelCost(mid);

                if (cost !== null && cost <= playerMiles) {
                    maxAffordable = mid;
                    low = mid + 1;
                } else {
                    high = mid - 1;
                }
            }

            // Round down to nearest step (60 seconds)
            return Math.floor((maxAffordable - currentFuel) / 60) * 60 + currentFuel;
        };
        const maxAffordableFuel = calculateMaxAffordableFuel();

        // Slider
        const slider = document.createElement('input');
        slider.type = 'range';
        slider.min = currentFuel.toString();
        slider.max = tankCapacity.toString();
        slider.step = '60'; // 1 minute increments
        // Default to minimum (current + 1 step) so user can enjoy sliding up, clamped to affordable
        const minFillValue = Math.min(currentFuel + 60, maxAffordableFuel, tankCapacity);
        slider.value = minFillValue.toString();
        slider.style.cssText = `
            width: 100%;
            height: 8px;
            -webkit-appearance: none;
            appearance: none;
            background: rgba(255,255,255,0.1);
            border-radius: 4px;
            outline: none;
            cursor: pointer;
        `;

        // Style the slider thumb with CSS
        const sliderStyle = document.createElement('style');
        sliderStyle.textContent = `
            .cf-fuel-slider::-webkit-slider-thumb {
                -webkit-appearance: none;
                appearance: none;
                width: 18px;
                height: 18px;
                background: ${fuelColor};
                border-radius: 50%;
                cursor: pointer;
                box-shadow: 0 0 8px rgba(196, 165, 53, 0.5);
            }
            .cf-fuel-slider::-moz-range-thumb {
                width: 18px;
                height: 18px;
                background: ${fuelColor};
                border-radius: 50%;
                cursor: pointer;
                border: none;
                box-shadow: 0 0 8px rgba(196, 165, 53, 0.5);
            }
        `;
        document.head.appendChild(sliderStyle);
        slider.className = 'cf-fuel-slider';

        sliderContainer.appendChild(slider);

        // Slider labels
        const sliderLabels = document.createElement('div');
        sliderLabels.style.cssText = `
            display: flex;
            justify-content: space-between;
            font-family: var(--cf-font-mono, 'IBM Plex Mono', monospace);
            font-size: 10px;
            color: rgba(255,255,255,0.5);
            margin-top: 6px;
        `;
        const minLabel = document.createElement('span');
        minLabel.textContent = this._formatDuration(currentFuel);
        const maxLabel = document.createElement('span');
        maxLabel.textContent = this._formatDuration(tankCapacity);
        sliderLabels.appendChild(minLabel);
        sliderLabels.appendChild(maxLabel);
        sliderContainer.appendChild(sliderLabels);

        modal.container.appendChild(sliderContainer);

        // Info rows
        const infoContainer = document.createElement('div');
        infoContainer.style.cssText = `
            display: flex;
            flex-direction: column;
            gap: 8px;
            padding: 12px;
            background: rgba(0,0,0,0.2);
            border-radius: 4px;
            margin-bottom: 20px;
        `;

        const createInfoRow = (label, valueId) => {
            const row = document.createElement('div');
            row.style.cssText = `
                display: flex;
                justify-content: space-between;
                font-family: var(--cf-font-mono, 'IBM Plex Mono', monospace);
                font-size: 11px;
            `;
            const labelSpan = document.createElement('span');
            labelSpan.style.color = 'rgba(255,255,255,0.6)';
            labelSpan.textContent = label;
            const valueSpan = document.createElement('span');
            valueSpan.id = valueId;
            valueSpan.style.color = fuelColor;
            row.appendChild(labelSpan);
            row.appendChild(valueSpan);
            return row;
        };

        infoContainer.appendChild(createInfoRow('DURATION', 'fuel-modal-duration'));
        infoContainer.appendChild(createInfoRow('COST', 'fuel-modal-cost'));
        modal.container.appendChild(infoContainer);

        // Buttons
        const buttonRow = document.createElement('div');
        buttonRow.style.cssText = `
            display: flex;
            gap: 12px;
            justify-content: center;
        `;

        const cancelBtn = document.createElement('button');
        cancelBtn.textContent = 'CANCEL';
        cancelBtn.style.cssText = `
            font-family: var(--cf-font-mono, 'IBM Plex Mono', monospace);
            font-size: 11px;
            padding: 10px 20px;
            background: rgba(255,255,255,0.05);
            border: 1px solid rgba(255,255,255,0.2);
            border-radius: 2px;
            color: rgba(255,255,255,0.6);
            cursor: pointer;
            transition: all 0.15s ease;
            outline: none;
        `;
        cancelBtn.addEventListener('mouseenter', () => {
            cancelBtn.style.background = 'rgba(255,255,255,0.1)';
            cancelBtn.style.borderColor = 'rgba(255,255,255,0.3)';
        });
        cancelBtn.addEventListener('mouseleave', () => {
            cancelBtn.style.background = 'rgba(255,255,255,0.05)';
            cancelBtn.style.borderColor = 'rgba(255,255,255,0.2)';
        });

        const fillBtn = document.createElement('button');
        fillBtn.style.cssText = `
            font-family: var(--cf-font-mono, 'IBM Plex Mono', monospace);
            font-size: 11px;
            padding: 10px 20px;
            background: rgba(196, 165, 53, 0.2);
            border: 1px solid ${fuelColor};
            border-radius: 2px;
            color: ${fuelColor};
            cursor: pointer;
            transition: all 0.15s ease;
            outline: none;
            min-width: 120px;
        `;
        fillBtn.addEventListener('mouseenter', () => {
            if (!fillBtn.disabled) {
                fillBtn.style.background = 'rgba(196, 165, 53, 0.3)';
                fillBtn.style.boxShadow = `0 0 12px rgba(196, 165, 53, 0.4)`;
            }
        });
        fillBtn.addEventListener('mouseleave', () => {
            fillBtn.style.background = 'rgba(196, 165, 53, 0.2)';
            fillBtn.style.boxShadow = 'none';
        });

        buttonRow.appendChild(cancelBtn);
        buttonRow.appendChild(fillBtn);
        modal.container.appendChild(buttonRow);

        // Update function
        const updateDisplay = () => {
            const targetFuel = parseFloat(slider.value);
            const fuelToAdd = targetFuel - currentFuel;
            const cost = this.gameState.getPartialRefuelCost(targetFuel) || 0;
            const canAfford = playerMiles >= cost && cost > 0;

            // Update amount display
            amountValue.textContent = this._formatDuration(targetFuel);

            // Update preview meter
            const previewSegments = meterPreview.querySelectorAll('.cf-fuel-preview-segment');
            const currentProgress = currentFuel / tankCapacity;
            const targetProgress = targetFuel / tankCapacity;

            previewSegments.forEach((seg, i) => {
                const segmentStart = i * 0.1;
                const segmentEnd = (i + 1) * 0.1;

                if (currentProgress >= segmentEnd) {
                    // Fully in current fuel
                    seg.style.background = fuelColor;
                    seg.style.opacity = '0.4';
                } else if (targetProgress >= segmentEnd) {
                    // Will be filled
                    seg.style.background = fuelColor;
                    seg.style.opacity = '1';
                } else if (targetProgress > segmentStart) {
                    // Partially filled
                    seg.style.background = fuelColor;
                    seg.style.opacity = '0.7';
                } else if (currentProgress > segmentStart) {
                    // Partially current
                    seg.style.background = fuelColor;
                    seg.style.opacity = '0.3';
                } else {
                    // Empty
                    seg.style.background = 'rgba(196, 165, 53, 0.15)';
                    seg.style.opacity = '1';
                }
            });

            // Update info
            document.getElementById('fuel-modal-duration').textContent = fuelToAdd > 0 ? `+${this._formatDuration(fuelToAdd)}` : '—';
            document.getElementById('fuel-modal-cost').textContent = cost > 0 ? `${cost} MI` : '—';

            // Update fill button
            if (cost <= 0) {
                fillBtn.textContent = 'NO CHANGE';
                fillBtn.disabled = true;
                fillBtn.style.opacity = '0.5';
                fillBtn.style.cursor = 'default';
            } else if (!canAfford) {
                fillBtn.textContent = `FILL ${cost.toLocaleString()} MI`;
                fillBtn.disabled = true;
                fillBtn.style.opacity = '0.5';
                fillBtn.style.cursor = 'default';
            } else {
                fillBtn.textContent = `FILL ${cost.toLocaleString()} MI`;
                fillBtn.disabled = false;
                fillBtn.style.opacity = '1';
                fillBtn.style.cursor = 'pointer';
            }
        };

        // Initial update
        updateDisplay();

        // Slider change handler - clamp to max affordable
        slider.addEventListener('input', () => {
            const value = parseFloat(slider.value);
            if (value > maxAffordableFuel) {
                slider.value = maxAffordableFuel.toString();
            }
            updateDisplay();
        });

        // Close modal function
        const closeModal = () => {
            sliderStyle.remove();
            modal.close();
        };

        // Cancel button
        cancelBtn.addEventListener('click', closeModal);

        // Escape key to close
        const escHandler = (e) => {
            if (e.key === 'Escape') {
                closeModal();
                document.removeEventListener('keydown', escHandler);
            }
        };
        document.addEventListener('keydown', escHandler);

        // Fill button
        fillBtn.addEventListener('click', () => {
            const targetFuel = parseFloat(slider.value);
            const cost = this.gameState.getPartialRefuelCost(targetFuel);

            if (cost && this.gameState.getMiles() >= cost) {
                const oldFuel = currentFuel;
                const success = this.gameState.partialRefuel(targetFuel);

                if (success) {
                    // Play upgrade sound
                    this.sfxManager?.playSound('upgrade');

                    this.mileAnimator.targetValue = Math.floor(this.gameState.state.miles);
                    this._startMileAnimation();

                    // Animate fuel meter in the upgrade menu
                    const newFuel = this.gameState.getFuel();
                    const newCapacity = this.gameState.getFuelTankCapacity();
                    this._animateFuelFill(segments, oldFuel / newCapacity, newFuel / newCapacity, fuelColor, fuelColorDim);

                    // Update the FILL button in the menu
                    const fillButton = document.getElementById('fuel-purchase-btn');
                    if (fillButton) {
                        const updatedFuel = this.gameState.getFuel();
                        const updatedCapacity = this.gameState.getFuelTankCapacity();
                        const tankIsFull = updatedFuel >= updatedCapacity;

                        if (tankIsFull) {
                            fillButton.textContent = 'FULL';
                            fillButton.disabled = true;
                            fillButton.style.background = 'rgba(255,255,255,0.05)';
                            fillButton.style.borderColor = 'rgba(255,255,255,0.1)';
                            fillButton.style.color = 'rgba(255,255,255,0.3)';
                            fillButton.style.cursor = 'default';

                            // Also disable + button
                            const plusBtn = document.querySelector('.cf-fuel-plus-btn');
                            if (plusBtn) {
                                plusBtn.disabled = true;
                                plusBtn.style.background = 'rgba(255,255,255,0.05)';
                                plusBtn.style.borderColor = 'rgba(255,255,255,0.1)';
                                plusBtn.style.color = 'rgba(255,255,255,0.3)';
                                plusBtn.style.cursor = 'default';
                            }
                        } else {
                            // Update with max affordable cost
                            const maxAffordable = this.gameState.getMaxAffordableFuel();
                            const canAffordAny = maxAffordable > updatedFuel;
                            const affordableCost = this.gameState.getPartialRefuelCost(maxAffordable);

                            if (canAffordAny && affordableCost !== null) {
                                fillButton.textContent = `FILL ${affordableCost.toLocaleString()} MI`;
                                fillButton.style.color = fuelColor;
                                fillButton.style.borderColor = 'rgba(196, 165, 53, 0.5)';
                                fillButton.style.background = 'rgba(196, 165, 53, 0.15)';
                            } else {
                                const fullCost = this.gameState.getRefuelCost();
                                if (fullCost !== null) {
                                    fillButton.textContent = `FILL ${fullCost.toLocaleString()} MI`;
                                } else {
                                    fillButton.textContent = 'FULL';
                                }
                                fillButton.style.color = 'rgba(255,255,255,0.4)';
                                fillButton.style.borderColor = 'rgba(255,255,255,0.15)';
                                fillButton.style.background = 'rgba(255,255,255,0.05)';
                            }
                        }
                    }

                    closeModal();
                }
            }
        });
    }

    /**
     * Get detailed information for driver skill tooltips
     */
    _getDriverSkillDetails(type, level) {
        const details = { description: '', currentDisplay: '', mechanic: '', improvement: '' };

        switch (type) {
            case 'fuelTank': {
                details.description = 'Increases maximum fuel capacity for offline driving. Larger tank = longer simulation time when fueled up.';
                const baseTime = 30 * 60; // 30 min
                const maxTime = 12 * 60 * 60; // 12 hours
                const time = baseTime + (maxTime - baseTime) * (level / 10);
                details.currentDisplay = this._formatDuration(time);
                details.mechanic = 'Linear scaling';
                if (level < 10) {
                    const nextTime = baseTime + (maxTime - baseTime) * ((level + 1) / 10);
                    const gain = nextTime - time;
                    details.improvement = `+${this._formatDuration(gain)}`;
                }
                break;
            }
            case 'scrapBonus': {
                details.description = 'Bonus Salvage earned when scrapping items and Lots. The bonus is applied on top of the base scrap value.';
                const multiplier = 1 + (level / 10) * 0.5;
                const percent = Math.round((multiplier - 1) * 100);
                details.currentDisplay = `+${percent}%`;
                details.mechanic = '+5% per level';
                if (level < 10) {
                    const nextPercent = Math.round(((1 + ((level + 1) / 10) * 0.5) - 1) * 100);
                    details.improvement = `+${nextPercent - percent}%`;
                }
                break;
            }
            case 'itemUpgradeBonus': {
                details.description = 'Increases the stat boost items gain from quality fusion. Affects all future fusions.';
                const multiplier = 1 + (level / 10) * 0.25;
                details.currentDisplay = `${multiplier.toFixed(2)}x`;
                details.mechanic = 'Fusion stat multiplier';
                if (level < 10) {
                    const nextMultiplier = 1 + ((level + 1) / 10) * 0.25;
                    const gain = nextMultiplier - multiplier;
                    details.improvement = `+${gain.toFixed(2)}x`;
                }
                break;
            }
            case 'waveLuck': {
                details.description = 'Multiplies the quality of newly spawned Lots, increasing the chance of finding higher tier items inside.';
                const bonus = (level / 10) * 0.50; // 0.0 to 0.50 matches DriverProgressManager
                const multiplier = 1 + bonus;
                details.currentDisplay = `${multiplier.toFixed(2)}x`;
                details.mechanic = 'Quality multiplier';
                if (level < 10) {
                    const nextMultiplier = 1 + ((level + 1) / 10) * 0.50;
                    const gain = nextMultiplier - multiplier;
                    details.improvement = `+${gain.toFixed(2)}x`;
                }
                break;
            }
        }

        return details;
    }

    /**
     * Format duration in seconds to human-readable string
     */
    _formatDuration(seconds) {
        return StatsFormatter.formatDurationValue(seconds);
    }

    /**
     * Build a single upgrade item row
     */
    _buildUpgradeItem(type, maxLevel, overlay, milesDisplay, upgradesContent) {
        const upgrade = this.gameState.state.cars[this.gameState.state.currentCarId];
        const upgradeInfo = this.gameState.upgrades[type];
        const currentLevel = upgrade.upgradeLevels[type] || 0;
        const cost = this.gameState.getUpgradeCost(type);
        const canAfford = this.gameState.canAffordUpgrade(type);
        const atMaxLevel = currentLevel >= maxLevel;
        const carConfig = this.gameState.carsConfig[this.gameState.state.currentCarId];
        const upgradeName = upgradeInfo.name || upgradeInfo.description;

        const upgradeItem = document.createElement('div');
        upgradeItem.className = `cf-car-upgrade${atMaxLevel ? ' maxed' : ''}`;

        // Left side - upgrade info with description tooltip
        const infoSection = document.createElement('div');
        infoSection.className = 'cf-car-upgrade-info';

        const nameRow = document.createElement('div');
        nameRow.className = 'cf-car-upgrade-name-row';

        const upgradeTitleName = document.createElement('span');
        upgradeTitleName.className = 'cf-car-upgrade-name';
        upgradeTitleName.textContent = upgradeName.toUpperCase();
        upgradeTitleName.style.cursor = 'pointer';
        nameRow.appendChild(upgradeTitleName);

        // Info icon with tooltip (always visible, next to name)
        const { icon: infoIcon, showTooltip, hideTooltip } = this._createStatTooltip(type, currentLevel, carConfig, upgradeName, upgradeInfo.description);
        infoIcon.className = 'cf-car-info-btn';
        infoIcon.textContent = 'i';
        nameRow.appendChild(infoIcon);

        // Also trigger tooltip on name click/touch
        upgradeTitleName.addEventListener('click', (e) => {
            e.stopPropagation();
            showTooltip(upgradeTitleName);
        });
        upgradeTitleName.addEventListener('mouseenter', () => showTooltip(upgradeTitleName));
        upgradeTitleName.addEventListener('mouseleave', hideTooltip);

        infoSection.appendChild(nameRow);

        upgradeItem.appendChild(infoSection);

        // Level display
        const upgradeTitle = document.createElement('div');
        upgradeTitle.className = 'cf-car-upgrade-level';
        upgradeTitle.textContent = `LV ${currentLevel}/${maxLevel}`;
        upgradeItem.appendChild(upgradeTitle);

        // Stat value
        const statSection = document.createElement('div');
        statSection.className = 'cf-car-upgrade-stat';

        const statsText = document.createElement('span');
        statsText.className = `cf-car-upgrade-value${atMaxLevel ? ' maxed' : ''}`;
        statsText.textContent = StatsFormatter.getCurrentStatValue(type, currentLevel, carConfig, this.gameState);
        statSection.appendChild(statsText);

        upgradeItem.appendChild(statSection);

        // Action button
        let purchaseButton = null;

        if (atMaxLevel) {
            const maxLabel = document.createElement('div');
            maxLabel.className = 'cf-car-upgrade-btn maxed';
            maxLabel.textContent = 'MAX';
            upgradeItem.appendChild(maxLabel);
        } else {
            const button = document.createElement('button');
            button.className = `cf-car-upgrade-btn ${canAfford ? 'can-afford' : 'cannot-afford'}`;
            button.textContent = `${cost.toLocaleString()} MI`;

            button.addEventListener('click', () => {
                this._handleUpgradePurchase(type, upgradeName, upgradeTitle, statsText,
                    milesDisplay, button, maxLevel, carConfig, overlay, upgradesContent);
            });
            button.disabled = !canAfford;

            purchaseButton = { button, type, cost };
            upgradeItem.appendChild(button);
        }

        return { upgradeItem, purchaseButton };
    }

    /**
     * Handle upgrade purchase
     */
    _handleUpgradePurchase(type, upgradeName, upgradeTitle, statsText, milesDisplay,
                           purchaseButton, maxLevel, carConfig, overlay, upgradesContent) {
        // Block purchases only during tier modal (not during particle animations)
        if (this.tierModalActive) {
            return;
        }

        const oldProgress = this.gameState.getCarTierProgress();
        const oldTier = oldProgress.currentTier;

        // Capture button position BEFORE purchase (purchase triggers stat invalidation which rebuilds UI)
        const buttonRect = purchaseButton.getBoundingClientRect();

        // Use upgradeSystem if available, fallback to gameState for backward compatibility
        const purchased = this.upgradeSystem
            ? this.upgradeSystem.purchaseUpgrade(type)
            : this.gameState.purchaseUpgrade(type);

        if (purchased) {
            // Play upgrade sound
            this.sfxManager?.playSound('upgrade');

            const newProgress = this.gameState.getCarTierProgress();
            const newLevel = this.gameState.getCurrentCar().upgradeLevels[type] || 0;
            const newMiles = this.gameState.getMiles();
            const newCost = this.gameState.getUpgradeCost(type);
            const newAtMaxLevel = newLevel >= maxLevel;

            // Update displays
            upgradeTitle.textContent = `LV ${newLevel}/${maxLevel}`;
            statsText.textContent = StatsFormatter.getCurrentStatValue(type, newLevel, carConfig, this.gameState);
            statsText.style.color = newAtMaxLevel ? 'rgba(255, 180, 100, 0.9)' : 'rgba(120, 200, 150, 0.9)';
            // Use animator for miles display (triggers pip sounds)
            this.mileAnimator.targetValue = Math.floor(newMiles);
            this._startMileAnimation();

            // Update button - use CSS classes consistently
            if (newAtMaxLevel) {
                purchaseButton.textContent = 'MAX';
                purchaseButton.disabled = true;
                purchaseButton.className = 'cf-car-upgrade-btn maxed';
            } else if (newCost !== null) {
                const canAffordNew = newMiles >= newCost;
                purchaseButton.textContent = `${newCost.toLocaleString()} MI`;
                purchaseButton.disabled = !canAffordNew;
                purchaseButton.className = `cf-car-upgrade-btn ${canAffordNew ? 'can-afford' : 'cannot-afford'}`;
            }

            // Get tier meter elements
            const progressContainer = document.getElementById('tier-progress-container');
            const tierBadge = document.getElementById('car-tier-badge');
            const progressText = document.getElementById('tier-progress-text');
            const tierChanged = oldTier !== newProgress.currentTier;
            const oldTierColor = progressContainer?.dataset.tierColor || '#5FB3B3';
            const newTierColor = this.gameState.getTierColor();

            // Get tier meter position for particle target
            const tierMeterRect = progressContainer?.getBoundingClientRect();
            const targetX = tierMeterRect ? tierMeterRect.left + tierMeterRect.width / 2 : 0;
            const targetY = tierMeterRect ? tierMeterRect.top + tierMeterRect.height / 2 : 0;

            // Only shake if vectorized and bar is completely full
            const isMaxedVectorized = newProgress.currentTier === 'vectorized' && !newProgress.nextTier;

            // Suppress stat invalidation rebuilds during animation
            this._animationInProgress = true;

            // Get segments for animation
            const segments = progressContainer?.querySelectorAll('.cf-car-tier-segment');

            // Start segment animation after particles arrive (flight duration = 600ms)
            const flightDuration = 600;
            const lerpDuration = 800; // Total time to lerp progress

            setTimeout(() => {
                if (!segments || segments.length === 0) return;

                // Animate progress lerp using requestAnimationFrame
                const startTime = performance.now();
                const startProgress = oldProgress.progress;

                // If tier changed, first animate to 100%, then reset and animate to new progress
                const endProgress = tierChanged ? 1.0 : newProgress.progress;

                const animateProgress = (currentTime) => {
                    const elapsed = currentTime - startTime;
                    const t = Math.min(elapsed / lerpDuration, 1);
                    // Ease out cubic for smooth deceleration
                    const eased = 1 - Math.pow(1 - t, 3);

                    const currentProgress = startProgress + (endProgress - startProgress) * eased;

                    // Update each segment based on current progress
                    this._updateSegments(segments, currentProgress, oldTierColor);

                    if (t < 1) {
                        requestAnimationFrame(animateProgress);
                    } else if (tierChanged) {
                        // Tier change complete - celebrate, reset, and fill with new tier
                        // Store tier upgrade info for modal display after animation
                        this.pendingTierUpgrade = {
                            oldTier,
                            newTier: newProgress.currentTier,
                            oldMultiplier: getTierStatMultiplier(oldTier),
                            newMultiplier: getTierStatMultiplier(newProgress.currentTier)
                        };
                        this._celebrateTierChange(segments, tierBadge, progressText, newProgress, newTierColor);
                    } else {
                        // Animation complete - update text
                        if (progressText) {
                            progressText.textContent = newProgress.nextTier
                                ? `${Math.round(newProgress.milesNeeded).toLocaleString()} MI TO ${newProgress.nextTier.toUpperCase()}`
                                : 'MAX TIER';
                        }
                        this._animationInProgress = false;
                    }
                };

                requestAnimationFrame(animateProgress);
            }, flightDuration);

            // Create particles
            this.particles.createParticles({
                startX: buttonRect.left + buttonRect.width / 2,
                startY: buttonRect.top + buttonRect.height / 2,
                target: { x: targetX, y: targetY },
                targetElementSelector: '#tier-progress-container',
                shakeIntensity: isMaxedVectorized ? 1.0 : 0,
                onComplete: () => {
                    // Particles arrived - segment animation handles the rest
                }
            });
        }
    }

    /**
     * Update segment visuals based on current progress value
     * @param {NodeList} segments - The 10 segment elements
     * @param {number} progress - Current progress 0.0 to 1.0
     * @param {string} tierColor - The tier color hex
     */
    _updateSegments(segments, progress, tierColor) {
        segments.forEach((segment, i) => {
            const segmentStart = i * 0.1;
            const segmentEnd = (i + 1) * 0.1;
            const wasFilled = segment.classList.contains('filled');

            // Remove previous state classes
            segment.classList.remove('active');

            if (progress >= segmentEnd) {
                // Fully filled segment
                segment.classList.add('filled');
                segment.style.opacity = '1';
                segment.style.background = tierColor;
            } else if (progress > segmentStart) {
                // Partially filled (active/leading segment)
                const segmentFill = (progress - segmentStart) / 0.1;
                const opacity = 0.15 + (segmentFill * 0.85); // 15% to 100%

                // If transitioning from empty to filled, disable transition briefly to avoid flash
                if (!wasFilled) {
                    segment.style.transition = 'none';
                    segment.style.opacity = opacity.toString();
                    segment.style.background = tierColor;
                    // Force reflow then re-enable transitions
                    segment.offsetHeight;
                    segment.style.transition = '';
                } else {
                    segment.style.opacity = opacity.toString();
                    segment.style.background = tierColor;
                }

                segment.classList.add('filled', 'active');
            } else {
                // Empty segment
                segment.classList.remove('filled');
                segment.style.background = '';
                segment.style.opacity = '1';
            }
        });
    }

    /**
     * Handle tier change celebration and reset
     */
    _celebrateTierChange(segments, tierBadge, progressText, newProgress, newTierColor) {
        // Play tier up sound
        this.sfxManager?.playSound('levelAvail');

        // Celebration animation - all segments pulse
        segments.forEach((segment, i) => {
            setTimeout(() => {
                segment.classList.add('celebrating');
            }, i * 30); // Stagger the celebration
        });

        // Update tier badge (left side - current tier)
        if (tierBadge) {
            tierBadge.textContent = newProgress.currentTier.toUpperCase();
            tierBadge.style.color = newTierColor;
        }

        // Update next tier label (right side)
        const nextTierLabel = document.getElementById('next-tier-label');
        if (nextTierLabel) {
            nextTierLabel.textContent = newProgress.nextTier
                ? newProgress.nextTier.toUpperCase()
                : 'MAX';
            const nextTierColor = newProgress.nextTier
                ? this.gameState.getTierColorByName(newProgress.nextTier)
                : newTierColor;
            nextTierLabel.style.color = nextTierColor;
        }

        // Create celebration particle burst from the tier meter
        const container = segments[0]?.parentElement;
        if (container) {
            const rect = container.getBoundingClientRect();
            this.particles.createParticles({
                preset: 'celebration',
                startX: rect.left + rect.width / 2,
                startY: rect.top + rect.height / 2,
                color: newTierColor,
            });
        }

        // After celebration, reset and fill with new tier progress
        setTimeout(() => {
            // Reset all segments to empty
            segments.forEach(segment => {
                segment.classList.remove('filled', 'active', 'celebrating');
                segment.style.background = '';
                segment.style.opacity = '1';
            });

            // Update the stored tier color
            if (container) {
                container.dataset.tierColor = newTierColor;
            }

            // Animate to new tier's progress
            const startTime = performance.now();
            const lerpDuration = 600;

            const animateNewTier = (currentTime) => {
                const elapsed = currentTime - startTime;
                const t = Math.min(elapsed / lerpDuration, 1);
                const eased = 1 - Math.pow(1 - t, 3);

                const currentProgress = newProgress.progress * eased;
                this._updateSegments(segments, currentProgress, newTierColor);

                if (t < 1) {
                    requestAnimationFrame(animateNewTier);
                } else {
                    // Update progress text
                    if (progressText) {
                        progressText.textContent = newProgress.nextTier
                            ? `${Math.round(newProgress.milesNeeded).toLocaleString()} MI TO ${newProgress.nextTier.toUpperCase()}`
                            : 'MAX TIER';
                    }
                    this._animationInProgress = false;

                    // Show tier upgrade modal after animation completes
                    if (this.pendingTierUpgrade) {
                        this._showTierUpgradeModal(this.pendingTierUpgrade);
                        this.pendingTierUpgrade = null;
                    }
                }
            };

            requestAnimationFrame(animateNewTier);
        }, 500); // Wait for celebration to finish
    }

    /**
     * Create stat breakdown tooltip
     */
    _createStatTooltip(type, currentLevel, carConfig, upgradeName, description = null) {
        const icon = document.createElement('button');
        icon.className = 'cf-car-info-btn';
        icon.textContent = 'i';

        const tooltip = document.createElement('div');
        tooltip.className = 'upgrade-tooltip cf-car-tooltip';
        tooltip.style.display = 'none';

        const breakdown = StatsFormatter.getStatBreakdown(type, currentLevel, carConfig, this.gameState);
        const formatVal = (v) => {
            // Time-based stats show with 1 decimal for consistency with stat display
            if (type === 'cruiseDuration' || type === 'cruiseCooldown') return v.toFixed(1);
            // Rate-based stats show three decimal places (small values)
            if (type === 'momentumRate') return v.toFixed(3);
            // All other stats show one decimal place for consistency
            return v.toFixed(1);
        };

        // Build tooltip content using CSS classes
        // Use description as header if provided, otherwise fall back to breakdown title
        const headerText = description || `${upgradeName.toUpperCase()} BREAKDOWN`;
        let html = `<div class="cf-car-tooltip-title">${headerText}</div>`;

        // Base value
        html += `<div class="cf-car-tooltip-row"><span class="cf-car-tooltip-label">Base</span><span class="cf-car-tooltip-value">${formatVal(breakdown.baseValue)} ${breakdown.unit}</span></div>`;

        if (breakdown.upgradeBonus !== 0) {
            const sign = breakdown.upgradeBonus > 0 ? '+' : '';
            html += `<div class="cf-car-tooltip-row"><span class="cf-car-tooltip-label">Upgrades (LV${breakdown.level})</span><span class="cf-car-tooltip-value positive">${sign}${formatVal(breakdown.upgradeBonus)} ${breakdown.unit}</span></div>`;
        }

        if (breakdown.tierBonus !== 0) {
            const sign = breakdown.tierBonus > 0 ? '+' : '';
            const tierName = this.gameState.getCarTier();
            const tierNameCapitalized = tierName.charAt(0).toUpperCase() + tierName.slice(1);
            html += `<div class="cf-car-tooltip-row"><span class="cf-car-tooltip-label">${tierNameCapitalized} Bonus</span><span class="cf-car-tooltip-value positive">${sign}${formatVal(breakdown.tierBonus)} ${breakdown.unit}</span></div>`;
        }

        if (breakdown.prestigeBonusValue !== 0) {
            const sign = type === 'cruiseCooldown' ? '-' : '+';
            const prestigePercent = (breakdown.prestigeBonus * 100).toFixed(1);
            html += `<div class="cf-car-tooltip-row"><span class="cf-car-tooltip-label">Prestige (${prestigePercent}%)</span><span class="cf-car-tooltip-value positive">${sign}${formatVal(Math.abs(breakdown.prestigeBonusValue))} ${breakdown.unit}</span></div>`;
        }

        if (breakdown.ab !== 0) {
            const sign = breakdown.ab > 0 ? '+' : '';
            html += `<div class="cf-car-tooltip-row"><span class="cf-car-tooltip-label">Ascension Bonus</span><span class="cf-car-tooltip-value positive">${sign}${formatVal(breakdown.ab)} ${breakdown.unit}</span></div>`;
        }

        // Item effects (additive)
        if (breakdown.itemAddBonus !== 0) {
            const sign = breakdown.itemAddBonus > 0 ? '+' : '';
            html += `<div class="cf-car-tooltip-row"><span class="cf-car-tooltip-label">Items</span><span class="cf-car-tooltip-value positive">${sign}${formatVal(breakdown.itemAddBonus)} ${breakdown.unit}</span></div>`;
        }

        // Item effects (multiplicative)
        if (breakdown.itemMultiplyBonus !== 0) {
            const sign = breakdown.itemMultiplyBonus > 0 ? '+' : '';
            const multPercent = Math.round((breakdown.itemMultiplier - 1) * 100);
            html += `<div class="cf-car-tooltip-row"><span class="cf-car-tooltip-label">Items (${multPercent}%)</span><span class="cf-car-tooltip-value positive">${sign}${formatVal(breakdown.itemMultiplyBonus)} ${breakdown.unit}</span></div>`;
        }

        // Collection milestone bonus
        if (breakdown.collectionBonus && breakdown.collectionBonus !== 0) {
            const sign = breakdown.collectionBonus > 0 ? '+' : '';
            html += `<div class="cf-car-tooltip-row"><span class="cf-car-tooltip-label">Collection</span><span class="cf-car-tooltip-value positive">${sign}${formatVal(breakdown.collectionBonus)} ${breakdown.unit}</span></div>`;
        }

        // Total
        const totalDisplay = formatVal(breakdown.total);
        const isAtMinCooldown = type === 'cruiseCooldown' && Math.round(breakdown.total) <= 5;
        html += `<div class="cf-car-tooltip-total"><span class="cf-car-tooltip-label">TOTAL</span><span class="cf-car-tooltip-value">${totalDisplay} ${breakdown.unit}${isAtMinCooldown ? ' (MIN)' : ''}</span></div>`;

        // Show minimum floor note for cruiseCooldown
        if (type === 'cruiseCooldown') {
            html += `<div class="cf-car-tooltip-row" style="margin-top: var(--cf-space-sm); padding-top: var(--cf-space-sm); border-top: 1px solid var(--cf-divider);"><span class="cf-car-tooltip-label" style="color: var(--cf-text-tertiary);">Minimum cooldown: 5s</span></div>`;
        }

        if (breakdown.nextLevelBonus !== null) {
            const nextSign = breakdown.nextLevelBonus > 0 ? '+' : '';
            html += `<div class="cf-car-tooltip-row" style="margin-top: var(--cf-space-sm); padding-top: var(--cf-space-sm); border-top: 1px solid var(--cf-divider);"><span class="cf-car-tooltip-label">Next Level</span><span class="cf-car-tooltip-value positive">${nextSign}${formatVal(breakdown.nextLevelBonus)} ${breakdown.unit}</span></div>`;
        }

        tooltip.innerHTML = html;
        document.body.appendChild(tooltip);

        // Helper to show tooltip
        const showTooltip = (anchor) => {
            document.querySelectorAll('.upgrade-tooltip').forEach(t => t.style.display = 'none');
            tooltip.style.display = 'block';
            this.gameUI.positionTooltip(tooltip, anchor);
        };

        // Helper to hide tooltip
        const hideTooltip = () => {
            tooltip.style.display = 'none';
        };

        // Icon hover events
        icon.addEventListener('mouseenter', () => showTooltip(icon));
        icon.addEventListener('mouseleave', hideTooltip);

        // Icon touch/click support
        icon.addEventListener('click', (e) => {
            e.stopPropagation();
            const isVisible = tooltip.style.display === 'block';
            if (isVisible) {
                hideTooltip();
            } else {
                showTooltip(icon);
            }
        });

        return { icon, tooltip, showTooltip, hideTooltip };
    }

    /**
     * Create prestige badge tooltip showing all prestige bonuses
     * @param {HTMLElement} badge - The prestige badge element
     * @param {object} prestigeInfo - The prestige info object from gameState
     * @returns {object} { tooltip, showTooltip, hideTooltip }
     */
    _createPrestigeTooltip(badge, prestigeInfo) {
        const tooltip = document.createElement('div');
        tooltip.className = 'upgrade-tooltip cf-car-tooltip';
        tooltip.style.display = 'none';

        // Build tooltip content
        let html = `<div class="cf-car-tooltip-title">PRESTIGE ${prestigeInfo.level} BONUSES</div>`;

        // Mile Earning Multiplier (always shown)
        const mileBonus = prestigeInfo.mileEarningBonus || 0;
        html += `<div class="cf-car-tooltip-row"><span class="cf-car-tooltip-label">Mile Earning</span><span class="cf-car-tooltip-value positive">${PrestigeSystem.formatBonusMultiplier(mileBonus)}</span></div>`;

        // Skill bonuses (only show non-zero)
        const skillBonuses = prestigeInfo.skillBonuses || {};
        let hasSkillBonuses = false;

        Object.entries(skillBonuses).forEach(([skillId, bonus]) => {
            if (bonus > 0) {
                hasSkillBonuses = true;
                const skillInfo = PrestigeSystem.SELECTABLE_SKILLS[skillId];
                const skillName = skillInfo ? skillInfo.name : skillId;
                // cruiseCooldown reduces (negative effect display), others increase
                const displaySign = skillId === 'cruiseCooldown' ? '-' : '+';
                html += `<div class="cf-car-tooltip-row"><span class="cf-car-tooltip-label">${skillName}</span><span class="cf-car-tooltip-value positive">${displaySign}${(bonus * 100).toFixed(1)}%</span></div>`;
            }
        });

        // If no skill bonuses yet, show a hint
        if (!hasSkillBonuses) {
            html += `<div class="cf-car-tooltip-row" style="margin-top: var(--cf-space-sm);"><span class="cf-car-tooltip-label" style="color: var(--cf-text-tertiary);">No skill bonuses selected yet</span></div>`;
        }

        tooltip.innerHTML = html;
        document.body.appendChild(tooltip);

        // Helper to show tooltip
        const showTooltip = () => {
            document.querySelectorAll('.upgrade-tooltip').forEach(t => t.style.display = 'none');
            tooltip.style.display = 'block';
            this.gameUI.positionTooltip(tooltip, badge);
        };

        // Helper to hide tooltip
        const hideTooltip = () => {
            tooltip.style.display = 'none';
        };

        // Badge hover events
        badge.addEventListener('mouseenter', showTooltip);
        badge.addEventListener('mouseleave', hideTooltip);

        // Badge touch/click support
        badge.addEventListener('click', (e) => {
            e.stopPropagation();
            const isVisible = tooltip.style.display === 'block';
            if (isVisible) {
                hideTooltip();
            } else {
                showTooltip();
            }
        });

        // Make badge look clickable
        badge.style.cursor = 'pointer';

        return { tooltip, showTooltip, hideTooltip };
    }

    /**
     * Build ascend item (next car unlock)
     */
    _buildAscendItem(nextCar, overlay) {
        const item = document.createElement('div');
        item.className = 'cf-car-ascend';

        // "ASCEND TO" label
        const label = document.createElement('span');
        label.className = 'cf-car-ascend-label';
        label.textContent = 'ASCEND TO ';
        item.appendChild(label);

        // Car name with pink styling
        const title = document.createElement('span');
        title.className = 'cf-car-ascend-name';
        title.textContent = nextCar.config.name.toUpperCase();
        item.appendChild(title);

        // Purchase button with cost (prestige-scaled)
        const canAfford = this.gameState.canAffordCar(nextCar.id);
        const scaledCost = this.gameState.getCarUnlockCost(nextCar.id);
        const button = document.createElement('button');
        button.className = `cf-car-ascend-btn ${canAfford ? 'can-afford' : 'cannot-afford'}`;
        button.textContent = `${scaledCost.toLocaleString()} MI`;

        button.addEventListener('click', (e) => {
            e.stopPropagation();
            if (this.gameState.canAffordCar(nextCar.id)) {
                this.gameUI.showAscensionScreen(nextCar.id, overlay);
            }
        });

        item.appendChild(button);

        return {
            item,
            button: { button, type: 'ascend', carId: nextCar.id, cost: scaledCost }
        };
    }

    /**
     * Build prestige item (when on final car)
     */
    _buildPrestigeItem(overlay) {
        const prestigeLevel = this.gameState.state.prestige?.level || 0;
        const tierColor = PrestigeSystem.getPrestigeTierColor(prestigeLevel);

        const item = document.createElement('div');
        item.style.cssText = `
            background: rgba(50, 30, 60, 0.85);
            border: 1px solid ${prestigeLevel > 0 ? tierColor + '66' : 'rgba(200, 150, 255, 0.4)'};
            border-radius: 8px;
            padding: 12px 15px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 15px;
        `;

        const leftSide = document.createElement('div');
        leftSide.style.cssText = `flex: 1; min-width: 0;`;

        const title = document.createElement('div');
        title.textContent = prestigeLevel > 0 ? `PRESTIGE (Level ${prestigeLevel})` : 'PRESTIGE';
        title.style.cssText = `
            font-size: 14px;
            font-weight: normal;
            letter-spacing: 0.5px;
            margin-bottom: 4px;
            line-height: 1.2;
            color: ${prestigeLevel > 0 ? tierColor : 'rgba(200, 150, 255, 0.95)'};
        `;
        leftSide.appendChild(title);

        const desc = document.createElement('div');
        desc.textContent = 'Reset progress for permanent bonuses';
        desc.style.cssText = `font-size: 11px; color: rgba(255, 255, 255, 0.55); line-height: 1.2;`;
        leftSide.appendChild(desc);
        item.appendChild(leftSide);

        const rightSide = document.createElement('div');
        rightSide.style.cssText = `display: flex; align-items: center; gap: 10px; flex-shrink: 0;`;

        const button = document.createElement('button');
        button.textContent = 'VIEW';
        button.style.cssText = `
            font-family: 'Courier New', monospace;
            font-size: 11px;
            font-weight: normal;
            letter-spacing: 0.5px;
            padding: 6px 12px;
            background: rgba(120, 80, 160, 0.85);
            color: rgba(255, 255, 255, 0.95);
            border: 1px solid rgba(200, 150, 255, 0.5);
            border-radius: 5px;
            cursor: pointer;
            white-space: nowrap;
            touch-action: manipulation;
            transition: all 0.25s ease;
        `;

        button.addEventListener('click', (e) => {
            e.stopPropagation();
            this.gameUI.showPrestigeScreen(overlay);
        });

        rightSide.appendChild(button);
        item.appendChild(rightSide);

        return item;
    }

    /**
     * Build inventory content tab
     * Fixed equipped section at top, scrollable items list below
     */
    _buildInventoryContent(container, parentOverlay) {
        if (!this.app.itemsData) {
            const loadingMsg = document.createElement('div');
            loadingMsg.style.cssText = 'color: rgba(255, 255, 255, 0.5); text-align: center; padding: 40px;';
            loadingMsg.textContent = 'Loading items...';
            container.appendChild(loadingMsg);
            return;
        }

        const itemsData = this.app.itemsData;
        const inventoryItems = this.gameState.getInventoryItems();
        const equippedSlots = this.gameState.getEquippedItems();

        if (inventoryItems.length === 0) {
            const emptyMsg = document.createElement('div');
            emptyMsg.style.cssText = 'color: rgba(255, 255, 255, 0.5); text-align: center; padding: 40px;';
            emptyMsg.textContent = 'No items collected yet. Open PICKUPS to get started!';
            container.appendChild(emptyMsg);
            return;
        }

        // Fixed equipped section (doesn't scroll)
        const equippedSection = this._buildEquippedSection(itemsData, equippedSlots, container, parentOverlay);
        equippedSection.style.flexShrink = '0';
        container.appendChild(equippedSection);

        // Scrollable all items section
        const scrollableSection = document.createElement('div');
        scrollableSection.className = 'cf-inventory-scroll';
        scrollableSection.style.cssText = `
            flex: 1;
            min-height: 0;
            overflow-y: auto;
            overflow-x: hidden;
            padding-right: 10px;
        `;
        scrollableSection.appendChild(this._buildAllItemsSection(itemsData, inventoryItems, equippedSlots, container, parentOverlay));
        container.appendChild(scrollableSection);
    }

    /**
     * Helper to rebuild inventory content (clears and rebuilds)
     * Preserves scroll position of the items list
     */
    _rebuildInventory(container, parentOverlay) {
        // Find the scrollable section and save its scroll position
        const scrollableSection = container.querySelector('.cf-inventory-scroll');
        const scrollTop = scrollableSection?.scrollTop || 0;

        this._clearChildren(container);
        this._buildInventoryContent(container, parentOverlay);

        // Restore scroll position after rebuild
        requestAnimationFrame(() => {
            const newScrollable = container.querySelector('.cf-inventory-scroll');
            if (newScrollable) {
                newScrollable.scrollTop = scrollTop;
            }

            // Clear newly fused badge after it's been shown once
            if (this._newlyFusedItemId) {
                setTimeout(() => {
                    this._newlyFusedItemId = null;
                }, 3000); // Keep badge visible for 3 seconds
            }
        });
    }

    /**
     * Rebuild the upgrades tab content
     */
    _rebuildUpgradesTab() {
        if (!this.upgradesContentEl || !this.overlayEl) return;
        this._clearChildren(this.upgradesContentEl);
        this._buildUpgradesTab(this.upgradesContentEl, this.overlayEl, this.menuMilesDisplay);
    }

    /**
     * Build equipped items section - compact 3-column layout
     * Uses horizontal layout on desktop and phone landscape, vertical on phone portrait
     */
    _buildEquippedSection(itemsData, equippedSlots, container, parentOverlay) {
        const isPhone = this.gameUI.isPhoneScreen();
        const isLandscape = this.gameUI.isLandscape();
        const isPhoneLandscape = isPhone && isLandscape;

        const section = document.createElement('div');
        section.style.cssText = `
            margin-bottom: var(--cf-space-md, 12px);
        `;

        // Section header
        const header = document.createElement('div');
        header.style.cssText = `
            display: flex;
            align-items: center;
            gap: var(--cf-space-sm, 8px);
            margin-bottom: var(--cf-space-sm, 8px);
            padding-bottom: var(--cf-space-xs, 4px);
            border-bottom: 1px solid var(--cf-divider, rgba(255,255,255,0.08));
        `;
        const headerLabel = document.createElement('span');
        headerLabel.textContent = 'EQUIPPED';
        headerLabel.style.cssText = `
            font-size: var(--cf-font-size-xs, 10px);
            font-weight: 500;
            color: var(--cf-text-tertiary, rgba(255,255,255,0.4));
            letter-spacing: 1px;
        `;
        header.appendChild(headerLabel);
        section.appendChild(header);

        // 3-column grid for slots
        const grid = document.createElement('div');
        grid.style.cssText = `
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: ${isPhoneLandscape ? '1px' : '1px'};
            background: var(--cf-divider, rgba(255,255,255,0.08));
        `;

        ['dashboard', 'keychain', 'rearview'].forEach(slotType => {
            grid.appendChild(this._buildEquipSlot(slotType, equippedSlots[slotType], itemsData, container, parentOverlay));
        });

        section.appendChild(grid);
        return section;
    }

    /**
     * Build single equipment slot - compact tile
     * Shows slot name, item name with level, upgrade/unequip buttons
     * Entire slot is clickable to show stats tooltip
     */
    _buildEquipSlot(slotType, item, itemsData, container, parentOverlay) {
        const isPhone = this.gameUI.isPhoneScreen();
        const isLandscape = this.gameUI.isLandscape();
        const isPhoneLandscape = isPhone && isLandscape;
        const rarityColor = item ? itemsData.rarityColors[item.rarity] : 'var(--cf-text-tertiary, rgba(255,255,255,0.4))';
        const isUnseen = item ? this.gameState.isItemUnseen(item.id) : false;

        const slot = document.createElement('div');
        slot.dataset.equipSlot = slotType;
        slot.style.cssText = `
            position: relative;
            background: var(--cf-bg-primary, #0D0D12);
            padding: ${isPhoneLandscape ? '8px 6px' : isPhone ? '10px 8px' : '12px 10px'};
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: ${isPhoneLandscape ? '4px' : '6px'};
            min-width: 0;
            cursor: ${item ? 'pointer' : 'default'};
            transition: all 0.15s ease;
        `;

        // NEW/FUSED badge for unseen or newly fused equipped items (top right)
        const isNewlyFused = item && this._newlyFusedItemId === item.id;
        if (isUnseen || isNewlyFused) {
            const badge = document.createElement('div');
            badge.className = 'cf-new-badge cf-new-badge--slot';
            badge.textContent = isNewlyFused ? 'FUSED' : 'NEW';
            if (isNewlyFused) {
                badge.style.background = 'var(--cf-cyan, #5FB3B3)';
                badge.style.color = '#000';
            }
            slot.appendChild(badge);
        }

        // Prestige badge for prestiged items (top left)
        if (item && item.prestigeLevel > 0) {
            const prestigeBadge = document.createElement('div');
            prestigeBadge.textContent = `P${item.prestigeLevel}`;
            prestigeBadge.style.cssText = `
                position: absolute;
                top: 4px;
                left: 4px;
                background: var(--cf-gold, #FFD700);
                color: #000;
                font-size: 9px;
                font-weight: 600;
                padding: 2px 5px;
                letter-spacing: 0.5px;
                z-index: 1;
            `;
            slot.appendChild(prestigeBadge);
        }

        // Tooltip for slots with items (hover on desktop, tap-hold on mobile)
        if (item) {
            const tooltip = this._createEquipSlotTooltip(item, itemsData, slotType);
            let tooltipPinnedByUpgrade = false;

            // Desktop: show on hover
            slot.addEventListener('mouseenter', () => {
                slot.style.background = 'var(--cf-bg-raised, #16161E)';
                tooltip.style.display = 'block';
                this.gameUI.positionTooltip(tooltip, slot);
            });
            slot.addEventListener('mouseleave', (e) => {
                slot.style.background = 'var(--cf-bg-primary, #0D0D12)';
                // Don't hide if tooltip is pinned by upgrade click or mouse is moving to tooltip
                if (tooltipPinnedByUpgrade) return;
                if (e.relatedTarget && (e.relatedTarget === tooltip || tooltip.contains(e.relatedTarget))) return;
                tooltip.style.display = 'none';
            });

            // Allow tooltip to stay visible when mouse enters it
            tooltip.addEventListener('mouseleave', (e) => {
                // Hide unless mouse moved back to slot or tooltip is pinned
                if (tooltipPinnedByUpgrade) return;
                if (e.relatedTarget && (e.relatedTarget === slot || slot.contains(e.relatedTarget))) return;
                tooltip.style.display = 'none';
                slot.style.background = 'var(--cf-bg-primary, #0D0D12)';
            });

            // Mobile: toggle on tap (but not on buttons)
            slot.addEventListener('touchstart', (e) => {
                if (e.target.tagName === 'BUTTON') return;
                e.preventDefault();
                const isVisible = tooltip.style.display === 'block';
                // Hide all other item tooltips first
                document.querySelectorAll('.cf-item-tooltip').forEach(t => t.style.display = 'none');
                tooltipPinnedByUpgrade = false;
                if (!isVisible) {
                    tooltip.style.display = 'block';
                    this.gameUI.positionTooltip(tooltip, slot);
                }
            });

            // Store reference for upgrade button to use
            slot._tooltip = tooltip;
            slot._setTooltipPinned = (pinned) => { tooltipPinnedByUpgrade = pinned; };
        }

        // Slot label (always at top)
        const label = document.createElement('div');
        label.textContent = slotType.toUpperCase();
        label.style.cssText = `
            font-size: var(--cf-font-size-xs, 10px);
            font-weight: 500;
            letter-spacing: 1px;
            color: var(--cf-text-tertiary, rgba(255,255,255,0.4));
            text-align: center;
        `;
        slot.appendChild(label);

        if (item) {
            // Item sprite with tier-colored background
            const slotIcons = { 'rearview': '🪞', 'dashboard': '📊', 'keychain': '🔑' };
            const spriteContainer = document.createElement('div');
            spriteContainer.dataset.itemName = item.id;
            spriteContainer.style.cssText = `
                width: 40px;
                height: 40px;
                background: ${rarityColor}33;
                border: 2px solid ${rarityColor};
                border-radius: 6px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 20px;
                flex-shrink: 0;
            `;
            // Use sprite if available, otherwise slot emoji fallback
            const itemSlot = item.slotType || item.slot;
            spriteContainer.textContent = slotIcons[itemSlot] || '📦';
            slot.appendChild(spriteContainer);

            // Hidden effects element for particle animation targeting
            const itemEffects = document.createElement('div');
            itemEffects.dataset.itemEffects = item.id;
            itemEffects.style.display = 'none';
            itemEffects.innerHTML = this._formatItemEffects(item);
            slot.appendChild(itemEffects);

            // Buttons row: upgrade/fusion, unequip
            const buttonRow = document.createElement('div');
            buttonRow.style.cssText = `
                display: flex;
                gap: 8px;
                justify-content: center;
                margin-top: auto;
            `;

            // Legacy items: level upgrade button
            const upgradeCost = this.gameState.getItemUpgradeCost(item.id);
            if (upgradeCost !== null) {
                // Pass tooltip and slot for equipped item upgrades
                // Use slot._tooltip which was set in the tooltip setup block above
                buttonRow.appendChild(this._createItemUpgradeButton(item, itemsData, spriteContainer, itemEffects, true, {
                    tooltip: slot._tooltip,
                    slot,
                    slotType
                }));
            }

            // V2 items: fusion upgrade button (compact icon version)
            if (!item.isLegacy) {
                const fusionBtn = this._createFusionButton(item, itemsData, container, parentOverlay, true);
                if (fusionBtn) {
                    buttonRow.appendChild(fusionBtn);
                }
            }

            // Unequip button
            const unequipBtn = document.createElement('button');
            unequipBtn.textContent = '✕';
            unequipBtn.title = 'Unequip';
            unequipBtn.className = 'cf-unequip-btn';
            unequipBtn.style.cssText = `
                background: transparent;
                border: 1px solid var(--cf-negative, #E86A6A);
                color: var(--cf-negative, #E86A6A);
                width: 28px;
                height: 28px;
                padding: 0;
                cursor: pointer;
                font-size: 14px;
                display: flex;
                align-items: center;
                justify-content: center;
            `;
            unequipBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                // Use inventorySystem if available, fallback to gameState
                if (this.inventorySystem) {
                    this.inventorySystem.unequipSlot(slotType);
                } else {
                    this.gameState.unequipSlot(slotType);
                }
                this._rebuildInventory(container, parentOverlay);
                if (this.app.equipmentManager) {
                    this.app.equipmentManager.updateEquippedItems();
                }
            });
            buttonRow.appendChild(unequipBtn);

            slot.appendChild(buttonRow);
        } else {
            // Empty slot
            const emptyText = document.createElement('div');
            emptyText.textContent = '—';
            emptyText.style.cssText = `
                color: var(--cf-text-tertiary, rgba(255,255,255,0.3));
                font-size: var(--cf-font-size-lg, 16px);
                text-align: center;
                padding: var(--cf-space-sm, 8px) 0;
            `;
            slot.appendChild(emptyText);
        }

        return slot;
    }

    /**
     * Highlight an equipment slot (used when hovering over equip/swap buttons)
     * @param {string} slotType - 'dashboard', 'keychain', or 'rearview'
     * @param {boolean} highlight - true to highlight, false to remove highlight
     */
    _highlightEquipSlot(slotType, highlight) {
        const slot = document.querySelector(`[data-equip-slot="${slotType}"]`);
        if (!slot) return;

        if (highlight) {
            slot.style.background = 'var(--cf-bg-raised, #16161E)';
            slot.style.boxShadow = 'inset 0 0 0 2px var(--cf-cyan, #5FB3B3)';

            // Show equipped item tooltip for comparison (if item exists)
            if (slot._tooltip) {
                slot._tooltip.style.display = 'block';
                this.gameUI.positionTooltip(slot._tooltip, slot);
            }
        } else {
            slot.style.background = 'var(--cf-bg-primary, #0D0D12)';
            slot.style.boxShadow = 'none';

            // Hide tooltip
            if (slot._tooltip) {
                slot._tooltip.style.display = 'none';
            }
        }
    }

    /**
     * Shorten item name for compact display
     */
    _shortenItemName(name) {
        // Remove rarity prefix if present (e.g., "Faded ", "Golden ")
        const rarities = ['Faded ', 'Standard ', 'Pristine ', 'Golden ', 'Vectorized '];
        for (const rarity of rarities) {
            if (name.startsWith(rarity)) {
                name = name.substring(rarity.length);
                break;
            }
        }
        return name;
    }

    /**
     * Create equipment slot tooltip (appended to body, hidden by default)
     */
    _createEquipSlotTooltip(item, itemsData, slotType) {
        const rarityColor = itemsData.rarityColors[item.rarity];
        const effects = this._formatItemEffectsForTooltip(item);

        const tooltip = document.createElement('div');
        tooltip.className = 'cf-item-tooltip cf-car-tooltip';
        tooltip.dataset.tooltipItemId = item.id;
        tooltip.style.display = 'none';

        // Build status line only for legacy items (shows level info)
        let statusLineHtml = '';
        if (item.isLegacy && item.level !== undefined) {
            const statusLine = `Level ${item.level}${item.level >= 10 ? ' (MAX)' : ''} | <span style="color: #f59e0b;">LEGACY</span>`;
            statusLineHtml = `<div class="cf-tooltip-level" data-tooltip-level="${item.id}" style="font-size: 10px; color: rgba(255,255,255,0.5); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;">${statusLine}</div>`;
        }

        tooltip.innerHTML = `
            <div class="cf-car-tooltip-title" style="color: ${rarityColor};">
                ${item.name}
            </div>
            ${statusLineHtml}
            <div class="cf-tooltip-effects" data-tooltip-effects="${item.id}" style="border-top: 1px solid rgba(255,255,255,0.1); padding-top: 8px;">
                ${effects}
            </div>
        `;

        document.body.appendChild(tooltip);
        return tooltip;
    }

    /**
     * Format item effects for tooltip display
     * Uses same row format as upgrade tooltips for consistency
     */
    _formatItemEffectsForTooltip(item) {
        let html = item.effects.map(eff => {
            const stat = StatsFormatter.getStatDisplayName(eff.type);
            const unit = StatsFormatter.getStatUnit(eff.type);
            const isMultiplier = eff.operation === 'multiply' ||
                (eff.operation !== 'add' && (eff.type === 'acceleration' || eff.type === 'timeScale'));
            const isPercentageStat = StatsFormatter.isPercentageStat(eff.type);
            const isMultiplierStat = StatsFormatter.isMultiplierStat(eff.type);
            const isDurationStat = StatsFormatter.isDurationStat(eff.type);

            // For cooldown stats, lower is better (negative = buff)
            const isLowerBetter = eff.type === 'cruiseCooldown';

            let value, color;
            if (isMultiplier) {
                const percent = Math.round((eff.value - 1) * 100);
                value = `${percent >= 0 ? '+' : ''}${percent}%`;
                const isBuff = isLowerBetter ? percent <= 0 : percent >= 0;
                color = isBuff ? 'var(--cf-positive)' : 'var(--cf-negative)';
            } else if (isMultiplierStat) {
                // Display as multiplier (0.10 -> 1.10x)
                const multiplier = 1 + eff.value;
                value = `${multiplier.toFixed(2)}x`;
                color = eff.value >= 0 ? 'var(--cf-positive)' : 'var(--cf-negative)';
            } else if (isPercentageStat) {
                // Display as percentage (0.04 -> +4%)
                const percent = Math.round(eff.value * 100);
                value = `${percent >= 0 ? '+' : ''}${percent}%`;
                color = percent >= 0 ? 'var(--cf-positive)' : 'var(--cf-negative)';
            } else if (isDurationStat) {
                const sign = eff.value >= 0 ? '+' : '';
                value = `${sign}${StatsFormatter.formatDurationValue(Math.abs(eff.value))}`;
                color = eff.value >= 0 ? 'var(--cf-positive)' : 'var(--cf-negative)';
            } else {
                // Momentum rate needs 3 decimal places (small values like 0.005)
                const decimals = eff.type === 'momentumRate' ? 3 : 1;
                const val = Number(eff.value.toFixed(decimals));
                value = `${val >= 0 ? '+' : ''}${val}${unit ? ' ' + unit : ''}`;
                const isBuff = isLowerBetter ? val <= 0 : val >= 0;
                color = isBuff ? 'var(--cf-positive)' : 'var(--cf-negative)';
            }

            let condition = '';
            if (eff.condition?.timeOfDay) {
                condition = ` (${eff.condition.timeOfDay})`;
            }

            // Use same format as upgrade tooltips: label on left, value on right
            return `<div class="cf-car-tooltip-row">
                <span class="cf-car-tooltip-label">${stat}${condition}</span>
                <span class="cf-car-tooltip-value" style="color: ${color};">${value}</span>
            </div>`;
        }).join('');

        // Add prestige bonus stats inline with regular stats (format: "P1 Stat Name" with value)
        const bonusStats = this.gameState.getItemPrestigeBonusStats(item);
        for (const bonus of bonusStats) {
            const statName = StatsFormatter.getStatDisplayName(bonus.type);
            const unit = StatsFormatter.getStatUnit(bonus.type);
            const pLevel = bonus.prestigeLevel || 1;
            // Format based on stat type with units
            let formattedValue;
            if (bonus.type === 'acceleration') {
                formattedValue = `+${Math.round(bonus.value * 100)}%`;
            } else if (bonus.type === 'momentumRate') {
                formattedValue = `+${bonus.value.toFixed(3)}${unit ? ' ' + unit : ''}`;
            } else if (bonus.type === 'cruiseCooldown') {
                formattedValue = `${bonus.value.toFixed(1)}${unit ? ' ' + unit : ''}`;
            } else if (StatsFormatter.isMultiplierStat(bonus.type)) {
                const multiplier = 1 + bonus.value;
                formattedValue = `${multiplier.toFixed(2)}x`;
            } else if (StatsFormatter.isPercentageStat(bonus.type)) {
                formattedValue = `+${Math.round(bonus.value * 100)}%`;
            } else {
                formattedValue = `+${bonus.value.toFixed(1)}${unit ? ' ' + unit : ''}`;
            }
            html += `<div class="cf-car-tooltip-row">
                <span class="cf-car-tooltip-label" style="color: var(--cf-gold, #FFD700);">${formattedValue} ${statName} (P${pLevel})</span>
            </div>`;
        }

        return html;
    }

    /**
     * Format item effects for display (returns HTML with bonus boxes)
     * @param {Object} item - The item to format effects for
     * @param {Object} options - Display options
     * @param {string} options.size - Size variant: 'normal', 'compact', 'xs'
     * @param {string} options.align - Alignment: 'left', 'centered'
     * @param {boolean} options.noMargin - Remove top margin
     * @returns {string} HTML string with bonus boxes
     */
    _formatItemEffects(item, options = {}) {
        const { size = 'compact', align = 'left', noMargin = false } = options;
        const bonuses = [];

        // Format regular item effects
        for (const eff of item.effects) {
            const stat = StatsFormatter.getStatDisplayName(eff.type);
            const unit = StatsFormatter.getStatUnit(eff.type);
            const isMultiplier = eff.operation === 'multiply' ||
                (eff.operation !== 'add' && (eff.type === 'acceleration' || eff.type === 'timeScale'));
            const isPercentageStat = StatsFormatter.isPercentageStat(eff.type);
            const isMultiplierStat = StatsFormatter.isMultiplierStat(eff.type);
            const isDurationStat = StatsFormatter.isDurationStat(eff.type);

            let formatted;
            if (isMultiplier) {
                const percent = Math.round((eff.value - 1) * 100);
                formatted = `${percent >= 0 ? '+' : ''}${percent}% ${stat}`;
            } else if (isMultiplierStat) {
                const multiplier = 1 + eff.value;
                formatted = `${multiplier.toFixed(2)}x ${stat}`;
            } else if (isPercentageStat) {
                const percent = Math.round(eff.value * 100);
                formatted = `${percent >= 0 ? '+' : ''}${percent}% ${stat}`;
            } else if (isDurationStat) {
                const sign = eff.value >= 0 ? '+' : '';
                formatted = `${sign}${StatsFormatter.formatDurationValue(Math.abs(eff.value))} ${stat}`;
            } else {
                const decimals = eff.type === 'momentumRate' ? 3 : 1;
                const val = Number(eff.value.toFixed(decimals));
                formatted = `${val >= 0 ? '+' : ''}${val}${unit ? ' ' + unit : ''} ${stat}`;
            }

            // Add indicator for conditional effects
            if (eff.condition?.timeOfDay) {
                formatted += ` (${eff.condition.timeOfDay})`;
            }

            bonuses.push({ text: formatted, variant: 'cyan' });
        }

        // Add prestige bonus stats in gold
        const bonusStats = this.gameState.getItemPrestigeBonusStats(item);
        for (const bonus of bonusStats) {
            const statName = StatsFormatter.getStatDisplayName(bonus.type);
            const unit = StatsFormatter.getStatUnit(bonus.type);
            const pLevel = bonus.prestigeLevel || 1;

            let formattedValue;
            if (bonus.type === 'acceleration') {
                formattedValue = `+${Math.round(bonus.value * 100)}%`;
            } else if (bonus.type === 'momentumRate') {
                formattedValue = `+${bonus.value.toFixed(3)}${unit ? ' ' + unit : ''}`;
            } else if (bonus.type === 'cruiseCooldown') {
                formattedValue = `${bonus.value.toFixed(1)}${unit ? ' ' + unit : ''}`;
            } else if (StatsFormatter.isMultiplierStat(bonus.type)) {
                const multiplier = 1 + bonus.value;
                formattedValue = `${multiplier.toFixed(2)}x`;
            } else if (StatsFormatter.isPercentageStat(bonus.type)) {
                formattedValue = `+${Math.round(bonus.value * 100)}%`;
            } else {
                formattedValue = `+${bonus.value.toFixed(1)}${unit ? ' ' + unit : ''}`;
            }

            bonuses.push({ text: `${formattedValue} ${statName} (P${pLevel})`, variant: 'gold' });
        }

        return this._bonusRowHTML(bonuses, { align, size, compact: true, noMargin });
    }

    /**
     * Format star rating for display (text fallback)
     * @param {number} stars - Star rating (1, 1.5, 2, 2.5, or 3)
     * @returns {string} Star display string
     */
    _formatStars(stars) {
        const fullStar = '★';
        const halfStar = '✦';
        const emptyStar = '☆';

        const rating = stars || 2; // Default to 2 if undefined
        const fullCount = Math.floor(rating);
        const hasHalf = rating % 1 !== 0;
        const emptyCount = 3 - fullCount - (hasHalf ? 1 : 0);

        return fullStar.repeat(fullCount) +
               (hasHalf ? halfStar : '') +
               emptyStar.repeat(emptyCount);
    }

    /**
     * Get color for star rating
     * @param {number} stars - Star rating
     * @returns {string} CSS color
     */
    _getStarColor(stars) {
        const rating = stars || 2;
        if (rating >= 2.5) return 'var(--cf-gold, #D4AF37)';      // Gold for 2.5-3 stars
        if (rating >= 2) return 'var(--cf-text-secondary, #9DA5B4)';  // Silver/gray for 2 stars
        return 'var(--cf-text-tertiary, #5C6170)';                // Dim for 1-1.5 stars
    }

    /**
     * Render star rating as HTML using sprite sheet
     * Sprite: assets/ui/stars.svg (360x360)
     * - Rows: Gold (y=10), Silver (y=120), Bronze (y=230)
     * - Columns: Full (x=10), Half (x=120), Empty (x=230)
     * @param {number} stars - Star rating (1, 1.5, 2, 2.5, or 3)
     * @param {number} size - Size of each star in pixels (default 16)
     * @returns {string} HTML string with star images
     */
    _renderStarsHTML(stars, size = 16) {
        const rating = stars || 2;
        const fullCount = Math.floor(rating);
        const hasHalf = rating % 1 !== 0;
        const emptyCount = 3 - fullCount - (hasHalf ? 1 : 0);

        // Determine row based on rating (gold=2.5+, silver=2, bronze=1-1.5)
        let rowY;
        if (rating >= 2.5) rowY = 10;      // Gold row
        else if (rating >= 2) rowY = 120;  // Silver row
        else rowY = 230;                    // Bronze row

        // Column positions in sprite
        const colX = { full: 10, half: 120, empty: 230 };

        // Scale factor (sprite stars are 100x100)
        const scale = size / 100;
        const spriteSize = 360 * scale;

        const makeStarStyle = (col) => `
            display: inline-block;
            width: ${size}px;
            height: ${size}px;
            background: url('assets/ui/stars.svg') no-repeat;
            background-size: ${spriteSize}px ${spriteSize}px;
            background-position: ${-colX[col] * scale}px ${-rowY * scale}px;
        `.replace(/\s+/g, ' ').trim();

        let html = '';
        for (let i = 0; i < fullCount; i++) {
            html += `<span style="${makeStarStyle('full')}"></span>`;
        }
        if (hasHalf) {
            html += `<span style="${makeStarStyle('half')}"></span>`;
        }
        for (let i = 0; i < emptyCount; i++) {
            html += `<span style="${makeStarStyle('empty')}"></span>`;
        }

        return html;
    }

    /**
     * Render star rating as DOM elements (for use in DOM-constructed UI)
     * @param {number} stars - Rating (e.g., 1, 1.5, 2, 2.5, 3)
     * @param {number} size - Size in pixels
     * @returns {DocumentFragment} Fragment containing star span elements
     */
    _renderStarsElements(stars, size) {
        const rating = stars || 2;
        const fullCount = Math.floor(rating);
        const hasHalf = rating % 1 !== 0;
        const emptyCount = 3 - fullCount - (hasHalf ? 1 : 0);

        // Determine row based on rating (gold=2.5+, silver=2, bronze=1-1.5)
        let rowY;
        if (rating >= 2.5) rowY = 10;      // Gold row
        else if (rating >= 2) rowY = 120;  // Silver row
        else rowY = 230;                    // Bronze row

        // Column positions in sprite
        const colX = { full: 10, half: 120, empty: 230 };

        // Scale factor (sprite stars are 100x100)
        const scale = size / 100;
        const spriteSize = 360 * scale;

        const makeStarStyle = (col) => `
            display: inline-block;
            width: ${size}px;
            height: ${size}px;
            background: url('assets/ui/stars.svg') no-repeat;
            background-size: ${spriteSize}px ${spriteSize}px;
            background-position: ${-colX[col] * scale}px ${-rowY * scale}px;
        `.replace(/\s+/g, ' ').trim();

        const fragment = document.createDocumentFragment();
        for (let i = 0; i < fullCount; i++) {
            const star = document.createElement('span');
            star.style.cssText = makeStarStyle('full');
            fragment.appendChild(star);
        }
        if (hasHalf) {
            const star = document.createElement('span');
            star.style.cssText = makeStarStyle('half');
            fragment.appendChild(star);
        }
        for (let i = 0; i < emptyCount; i++) {
            const star = document.createElement('span');
            star.style.cssText = makeStarStyle('empty');
            fragment.appendChild(star);
        }

        return fragment;
    }

    /**
     * Create item upgrade button
     * @param {object} equippedOptions - Optional: { tooltip, slot, slotType } for equipped items
     */
    _createItemUpgradeButton(item, itemsData, itemNameEl, itemEffectsEl, isCompact = false, equippedOptions = null) {
        const upgradeCost = this.gameState.getItemUpgradeCost(item.id);
        const canAfford = this.gameState.canAffordItemUpgrade(item.id);
        const isEquipped = equippedOptions !== null;

        const btn = document.createElement('button');
        btn.textContent = isCompact ? `${upgradeCost}` : `${upgradeCost} MI`;
        btn.className = `cf-item-upgrade-btn ${isCompact ? 'compact' : ''} ${canAfford ? 'can-afford' : 'cannot-afford'}`;
        btn.dataset.itemId = item.id;
        btn.dataset.buttonType = 'itemUpgrade';

        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            if (!this.gameState.canAffordItemUpgrade(item.id)) return;

            const btnRect = btn.getBoundingClientRect();
            const itemColor = itemsData.rarityColors[item.rarity] || '#88f';

            if (isEquipped) {
                // EQUIPPED ITEM: Show tooltip and target stats within it
                const { tooltip, slot, slotType } = equippedOptions;

                // Hide all other tooltips first
                document.querySelectorAll('.cf-item-tooltip').forEach(t => t.style.display = 'none');

                // Pin tooltip so it doesn't hide on mouseleave during upgrade animation
                if (slot._setTooltipPinned) {
                    slot._setTooltipPinned(true);
                }

                // Show and position the tooltip
                tooltip.style.display = 'block';
                this.gameUI.positionTooltip(tooltip, slot);

                // Get the effects container in the tooltip for particle targeting
                const tooltipEffectsEl = tooltip.querySelector(`[data-tooltip-effects="${item.id}"]`);
                const tooltipLevelEl = tooltip.querySelector(`[data-tooltip-level="${item.id}"]`);

                // Capture old effects HTML before upgrade
                const oldEffectsHtml = tooltipEffectsEl ? tooltipEffectsEl.innerHTML : '';

                if (this.gameState.upgradeItem(item.id)) {
                    const updatedItem = this.gameState.getInventoryItems().find(i => i.id === item.id);
                    if (updatedItem) {
                        // Generate new effects HTML
                        const newEffectsHtml = this._formatItemEffectsForTooltip(updatedItem);
                        const newEffectsText = this._formatItemEffects(updatedItem);

                        // Restore old HTML temporarily for animation
                        if (tooltipEffectsEl) {
                            tooltipEffectsEl.innerHTML = oldEffectsHtml;
                        }

                        // Get target position from tooltip effects
                        const effectsRect = tooltipEffectsEl ? tooltipEffectsEl.getBoundingClientRect() : btnRect;

                        // Create particles targeting the tooltip effects
                        // Don't pass newValue - we'll update HTML manually in onArrive
                        this.particles.createItemParticles(
                            btnRect.left + btnRect.width / 2,
                            btnRect.top + btnRect.height / 2,
                            { x: effectsRect.left + effectsRect.width / 2, y: effectsRect.top + effectsRect.height / 2 },
                            undefined, // Don't set text content, we update HTML manually
                            itemColor,
                            {
                                targetElementSelector: `[data-tooltip-effects="${item.id}"]`,
                                onArrive: () => {
                                    // Update tooltip content when particles arrive
                                    if (tooltipEffectsEl) {
                                        tooltipEffectsEl.innerHTML = newEffectsHtml;
                                        // Apply glow and shake effect manually
                                        tooltipEffectsEl.style.textShadow = `0 0 10px ${itemColor}`;
                                        tooltipEffectsEl.style.transition = 'none';

                                        // Shake animation
                                        let shakeFrame = 0;
                                        const shakePattern = [3, -3, 2, -2, 1, -1, 0];
                                        const shakeInterval = setInterval(() => {
                                            if (shakeFrame < shakePattern.length) {
                                                tooltipEffectsEl.style.transform = `translateX(${shakePattern[shakeFrame]}px)`;
                                                shakeFrame++;
                                            } else {
                                                clearInterval(shakeInterval);
                                                tooltipEffectsEl.style.transform = '';
                                            }
                                        }, 30);
                                    }
                                    if (tooltipLevelEl) {
                                        tooltipLevelEl.textContent = `${slotType} | ${updatedItem.rarity} | Level ${updatedItem.level}${updatedItem.level >= 10 ? ' (MAX)' : ''}`;
                                    }
                                },
                                onComplete: () => {
                                    // Remove glow effect
                                    if (tooltipEffectsEl) {
                                        tooltipEffectsEl.style.textShadow = '';
                                        tooltipEffectsEl.style.transform = '';
                                    }

                                    // Hide tooltip after animation completes
                                    setTimeout(() => {
                                        tooltip.style.display = 'none';
                                        if (slot._setTooltipPinned) {
                                            slot._setTooltipPinned(false);
                                        }
                                    }, 100);
                                }
                            }
                        );

                        // Update slot name element
                        const nameEl = document.querySelector(`[data-item-name="${item.id}"]`);
                        if (nameEl) {
                            nameEl.textContent = this._shortenItemName(updatedItem.name);
                        }

                        // Update level display in slot
                        const levelText = updatedItem.level >= 10 ? 'MAX' : `L${updatedItem.level}`;
                        const slotEl = slot.querySelector('div:nth-child(3)'); // Level display is 3rd child
                        if (slotEl && slotEl.textContent.match(/^(L\d+|MAX)$/)) {
                            slotEl.textContent = levelText;
                        }

                        // Update button
                        const newCost = this.gameState.getItemUpgradeCost(item.id);
                        if (newCost === null) {
                            btn.remove();
                        } else {
                            const newCanAfford = this.gameState.canAffordItemUpgrade(item.id);
                            btn.textContent = `${newCost}`;
                            btn.className = `cf-item-upgrade-btn compact ${newCanAfford ? 'can-afford' : 'cannot-afford'}`;
                        }
                    }

                    if (this.app.equipmentManager) {
                        this.app.equipmentManager.updateEquippedItems();
                    }
                    this.sfxManager?.playSound('upgrade');

                    // Show bonus popup if Item Upgrade Bonus is active
                    const upgradeBonus = this.gameState.getItemUpgradeBonusMultiplier() - 1;
                    if (upgradeBonus > 0) {
                        const btnRect = btn.getBoundingClientRect();
                        this.gameUI.popups.showBonusPopup(
                            btnRect.left + btnRect.width / 2,
                            btnRect.top - 8,
                            `+${Math.round(upgradeBonus * 100)}% BONUS!`
                        );
                    }
                }
            } else {
                // INVENTORY ITEM: Original behavior - target effects element in row
                const effectsRect = itemEffectsEl.getBoundingClientRect();
                const oldEffectsText = itemEffectsEl.innerHTML;

                if (this.gameState.upgradeItem(item.id)) {
                    const updatedItem = this.gameState.getInventoryItems().find(i => i.id === item.id);
                    if (updatedItem) {
                        const newEffectsText = this._formatItemEffects(updatedItem);

                        // Restore old text on rebuilt element - animation will update to new value
                        const rebuiltEffectsEl = document.querySelector(`[data-item-effects="${item.id}"]`);
                        if (rebuiltEffectsEl) {
                            rebuiltEffectsEl.innerHTML = oldEffectsText;
                        }

                        // Create particles targeting the effects element
                        this.particles.createItemParticles(
                            btnRect.left + btnRect.width / 2,
                            btnRect.top + btnRect.height / 2,
                            { x: effectsRect.left + effectsRect.width / 2, y: effectsRect.top + effectsRect.height / 2 },
                            newEffectsText,
                            itemColor,
                            {
                                targetElementSelector: `[data-item-effects="${item.id}"]`
                            }
                        );

                        // Update name element
                        const newLevelDisplay = updatedItem.level >= 10 ? ' (MAX)' : ` (Lv ${updatedItem.level})`;
                        const nameEl = document.querySelector(`[data-item-name="${item.id}"]`);
                        if (nameEl) {
                            nameEl.textContent = updatedItem.name + newLevelDisplay;
                        }

                        // Update button
                        const newCost = this.gameState.getItemUpgradeCost(item.id);
                        const upgradeBtn = document.querySelector(`button[data-item-id="${item.id}"][data-button-type="itemUpgrade"]`);
                        if (upgradeBtn) {
                            if (newCost === null) {
                                upgradeBtn.remove();
                            } else {
                                const newCanAfford = this.gameState.canAffordItemUpgrade(item.id);
                                upgradeBtn.textContent = `${newCost} MI`;
                                upgradeBtn.className = `cf-item-upgrade-btn ${newCanAfford ? 'can-afford' : 'cannot-afford'}`;
                            }
                        }
                    }

                    if (this.app.equipmentManager) {
                        this.app.equipmentManager.updateEquippedItems();
                    }
                    this.sfxManager?.playSound('upgrade');

                    // Show bonus popup if Item Upgrade Bonus is active
                    const upgradeBonus = this.gameState.getItemUpgradeBonusMultiplier() - 1;
                    if (upgradeBonus > 0) {
                        const btnRect = btn.getBoundingClientRect();
                        this.gameUI.popups.showBonusPopup(
                            btnRect.left + btnRect.width / 2,
                            btnRect.top - 8,
                            `+${Math.round(upgradeBonus * 100)}% BONUS!`
                        );
                    }
                }
            }
        });

        return btn;
    }

    /**
     * Create quality fusion button for v2 items
     * @param {boolean} compact - If true, show icon-only button for equipped items
     */
    _createFusionButton(item, itemsData, container, parentOverlay, compact = false) {
        const check = this.gameState.canUpgradeItemQuality(item.id);
        const nextRarity = this.gameState.getNextRarity(item.rarity);

        // Don't show button if already at max rarity
        if (!nextRarity) return null;

        const btn = document.createElement('button');
        btn.textContent = compact ? '↑' : 'FUSE';
        btn.title = `Upgrade to ${nextRarity}`;
        btn.className = `cf-item-fusion-btn ${check.canUpgrade ? 'can-afford' : 'cannot-afford'}`;
        btn.dataset.itemId = item.id;
        btn.dataset.buttonType = 'itemFusion';

        if (compact) {
            // Compact icon button for equipped items
            btn.style.cssText = `
                background: transparent;
                border: 1px solid var(--cf-lavender, #a78bfa);
                color: var(--cf-lavender, #a78bfa);
                width: 28px;
                height: 28px;
                padding: 0;
                cursor: pointer;
                font-size: 14px;
                font-weight: bold;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: all 0.1s;
            `;
        } else {
            // Full button for inventory items - match equip/scrap button styling
            btn.style.cssText = `
                background: transparent;
                border: 1px solid var(--cf-lavender, #a78bfa);
                color: var(--cf-lavender, #a78bfa);
                padding: 6px 10px;
                cursor: pointer;
                font-size: 10px;
                text-transform: uppercase;
                letter-spacing: 0.05em;
                font-family: inherit;
                transition: all 0.1s;
            `;
        }

        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            this._showFusionModal(item, itemsData, container, parentOverlay);
        });

        return btn;
    }

    /**
     * Show quality fusion modal with material selection
     */
    _showFusionModal(item, itemsData, container, parentOverlay) {
        const check = this.gameState.canUpgradeItemQuality(item.id);
        const cost = check.cost || { wavedust: 50, materialCount: 3 };
        const nextRarity = this.gameState.getNextRarity(item.rarity);
        const availablematerial = this.gameState.getAvailablematerial(item.id);
        const currentWavedust = this.gameState.getWavedust();
        const hasEnoughWavedust = currentWavedust >= cost.wavedust;

        // Create modal overlay - cassette future style
        const modalOverlay = document.createElement('div');
        modalOverlay.className = 'cf-modal-overlay';
        modalOverlay.style.cssText = `
            position: fixed;
            inset: 0;
            background: rgba(11, 13, 18, 0.92);
            backdrop-filter: blur(4px);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10000;
            padding: 16px;
        `;

        const modal = document.createElement('div');
        modal.className = 'cf-modal cf-fusion-modal';
        modal.style.cssText = `
            background: var(--cf-bg-primary, #11131A);
            border: 1px solid var(--cf-divider, #2A2F3C);
            max-width: 380px;
            width: 100%;
            max-height: 92vh;
            display: flex;
            flex-direction: column;
            overflow: hidden;
        `;

        // Header with wavedust balance
        const header = document.createElement('div');
        header.className = 'cf-modal-header';
        header.style.cssText = `
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 16px 24px;
            border-bottom: 1px solid var(--cf-divider, #2A2F3C);
        `;
        // Build header using DOM APIs
        const titleDiv = document.createElement('div');
        titleDiv.style.cssText = 'font-size: 10px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.12em; color: var(--cf-text-tertiary, #5C6170);';
        titleDiv.textContent = 'QUALITY FUSION';
        const rightGroup = document.createElement('div');
        rightGroup.style.cssText = 'display: flex; align-items: center; gap: 16px;';
        const wavedustSpan = document.createElement('span');
        wavedustSpan.style.cssText = 'font-size: 14px; color: var(--cf-lavender, #a78bfa); font-variant-numeric: tabular-nums;';
        wavedustSpan.textContent = `${currentWavedust.toLocaleString()} ⚙`;
        const fusionCloseBtn = document.createElement('button');
        fusionCloseBtn.id = 'fusion-close-btn';
        fusionCloseBtn.style.cssText = 'background: none; border: none; color: var(--cf-text-tertiary, #5C6170); font-size: 20px; cursor: pointer; padding: 0; line-height: 1;';
        fusionCloseBtn.textContent = '×';
        rightGroup.appendChild(wavedustSpan);
        rightGroup.appendChild(fusionCloseBtn);
        header.appendChild(titleDiv);
        header.appendChild(rightGroup);
        modal.appendChild(header);

        // Body
        const body = document.createElement('div');
        body.className = 'cf-modal-body';
        body.style.cssText = `
            padding: 20px;
            overflow-y: auto;
            flex: 1;
        `;

        // Colors
        const itemColor = itemsData.rarityColors[item.rarity] || '#888';
        const nextColor = itemsData.rarityColors[nextRarity] || '#888';

        // Compute result name using base item lookup for accuracy
        const rarityPrefixes = { faded: 'Faded', standard: 'Standard', pristine: 'Pristine', golden: 'Golden', vectorized: 'Vectorized' };
        const slotIcons = { 'rearview': '🪞', 'dashboard': '📊', 'keychain': '🔑' };
        const itemSlot = item.slotType || item.slot;
        let baseName = item.name;
        if (item.baseId && itemRegistry.has(item.baseId)) {
            baseName = itemRegistry.get(item.baseId).name;
        }
        const resultName = `${rarityPrefixes[nextRarity]} ${baseName}`;

        // === TOP SECTION: Two item cards side by side ===
        const transformSection = document.createElement('div');
        transformSection.style.cssText = `
            display: flex;
            align-items: stretch;
            gap: 12px;
            margin-bottom: 20px;
        `;

        // Helper to create an item card
        const createItemCard = (label, isResult = false) => {
            const card = document.createElement('div');
            card.style.cssText = `
                flex: 1;
                background: var(--cf-bg-deep, #0B0D12);
                border: 1px solid ${isResult ? 'var(--cf-divider, #2A2F3C)' : itemColor};
                padding: 16px 12px;
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 8px;
                min-height: 120px;
                transition: all 0.2s ease;
            `;

            const labelEl = document.createElement('div');
            labelEl.style.cssText = `
                font-size: 9px;
                text-transform: uppercase;
                letter-spacing: 0.1em;
                color: var(--cf-text-tertiary, #5C6170);
            `;
            labelEl.textContent = label;
            card.appendChild(labelEl);

            const content = document.createElement('div');
            content.style.cssText = `
                flex: 1;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 6px;
                width: 100%;
            `;
            card.appendChild(content);

            return { card, content };
        };

        // Helper to format effects for tooltip using bonus boxes
        const formatEffectsHTML = (effects, variant) => {
            const bonuses = effects.map(eff => {
                const statName = StatsFormatter.getStatDisplayName(eff.type);
                const unit = StatsFormatter.getStatUnit(eff.type);
                const isMultiplier = eff.operation === 'multiply' ||
                    (eff.operation !== 'add' && (eff.type === 'acceleration' || eff.type === 'timeScale'));
                const isPercentageStat = StatsFormatter.isPercentageStat(eff.type);
                const isMultiplierStat = StatsFormatter.isMultiplierStat(eff.type);
                const isDurationStat = StatsFormatter.isDurationStat(eff.type);

                let formatted;
                if (isMultiplier) {
                    const percent = Math.round((eff.value - 1) * 100);
                    formatted = `${percent >= 0 ? '+' : ''}${percent}% ${statName}`;
                } else if (isMultiplierStat) {
                    const multiplier = 1 + eff.value;
                    formatted = `${multiplier.toFixed(2)}x ${statName}`;
                } else if (isPercentageStat) {
                    const percent = Math.round(eff.value * 100);
                    formatted = `${percent >= 0 ? '+' : ''}${percent}% ${statName}`;
                } else if (isDurationStat) {
                    const sign = eff.value >= 0 ? '+' : '';
                    formatted = `${sign}${StatsFormatter.formatDurationValue(Math.abs(eff.value))} ${statName}`;
                } else {
                    const decimals = eff.type === 'momentumRate' ? 3 : 1;
                    const val = Number(eff.value.toFixed(decimals));
                    const unitSuffix = unit ? ' ' + unit : '';
                    formatted = `${val >= 0 ? '+' : ''}${val}${unitSuffix} ${statName}`;
                }
                return { text: formatted, variant };
            });
            return this._bonusRowHTML(bonuses, { align: 'left', size: 'compact', noMargin: true });
        };

        // Helper to calculate projected stats after fusion
        // Uses fixed stat values from item registry for the target tier, with fusion bonus applied
        const calculateProjectedEffects = () => {
            const fusionBonusMultiplier = this.gameState.getItemUpgradeBonusMultiplier();
            if (item.baseId && itemRegistry.has(item.baseId)) {
                const baseItem = itemRegistry.get(item.baseId);
                if (baseItem.stat_values) {
                    return item.effects.map(effect => {
                        const statValues = baseItem.stat_values[effect.type];
                        if (statValues && statValues[nextRarity] !== undefined) {
                            const baseValue = statValues[nextRarity];
                            let boostedValue;

                            // Apply fusion bonus (matching logic in GameState.upgradeItemQuality)
                            if (effect.operation === 'multiply') {
                                // For multiplicative effects, apply bonus to the bonus portion
                                // e.g., 1.20 with 1.10x bonus = 1 + (0.20 * 1.10) = 1.22
                                boostedValue = 1 + (baseValue - 1) * fusionBonusMultiplier;
                            } else {
                                // Additive effects: multiply directly
                                boostedValue = baseValue * fusionBonusMultiplier;
                            }

                            // Round to reasonable precision
                            boostedValue = Math.round(boostedValue * 1000) / 1000;

                            return { ...effect, value: boostedValue };
                        }
                        // Fallback: keep existing value if no stat_values defined
                        return effect;
                    });
                }
            }
            // Fallback for legacy items: return current effects
            return item.effects;
        };

        // Create tooltip element
        const createStatsTooltip = () => {
            const tooltip = document.createElement('div');
            tooltip.style.cssText = `
                position: absolute;
                background: var(--cf-bg-primary, #11131A);
                border: 1px solid var(--cf-divider, #2A2F3C);
                padding: 12px 16px;
                font-size: 11px;
                line-height: 1.6;
                z-index: 10002;
                pointer-events: none;
                opacity: 0;
                transition: opacity 0.15s ease;
                min-width: 140px;
                box-shadow: 0 4px 12px rgba(0,0,0,0.4);
            `;
            document.body.appendChild(tooltip);
            return tooltip;
        };

        const sourceTooltip = createStatsTooltip();
        const resultTooltip = createStatsTooltip();

        // Track active tooltip for click-outside handling
        let activeTooltip = null;
        let isTouchDevice = false;

        // Detect touch device
        const markTouchDevice = () => { isTouchDevice = true; };
        document.addEventListener('touchstart', markTouchDevice, { once: true, passive: true });

        // Tooltip positioning helper
        const positionTooltip = (tooltip, card) => {
            const rect = card.getBoundingClientRect();
            tooltip.style.left = `${rect.left + rect.width / 2}px`;
            tooltip.style.top = `${rect.bottom + 8}px`;
            tooltip.style.transform = 'translateX(-50%)';

            // Adjust if off-screen
            requestAnimationFrame(() => {
                const tooltipRect = tooltip.getBoundingClientRect();
                if (tooltipRect.right > window.innerWidth - 10) {
                    tooltip.style.left = `${window.innerWidth - tooltipRect.width - 10}px`;
                    tooltip.style.transform = 'none';
                }
                if (tooltipRect.left < 10) {
                    tooltip.style.left = '10px';
                    tooltip.style.transform = 'none';
                }
            });
        };

        // Source item card (left)
        const sourceCard = createItemCard('CURRENT');
        sourceCard.content.innerHTML = `
            <div style="width: 48px; height: 48px; background: ${itemColor}33; border: 2px solid ${itemColor}; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 24px; margin-bottom: 8px;">${slotIcons[itemSlot] || '📦'}</div>
            <div style="font-size: 13px; color: ${itemColor}; text-align: center; line-height: 1.3;">${item.name}</div>
            <div style="font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; color: ${itemColor};">${item.rarity}</div>
        `;
        sourceCard.card.style.cursor = 'pointer';

        // Source card tooltip events - show legacy prestige bonus stats if any
        const prestigeBonusStats = this.gameState.getItemPrestigeBonusStats(item);
        const formatBonusStat = (b) => {
            if (b.type === 'acceleration') return `+${Math.round(b.value * 100)}%`;
            if (b.type === 'momentumRate') return `+${b.value.toFixed(3)}`;
            if (b.type === 'cruiseCooldown') return `${b.value.toFixed(1)}`;
            return `+${b.value.toFixed(1)}`;
        };
        // Group all stats together (regular + legacy prestige bonus stats)
        const prestigeBonusesHTML = prestigeBonusStats.length > 0
            ? this._bonusRowHTML(prestigeBonusStats.map(b => ({
                text: `${formatBonusStat(b)} ${StatsFormatter.getStatDisplayName(b.type)} (P${b.prestigeLevel || 1})`,
                variant: 'gold'
            })), { align: 'left', size: 'compact', noMargin: false })
            : '';
        sourceTooltip.innerHTML = `
            <div style="font-size: 9px; text-transform: uppercase; letter-spacing: 0.1em; color: var(--cf-text-tertiary, #5C6170); margin-bottom: 8px;">CURRENT STATS</div>
            ${formatEffectsHTML(item.effects, 'cyan')}
            ${prestigeBonusesHTML}
        `;

        sourceCard.card.addEventListener('mouseenter', () => {
            // Only show on hover for non-touch devices
            if (!isTouchDevice) {
                positionTooltip(sourceTooltip, sourceCard.card);
                sourceTooltip.style.opacity = '1';
                activeTooltip = sourceTooltip;
            }
        });
        sourceCard.card.addEventListener('mouseleave', () => {
            // Only hide on hover for non-touch devices
            if (!isTouchDevice) {
                sourceTooltip.style.opacity = '0';
                if (activeTooltip === sourceTooltip) activeTooltip = null;
            }
        });
        sourceCard.card.addEventListener('click', (e) => {
            e.stopPropagation();
            // On touch devices, use click to toggle
            if (isTouchDevice) {
                const isVisible = sourceTooltip.style.opacity === '1';
                // Close other tooltips
                resultTooltip.style.opacity = '0';

                if (isVisible) {
                    sourceTooltip.style.opacity = '0';
                    activeTooltip = null;
                } else {
                    positionTooltip(sourceTooltip, sourceCard.card);
                    sourceTooltip.style.opacity = '1';
                    activeTooltip = sourceTooltip;
                }
            }
        });

        transformSection.appendChild(sourceCard.card);

        // Arrow indicator
        const arrowContainer = document.createElement('div');
        arrowContainer.style.cssText = `
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 0 4px;
        `;

        const arrow = document.createElement('div');
        arrow.style.cssText = `
            color: var(--cf-text-tertiary, #5C6170);
            font-size: 20px;
        `;
        arrow.textContent = '→';
        arrowContainer.appendChild(arrow);

        transformSection.appendChild(arrowContainer);

        // Result item card (right) - initially shows placeholder
        const resultCard = createItemCard('RESULT', true);
        resultCard.content.innerHTML = `
            <div style="font-size: 24px; color: var(--cf-text-disabled, #3D424F);">?</div>
            <div style="font-size: 10px; color: var(--cf-text-disabled, #3D424F);">Select materials</div>
        `;
        resultCard.card.style.cursor = 'pointer';

        // Result card tooltip events (only show when preview is ready)
        let projectedEffects = null;

        resultCard.card.addEventListener('mouseenter', () => {
            // Only show on hover for non-touch devices
            if (!isTouchDevice && projectedEffects) {
                positionTooltip(resultTooltip, resultCard.card);
                resultTooltip.style.opacity = '1';
                activeTooltip = resultTooltip;
            }
        });
        resultCard.card.addEventListener('mouseleave', () => {
            // Only hide on hover for non-touch devices
            if (!isTouchDevice) {
                resultTooltip.style.opacity = '0';
                if (activeTooltip === resultTooltip) activeTooltip = null;
            }
        });
        resultCard.card.addEventListener('click', (e) => {
            e.stopPropagation();
            if (!projectedEffects) return;
            // On touch devices, use click to toggle
            if (isTouchDevice) {
                const isVisible = resultTooltip.style.opacity === '1';
                // Close other tooltips
                sourceTooltip.style.opacity = '0';

                if (isVisible) {
                    resultTooltip.style.opacity = '0';
                    activeTooltip = null;
                } else {
                    positionTooltip(resultTooltip, resultCard.card);
                    resultTooltip.style.opacity = '1';
                    activeTooltip = resultTooltip;
                }
            }
        });

        transformSection.appendChild(resultCard.card);

        body.appendChild(transformSection);

        // Fusion Bonus display (below the transform section)
        const fusionBonus = this.gameState.getItemUpgradeBonusMultiplier();
        const hasFusionBonus = fusionBonus > 1;
        const fusionBonusRow = document.createElement('div');
        fusionBonusRow.style.cssText = `
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            margin-bottom: 16px;
        `;
        const fusionBonusLabel = document.createElement('span');
        fusionBonusLabel.style.cssText = `
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            color: ${hasFusionBonus ? 'var(--cf-lavender, #a78bfa)' : 'var(--cf-text-tertiary, #5C6170)'};
        `;
        fusionBonusLabel.textContent = 'FUSION BONUS';
        const fusionBonusValue = document.createElement('span');
        fusionBonusValue.style.cssText = `
            font-size: 14px;
            font-weight: 500;
            color: ${hasFusionBonus ? 'var(--cf-lavender, #a78bfa)' : 'var(--cf-text-tertiary, #5C6170)'};
            font-variant-numeric: tabular-nums;
        `;
        fusionBonusValue.textContent = `${fusionBonus.toFixed(2)}×`;
        fusionBonusRow.appendChild(fusionBonusLabel);
        fusionBonusRow.appendChild(fusionBonusValue);
        body.appendChild(fusionBonusRow);

        // Click-outside handler to close tooltips
        body.addEventListener('click', (e) => {
            // Close active tooltip when clicking elsewhere (mobile only)
            if (activeTooltip && isTouchDevice) {
                activeTooltip.style.opacity = '0';
                activeTooltip = null;
            }
        });

        // === COST ROW: Compact inline ===
        const costRow = document.createElement('div');
        costRow.style.cssText = `
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 24px;
            margin-bottom: 16px;
            padding: 12px 16px;
            background: var(--cf-bg-deep, #0B0D12);
            border-left: 2px solid var(--cf-lavender-dim, #5E4A6B);
        `;
        const wavedustColor = hasEnoughWavedust ? 'var(--cf-positive, #7CFF9E)' : 'var(--cf-negative, #E86A6A)';
        // Build cost row using DOM APIs
        const costGroup = document.createElement('div');
        costGroup.style.cssText = 'display: flex; align-items: center; gap: 8px;';
        const costLabel = document.createElement('span');
        costLabel.style.cssText = 'font-size: 9px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--cf-text-tertiary, #5C6170);';
        costLabel.textContent = 'COST';
        const costValue = document.createElement('span');
        costValue.style.cssText = `font-size: 14px; color: ${wavedustColor}; font-variant-numeric: tabular-nums;`;
        costValue.textContent = `${cost.wavedust} ⚙`;
        costGroup.appendChild(costLabel);
        costGroup.appendChild(costValue);

        const divider = document.createElement('div');
        divider.style.cssText = 'width: 1px; height: 16px; background: var(--cf-divider, #2A2F3C);';

        const matCostGroup = document.createElement('div');
        matCostGroup.style.cssText = 'display: flex; align-items: center; gap: 8px;';
        const matCostLabel = document.createElement('span');
        matCostLabel.style.cssText = 'font-size: 9px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--cf-text-tertiary, #5C6170);';
        matCostLabel.textContent = 'MATERIALS';
        const materialCounter = document.createElement('span');
        materialCounter.id = 'material-counter';
        materialCounter.style.cssText = 'font-size: 14px; color: var(--cf-text-secondary, #9BA1B0);';
        const materialSelected = document.createElement('span');
        materialSelected.id = 'material-selected';
        materialSelected.textContent = '0';
        materialCounter.appendChild(materialSelected);
        materialCounter.appendChild(document.createTextNode(`/${cost.materialCount}`));
        matCostGroup.appendChild(matCostLabel);
        matCostGroup.appendChild(materialCounter);

        costRow.appendChild(costGroup);
        costRow.appendChild(divider);
        costRow.appendChild(matCostGroup);
        body.appendChild(costRow);

        // Helper to update the result card preview
        const updatePreview = () => {
            if (selectedmaterial.size < cost.materialCount) {
                // Show placeholder with dimmed sprite container
                resultCard.card.style.borderColor = 'var(--cf-divider, #2A2F3C)';
                resultCard.content.innerHTML = `
                    <div style="width: 48px; height: 48px; background: var(--cf-divider, #2A2F3C)33; border: 2px solid var(--cf-divider, #2A2F3C); border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 24px; margin-bottom: 8px; color: var(--cf-text-disabled, #3D424F);">?</div>
                    <div style="font-size: 10px; color: var(--cf-text-disabled, #3D424F);">Select materials</div>
                `;
                arrow.style.color = 'var(--cf-text-tertiary, #5C6170)';
                projectedEffects = null;
                resultTooltip.style.opacity = '0';
                return;
            }

            // V3+ bespoke items: use fixed stat values from item registry
            projectedEffects = calculateProjectedEffects();

            // Show legacy bonus stats (grandfathered, no changes on fusion)
            const resultBonusStatsHtml = prestigeBonusStats.length > 0
                ? this._bonusRowHTML(prestigeBonusStats.map(b => {
                    let formatted;
                    if (b.type === 'acceleration') formatted = `+${Math.round(b.value * 100)}%`;
                    else if (b.type === 'momentumRate') formatted = `+${b.value.toFixed(3)}`;
                    else if (b.type === 'cruiseCooldown') formatted = `${b.value.toFixed(1)}`;
                    else formatted = `+${b.value.toFixed(1)}`;
                    return { text: `${formatted} ${StatsFormatter.getStatDisplayName(b.type)} (P${b.prestigeLevel || 1})`, variant: 'gold' };
                }), { align: 'left', size: 'compact', noMargin: false })
                : '';

            resultTooltip.innerHTML = `
                <div style="font-size: 9px; text-transform: uppercase; letter-spacing: 0.1em; color: var(--cf-text-tertiary, #5C6170); margin-bottom: 8px;">NEW STATS</div>
                ${formatEffectsHTML(projectedEffects, 'positive')}
                ${resultBonusStatsHtml}
            `;

            // Update result card with preview (no stars display)
            resultCard.card.style.borderColor = nextColor;
            resultCard.content.innerHTML = `
                <div style="width: 48px; height: 48px; background: ${nextColor}33; border: 2px solid ${nextColor}; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 24px; margin-bottom: 8px;">${slotIcons[itemSlot] || '📦'}</div>
                <div style="font-size: 13px; color: ${nextColor}; text-align: center; line-height: 1.3;">${resultName}</div>
                <div style="font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; color: ${nextColor};">${nextRarity}</div>
            `;
            arrow.style.color = 'var(--cf-cyan, #5FB3B3)';
        };

        // material selection
        const materialSection = document.createElement('div');
        materialSection.style.cssText = `margin-bottom: 20px;`;

        const materialLabel = document.createElement('div');
        materialLabel.style.cssText = `
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            color: var(--cf-text-tertiary, #5C6170);
            margin-bottom: 12px;
        `;
        materialLabel.textContent = availablematerial.length > 0 ? 'SELECT MATERIALS' : 'NO MATERIALS AVAILABLE';
        materialSection.appendChild(materialLabel);

        const selectedmaterial = new Set();
        const materialList = document.createElement('div');
        materialList.style.cssText = `
            display: flex;
            flex-direction: column;
            gap: 2px;
            min-height: 120px;
            max-height: 40vh;
            overflow-y: auto;
        `;

        // Store tooltips for cleanup
        const materialTooltips = [];

        // Store references to checkboxes for visibility updates
        const materialCheckboxes = new Map(); // material.id -> { checkbox, item }

        // Update checkbox visibility when selection changes
        const updateCheckboxVisibility = () => {
            const isFull = selectedmaterial.size >= cost.materialCount;
            materialCheckboxes.forEach(({ checkbox, item }, materialId) => {
                const isSelected = selectedmaterial.has(materialId);
                if (isSelected) {
                    // Always show selected checkboxes
                    checkbox.style.visibility = 'visible';
                    item.style.cursor = 'pointer';
                    item.style.opacity = '1';
                } else if (isFull) {
                    // Hide unselected checkboxes when max reached
                    checkbox.style.visibility = 'hidden';
                    item.style.cursor = 'default';
                    item.style.opacity = '0.5';
                } else {
                    // Show all checkboxes when not at max
                    checkbox.style.visibility = 'visible';
                    item.style.cursor = 'pointer';
                    item.style.opacity = '1';
                }
            });
        };

        availablematerial.forEach(material => {
            const materialItem = document.createElement('div');
            materialItem.style.cssText = `
                display: flex;
                align-items: center;
                gap: 12px;
                padding: 10px 12px;
                cursor: pointer;
                border-bottom: 1px solid var(--cf-divider, #2A2F3C);
                transition: background 0.1s;
                position: relative;
            `;

            const checkbox = document.createElement('div');
            checkbox.style.cssText = `
                width: 16px;
                height: 16px;
                border: 1px solid var(--cf-divider-light, #363C4D);
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 11px;
                flex-shrink: 0;
                transition: all 0.1s;
            `;

            const materialColor = itemsData.rarityColors[material.rarity] || '#888';
            const materialInfo = document.createElement('div');
            materialInfo.style.cssText = `flex: 1; min-width: 0;`;
            materialInfo.innerHTML = `
                <div style="color: ${materialColor}; font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${material.name}</div>
                <div style="font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--cf-text-tertiary, #5C6170);">
                    <span>${material.slotType}</span>
                </div>
            `;

            // Info button for tooltip
            const infoBtn = document.createElement('button');
            infoBtn.textContent = 'i';
            infoBtn.style.cssText = `
                width: 18px;
                height: 18px;
                border-radius: 50%;
                border: 1px solid var(--cf-divider-light, #363C4D);
                background: transparent;
                color: var(--cf-text-tertiary, #5C6170);
                font-size: 10px;
                font-style: italic;
                cursor: pointer;
                flex-shrink: 0;
                transition: all 0.1s;
            `;
            infoBtn.addEventListener('mouseenter', () => {
                infoBtn.style.borderColor = 'var(--cf-text-secondary, #9BA1B0)';
                infoBtn.style.color = 'var(--cf-text-secondary, #9BA1B0)';
            });
            infoBtn.addEventListener('mouseleave', () => {
                infoBtn.style.borderColor = 'var(--cf-divider-light, #363C4D)';
                infoBtn.style.color = 'var(--cf-text-tertiary, #5C6170)';
            });

            // Create tooltip for this material item
            const tooltip = this._createEquipSlotTooltip(material, itemsData, material.slotType);
            tooltip.style.zIndex = '10001'; // Above fusion modal
            materialTooltips.push(tooltip);

            // Helper to show tooltip
            const showmaterialTooltip = () => {
                // Hide other tooltips
                materialTooltips.forEach(t => t.style.display = 'none');
                // Position and show this tooltip
                const rect = infoBtn.getBoundingClientRect();
                tooltip.style.display = 'block';
                tooltip.style.left = `${rect.right + 8}px`;
                tooltip.style.top = `${rect.top}px`;
                // Adjust if off-screen
                const tooltipRect = tooltip.getBoundingClientRect();
                if (tooltipRect.right > window.innerWidth - 10) {
                    tooltip.style.left = `${rect.left - tooltipRect.width - 8}px`;
                }
                if (tooltipRect.bottom > window.innerHeight - 10) {
                    tooltip.style.top = `${window.innerHeight - tooltipRect.height - 10}px`;
                }
            };

            // Show tooltip on info button hover (desktop)
            infoBtn.addEventListener('mouseenter', (e) => {
                e.stopPropagation();
                showmaterialTooltip();
            });
            infoBtn.addEventListener('mouseleave', () => {
                tooltip.style.display = 'none';
            });

            // Touch support for tooltips (mobile)
            infoBtn.addEventListener('touchstart', (e) => {
                e.stopPropagation();
                e.preventDefault();
                const isVisible = tooltip.style.display === 'block';
                materialTooltips.forEach(t => t.style.display = 'none');
                if (!isVisible) {
                    showmaterialTooltip();
                }
            });

            // Prevent info button click from selecting the item
            infoBtn.addEventListener('click', (e) => {
                e.stopPropagation();
            });

            materialItem.appendChild(checkbox);
            materialItem.appendChild(materialInfo);
            materialItem.appendChild(infoBtn);

            materialItem.addEventListener('mouseenter', () => {
                if (!selectedmaterial.has(material.id)) {
                    materialItem.style.background = 'var(--cf-bg-raised, #181B24)';
                }
            });
            materialItem.addEventListener('mouseleave', () => {
                if (!selectedmaterial.has(material.id)) {
                    materialItem.style.background = 'transparent';
                }
            });

            materialItem.addEventListener('click', () => {
                // Don't allow clicking unselected items when max reached
                const isFull = selectedmaterial.size >= cost.materialCount;
                if (!selectedmaterial.has(material.id) && isFull) {
                    return;
                }

                if (selectedmaterial.has(material.id)) {
                    selectedmaterial.delete(material.id);
                    materialItem.style.background = 'transparent';
                    checkbox.textContent = '';
                    checkbox.style.background = 'transparent';
                    checkbox.style.borderColor = 'var(--cf-divider-light, #363C4D)';
                } else if (selectedmaterial.size < cost.materialCount) {
                    selectedmaterial.add(material.id);
                    materialItem.style.background = 'var(--cf-bg-raised, #181B24)';
                    checkbox.textContent = '✓';
                    checkbox.style.background = 'var(--cf-cyan-dim, #3D7A7A)';
                    checkbox.style.borderColor = 'var(--cf-cyan, #5FB3B3)';
                    checkbox.style.color = 'var(--cf-cyan, #5FB3B3)';
                }
                updateFuseButton();
                updatePreview();
                updateCheckboxVisibility();
            });

            // Register checkbox for visibility updates
            materialCheckboxes.set(material.id, { checkbox, item: materialItem });

            materialList.appendChild(materialItem);
        });

        // Cleanup tooltips when modal closes
        const cleanupTooltips = () => {
            materialTooltips.forEach(t => t.remove());
            sourceTooltip.remove();
            resultTooltip.remove();
            // Remove touch detection listener if it hasn't fired
            document.removeEventListener('touchstart', markTouchDevice);
        };

        materialSection.appendChild(materialList);
        body.appendChild(materialSection);

        // Fuse button
        const fuseBtn = document.createElement('button');
        fuseBtn.className = 'cf-button cf-button-full';
        fuseBtn.textContent = 'FUSE';
        fuseBtn.style.cssText = `
            font-family: inherit;
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 0.12em;
            padding: 14px 16px;
            cursor: pointer;
            transition: all 0.1s;
            width: 100%;
        `;

        const updateFuseButton = () => {
            const canFuse = selectedmaterial.size === cost.materialCount && hasEnoughWavedust;
            fuseBtn.disabled = !canFuse;

            // Update material counter
            const materialSelectedEl = body.querySelector('#material-selected');
            const materialCounterEl = body.querySelector('#material-counter');
            if (materialSelectedEl) {
                materialSelectedEl.textContent = selectedmaterial.size;
            }
            if (materialCounterEl) {
                const isFull = selectedmaterial.size === cost.materialCount;
                materialCounterEl.style.color = isFull ? 'var(--cf-positive, #7CFF9E)' : 'var(--cf-text-secondary, #9BA1B0)';
            }

            if (canFuse) {
                fuseBtn.style.background = 'transparent';
                fuseBtn.style.border = '1px solid var(--cf-cyan, #5FB3B3)';
                fuseBtn.style.color = 'var(--cf-cyan, #5FB3B3)';
                fuseBtn.style.cursor = 'pointer';
            } else {
                fuseBtn.style.background = 'transparent';
                fuseBtn.style.border = '1px solid var(--cf-divider, #2A2F3C)';
                fuseBtn.style.color = 'var(--cf-text-disabled, #3D424F)';
                fuseBtn.style.cursor = 'not-allowed';
            }
        };
        updateFuseButton();

        fuseBtn.addEventListener('click', () => {
            if (selectedmaterial.size !== cost.materialCount || !hasEnoughWavedust) return;

            // Lock user input during celebration
            const inputLock = document.createElement('div');
            inputLock.style.cssText = `
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                z-index: 10002;
                pointer-events: auto;
            `;
            document.body.appendChild(inputLock);

            // Capture old effects before fusion (no stars for v3+ bespoke items)
            const oldEffects = item.effects.map(e => ({ ...e }));

            const materialIds = [...selectedmaterial];
            const fusionResult = this.gameState.upgradeItemQuality(item.id, materialIds);

            if (fusionResult) {
                this.sfxManager?.playSound('upgrade');

                // Update wavedust display with animation
                this.wavedustAnimator.targetValue = this.gameState.getWavedust();
                this._startWavedustAnimation();

                // Screen shake effect
                const gameContainer = document.getElementById('game-container') || document.body;
                gameContainer.style.transition = 'transform 0.05s ease-out';
                let shakeCount = 0;
                const shakePattern = [4, -4, 3, -3, 2, -2, 1, -1, 0];
                const shakeInterval = setInterval(() => {
                    if (shakeCount >= shakePattern.length) {
                        clearInterval(shakeInterval);
                        gameContainer.style.transform = '';
                        gameContainer.style.transition = '';
                    } else {
                        gameContainer.style.transform = `translateX(${shakePattern[shakeCount]}px)`;
                        shakeCount++;
                    }
                }, 40);

                // Particle explosion from center
                const centerX = window.innerWidth / 2;
                const centerY = window.innerHeight / 2;
                const newColor = itemsData.rarityColors[fusionResult.newRarity] || '#5FB3B3';

                if (this.app?.gameUI?.particles) {
                    this.app.gameUI.particles.createParticles({
                        preset: 'celebration',
                        startX: centerX,
                        startY: centerY,
                        color: newColor,
                        particleCount: 24,
                        initialSpeedMin: 100,
                        initialSpeedMax: 300,
                        flightDuration: 600,
                    });
                }

                // Cleanup tooltips and close fusion modal after short delay
                setTimeout(() => {
                    cleanupTooltips();
                    modalOverlay.remove();
                    inputLock.remove();

                    // Mark item as newly fused for NEW badge
                    this._newlyFusedItemId = fusionResult.item.id;

                    // Show celebration modal with fusion metadata (no stars for v3+ bespoke items)
                    this._showFusionCelebration(fusionResult, oldEffects, itemsData, container, parentOverlay);

                    // Update equipped items if needed
                    if (this.app.equipmentManager) {
                        this.app.equipmentManager.updateEquippedItems();
                    }
                }, 350);
            } else {
                inputLock.remove();
            }
        });

        body.appendChild(fuseBtn);
        modal.appendChild(body);
        modalOverlay.appendChild(modal);
        document.body.appendChild(modalOverlay);

        // Close button handler
        modal.querySelector('#fusion-close-btn').addEventListener('click', () => {
            cleanupTooltips();
            modalOverlay.remove();
        });

        // Hover effect on close button
        const closeBtn = modal.querySelector('#fusion-close-btn');
        closeBtn.addEventListener('mouseenter', () => {
            closeBtn.style.color = 'var(--cf-text-primary, #E8E9ED)';
        });
        closeBtn.addEventListener('mouseleave', () => {
            closeBtn.style.color = 'var(--cf-text-tertiary, #5C6170)';
        });

        // Click outside to close
        modalOverlay.addEventListener('click', (e) => {
            if (e.target === modalOverlay) {
                cleanupTooltips();
                modalOverlay.remove();
            }
        });
    }

    /**
     * Show fusion celebration modal (similar to car ascension screen)
     * @param {Object} fusionResult - Result from gameState.upgradeItemQuality()
     * @param {Array} oldEffects - Effects before fusion
     * @param {Object} itemsData - Items data for colors
     * @param {Element} container - Container element
     * @param {Element} parentOverlay - Parent overlay element
     */
    _showFusionCelebration(fusionResult, oldEffects, itemsData, container, parentOverlay) {
        const { item: upgradedItem, oldRarity, newRarity, upgradeBonusPercent } = fusionResult;
        const oldColor = itemsData.rarityColors[oldRarity] || '#888';
        const newColor = itemsData.rarityColors[newRarity] || '#888';

        // Create modal overlay
        const modalOverlay = document.createElement('div');
        modalOverlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.95);
            z-index: 10001;
            display: flex;
            align-items: center;
            justify-content: center;
            pointer-events: auto;
            backdrop-filter: blur(4px);
        `;

        // Create modal content
        const modal = document.createElement('div');
        modal.style.cssText = `
            background: var(--cf-bg-primary, #11131A);
            border: 2px solid ${newColor};
            max-width: 380px;
            width: 90vw;
            max-height: 85vh;
            overflow-y: auto;
            font-family: var(--cf-font-mono);
            color: var(--cf-text-primary, #F5F5F5);
            position: relative;
            padding: 24px;
        `;

        // Title
        const title = document.createElement('div');
        title.textContent = 'QUALITY FUSION COMPLETE';
        title.style.cssText = `
            font-size: 10px;
            letter-spacing: 2px;
            color: ${newColor};
            text-align: center;
            margin-bottom: 20px;
            text-transform: uppercase;
        `;
        modal.appendChild(title);

        // Rarity transition display
        const rarityTransition = document.createElement('div');
        rarityTransition.style.cssText = `
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 15px;
            margin-bottom: 20px;
        `;

        // Old rarity
        const oldRarityDisplay = document.createElement('div');
        oldRarityDisplay.style.cssText = `text-align: center;`;

        const oldRarityBadge = document.createElement('div');
        oldRarityBadge.textContent = oldRarity.toUpperCase();
        oldRarityBadge.style.cssText = `
            font-size: 9px;
            padding: 3px 8px;
            background: ${oldColor}22;
            color: ${oldColor};
            border: 1px solid ${oldColor}66;
            letter-spacing: 0.5px;
            display: inline-block;
        `;
        oldRarityDisplay.appendChild(oldRarityBadge);
        rarityTransition.appendChild(oldRarityDisplay);

        // Arrow
        const arrow = document.createElement('div');
        arrow.textContent = '→';
        arrow.style.cssText = `
            font-size: 20px;
            color: ${newColor};
        `;
        rarityTransition.appendChild(arrow);

        // New rarity
        const newRarityDisplay = document.createElement('div');
        newRarityDisplay.style.cssText = `text-align: center;`;

        const newRarityBadge = document.createElement('div');
        newRarityBadge.textContent = newRarity.toUpperCase();
        newRarityBadge.style.cssText = `
            font-size: 9px;
            padding: 3px 8px;
            background: ${newColor}22;
            color: ${newColor};
            border: 1px solid ${newColor}66;
            letter-spacing: 0.5px;
            display: inline-block;
        `;
        newRarityDisplay.appendChild(newRarityBadge);
        rarityTransition.appendChild(newRarityDisplay);

        modal.appendChild(rarityTransition);

        // Item name
        const itemName = document.createElement('div');
        itemName.textContent = upgradedItem.name;
        itemName.style.cssText = `
            font-size: 14px;
            color: ${newColor};
            text-align: center;
            margin-bottom: 8px;
        `;
        modal.appendChild(itemName);

        // Stats section - show old vs new
        const statsSection = document.createElement('div');
        statsSection.style.cssText = `
            background: var(--cf-bg-raised, #181B24);
            border: 1px solid var(--cf-divider, #2A2F3C);
            padding: 16px;
            margin-bottom: 20px;
        `;

        const statsLabel = document.createElement('div');
        statsLabel.textContent = 'STAT IMPROVEMENTS';
        statsLabel.style.cssText = `
            font-size: 9px;
            letter-spacing: 1px;
            color: var(--cf-text-tertiary, #5C6170);
            margin-bottom: 12px;
            text-transform: uppercase;
        `;
        statsSection.appendChild(statsLabel);

        // Compare each effect
        upgradedItem.effects.forEach((newEffect, index) => {
            const oldEffect = oldEffects[index];
            if (!oldEffect) return;

            const statRow = document.createElement('div');
            statRow.style.cssText = `
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 8px;
                font-size: 11px;
            `;

            const statName = StatsFormatter.getStatDisplayName(newEffect.type);
            const labelEl = document.createElement('span');
            labelEl.textContent = statName;
            labelEl.style.cssText = `color: var(--cf-text-secondary, #9BA1B0);`;
            statRow.appendChild(labelEl);

            const valuesEl = document.createElement('div');
            valuesEl.style.cssText = `display: flex; align-items: center; gap: 8px;`;

            // Format values
            const isMultiplier = newEffect.operation === 'multiply';
            const isPercentageStat = StatsFormatter.isPercentageStat(newEffect.type);
            const isMultiplierStat = StatsFormatter.isMultiplierStat(newEffect.type);
            const unit = StatsFormatter.getStatUnit(newEffect.type);

            let oldDisplay, newDisplay;
            if (isMultiplier) {
                const oldPercent = Math.round((oldEffect.value - 1) * 100);
                const newPercent = Math.round((newEffect.value - 1) * 100);
                oldDisplay = `${oldPercent >= 0 ? '+' : ''}${oldPercent}%`;
                newDisplay = `${newPercent >= 0 ? '+' : ''}${newPercent}%`;
            } else if (isMultiplierStat) {
                const oldMultiplier = 1 + oldEffect.value;
                const newMultiplier = 1 + newEffect.value;
                oldDisplay = `${oldMultiplier.toFixed(2)}x`;
                newDisplay = `${newMultiplier.toFixed(2)}x`;
            } else if (isPercentageStat) {
                const oldPercent = Math.round(oldEffect.value * 100);
                const newPercent = Math.round(newEffect.value * 100);
                oldDisplay = `${oldPercent >= 0 ? '+' : ''}${oldPercent}%`;
                newDisplay = `${newPercent >= 0 ? '+' : ''}${newPercent}%`;
            } else {
                const decimals = newEffect.type === 'momentumRate' ? 3 : 1;
                const oldVal = Number(oldEffect.value.toFixed(decimals));
                const newVal = Number(newEffect.value.toFixed(decimals));
                const unitSuffix = unit ? ' ' + unit : '';
                oldDisplay = `${oldVal >= 0 ? '+' : ''}${oldVal}${unitSuffix}`;
                newDisplay = `${newVal >= 0 ? '+' : ''}${newVal}${unitSuffix}`;
            }

            const oldValEl = document.createElement('span');
            oldValEl.textContent = oldDisplay;
            oldValEl.style.cssText = `color: var(--cf-text-tertiary, #5C6170); text-decoration: line-through;`;
            valuesEl.appendChild(oldValEl);

            const arrowSmall = document.createElement('span');
            arrowSmall.textContent = '→';
            arrowSmall.style.cssText = `color: var(--cf-text-tertiary, #5C6170); font-size: 10px;`;
            valuesEl.appendChild(arrowSmall);

            const newValEl = document.createElement('span');
            newValEl.textContent = newDisplay;
            newValEl.style.cssText = `color: var(--cf-positive, #7CFF9E); font-weight: 500;`;
            valuesEl.appendChild(newValEl);

            statRow.appendChild(valuesEl);
            statsSection.appendChild(statRow);
        });

        // Show prestige bonus stats if item has any (these also get scaled during fusion)
        const prestigeBonusStats = this.gameState.getItemPrestigeBonusStats(upgradedItem);
        if (prestigeBonusStats && prestigeBonusStats.length > 0) {
            prestigeBonusStats.forEach(bonus => {
                const statRow = document.createElement('div');
                statRow.style.cssText = `
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 8px;
                    font-size: 11px;
                `;

                const statName = StatsFormatter.getStatDisplayName(bonus.type);
                const unit = StatsFormatter.getStatUnit(bonus.type);
                const pLevel = bonus.prestigeLevel || 1;

                // Format bonus value with units
                let display;
                if (bonus.type === 'acceleration') {
                    display = `+${Math.round(bonus.value * 100)}%`;
                } else if (bonus.type === 'momentumRate') {
                    display = `+${bonus.value.toFixed(3)}${unit ? ' ' + unit : ''}`;
                } else if (bonus.type === 'cruiseCooldown') {
                    display = `${bonus.value.toFixed(1)}${unit ? ' ' + unit : ''}`;
                } else if (StatsFormatter.isMultiplierStat(bonus.type)) {
                    const multiplier = 1 + bonus.value;
                    display = `${multiplier.toFixed(2)}x`;
                } else if (StatsFormatter.isPercentageStat(bonus.type)) {
                    display = `+${Math.round(bonus.value * 100)}%`;
                } else {
                    display = `+${bonus.value.toFixed(1)}${unit ? ' ' + unit : ''}`;
                }

                const labelEl = document.createElement('span');
                labelEl.textContent = `${display} ${statName} (P${pLevel})`;
                labelEl.style.cssText = `color: var(--cf-gold, #FFD700);`;
                statRow.appendChild(labelEl);

                statsSection.appendChild(statRow);
            });
        }

        modal.appendChild(statsSection);

        // Show upgrade bonus if player has the driver skill
        if (upgradeBonusPercent > 0) {
            const bonusSection = document.createElement('div');
            bonusSection.style.cssText = `
                background: var(--cf-lavender, #a78bfa)11;
                border: 1px solid var(--cf-lavender, #a78bfa)44;
                padding: 10px 14px;
                margin-bottom: 20px;
                display: flex;
                justify-content: space-between;
                align-items: center;
            `;

            const bonusLabel = document.createElement('span');
            bonusLabel.textContent = 'ITEM FUSION BONUS';
            bonusLabel.style.cssText = `
                font-size: 9px;
                letter-spacing: 0.5px;
                color: var(--cf-lavender, #a78bfa);
                text-transform: uppercase;
            `;
            bonusSection.appendChild(bonusLabel);

            const bonusValue = document.createElement('span');
            bonusValue.textContent = `+${upgradeBonusPercent.toFixed(1)}%`;
            bonusValue.style.cssText = `
                font-size: 11px;
                color: var(--cf-lavender, #a78bfa);
                font-weight: 500;
            `;
            bonusSection.appendChild(bonusValue);

            modal.appendChild(bonusSection);
        }

        // Button container for CONTINUE and EQUIP
        const buttonContainer = document.createElement('div');
        buttonContainer.style.cssText = `
            display: flex;
            gap: 12px;
        `;

        // Continue button
        const continueBtn = document.createElement('button');
        continueBtn.textContent = 'CONTINUE';
        continueBtn.style.cssText = `
            flex: 1;
            padding: 12px;
            background: transparent;
            border: 1px solid var(--cf-divider-light, #363C4D);
            color: var(--cf-text-secondary, #9BA1B0);
            font-family: var(--cf-font-mono);
            font-size: 11px;
            letter-spacing: 1px;
            cursor: pointer;
            transition: all 0.15s ease;
            text-transform: uppercase;
        `;
        continueBtn.addEventListener('mouseenter', () => {
            continueBtn.style.background = 'var(--cf-bg-raised, #181B24)';
        });
        continueBtn.addEventListener('mouseleave', () => {
            continueBtn.style.background = 'transparent';
        });
        continueBtn.addEventListener('click', () => {
            modalOverlay.remove();
            this._rebuildInventory(container, parentOverlay);
        });
        buttonContainer.appendChild(continueBtn);

        // Equip button (primary action)
        const equipBtn = document.createElement('button');
        equipBtn.textContent = 'EQUIP';
        equipBtn.style.cssText = `
            flex: 1;
            padding: 12px;
            background: transparent;
            border: 1px solid ${newColor};
            color: ${newColor};
            font-family: var(--cf-font-mono);
            font-size: 11px;
            letter-spacing: 1px;
            cursor: pointer;
            transition: all 0.15s ease;
            text-transform: uppercase;
        `;
        equipBtn.addEventListener('mouseenter', () => {
            equipBtn.style.background = `${newColor}22`;
        });
        equipBtn.addEventListener('mouseleave', () => {
            equipBtn.style.background = 'transparent';
        });
        equipBtn.addEventListener('click', () => {
            // Equip to appropriate slot (use inventorySystem if available, fallback to gameState)
            if (this.inventorySystem) {
                this.inventorySystem.equipItem(upgradedItem.id, upgradedItem.slotType);
            } else {
                this.gameState.equipItem(upgradedItem.id, upgradedItem.slotType);
            }
            this.sfxManager?.playSound('upgrade');

            // Update equipped items display
            if (this.app.equipmentManager) {
                this.app.equipmentManager.updateEquippedItems();
            }

            modalOverlay.remove();
            this._rebuildInventory(container, parentOverlay);
        });
        buttonContainer.appendChild(equipBtn);

        modal.appendChild(buttonContainer);

        modalOverlay.appendChild(modal);
        document.body.appendChild(modalOverlay);

        // Click outside to close
        modalOverlay.addEventListener('click', (e) => {
            if (e.target === modalOverlay) {
                modalOverlay.remove();
                this._rebuildInventory(container, parentOverlay);
            }
        });
    }

    /**
     * Build all items section with sort and bulk selection - cassette future style
     */
    _buildAllItemsSection(itemsData, inventoryItems, equippedSlots, container, parentOverlay) {
        const section = document.createElement('div');

        // Filter items first to get unequipped only
        const equippedIds = new Set(Object.values(equippedSlots).filter(i => i).map(i => i.id));
        const unequippedItems = inventoryItems.filter(item => !equippedIds.has(item.id));

        // Clean up any invalid selections (items that no longer exist or are equipped)
        const validItemIds = new Set(unequippedItems.map(i => i.id));
        for (const itemId of this.selectedItems) {
            if (!validItemIds.has(itemId)) {
                this.selectedItems.delete(itemId);
            }
        }

        // Header row with bulk select and sort
        const header = document.createElement('div');
        header.style.cssText = `
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: var(--cf-space-md, 16px) 0;
            border-bottom: 1px solid var(--cf-divider, #2A2F3C);
            margin-top: var(--cf-space-md, 16px);
        `;

        // Left side: select-all and bulk scrap info
        const bulkScrapSection = document.createElement('div');
        bulkScrapSection.style.cssText = `display: flex; align-items: center; gap: 12px;`;
        this.itemBulkScrapHeaderEl = bulkScrapSection;

        // Select-all circle
        const selectAllCircle = document.createElement('div');
        selectAllCircle.id = 'item-select-all-circle';
        const allSelected = unequippedItems.length > 0 && unequippedItems.every(i => this.selectedItems.has(i.id));
        selectAllCircle.style.cssText = `
            width: 18px;
            height: 18px;
            border: 1px solid ${allSelected ? 'var(--cf-lavender, #8B6F9C)' : 'var(--cf-divider-light, #363C4D)'};
            background: ${allSelected ? 'var(--cf-lavender-dim, #5E4A6B)' : 'transparent'};
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            font-size: 11px;
            color: var(--cf-lavender, #8B6F9C);
            transition: all 0.1s;
        `;
        selectAllCircle.textContent = allSelected ? '✓' : '';
        selectAllCircle.title = 'Select all';
        selectAllCircle.addEventListener('click', () => {
            this._toggleAllItemsSelected(unequippedItems, container, parentOverlay);
        });
        bulkScrapSection.appendChild(selectAllCircle);

        // Bulk scrap info/actions
        const bulkInfo = document.createElement('div');
        bulkInfo.id = 'item-bulk-scrap-info';
        bulkInfo.style.cssText = `
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            color: var(--cf-text-tertiary, #5C6170);
            display: flex;
            align-items: center;
        `;

        if (this.selectedItems.size > 0) {
            // Calculate total scrap value
            let totalScrapValue = 0;
            for (const itemId of this.selectedItems) {
                totalScrapValue += this.gameState.getItemScrapValue(itemId);
            }
            bulkInfo.innerHTML = `
                <span style="color: var(--cf-text-secondary, #9BA1B0);">${this.selectedItems.size} selected</span>
                <button id="item-bulk-scrap-btn" style="
                    margin-left: 12px;
                    background: transparent;
                    border: 1px solid var(--cf-negative, #E86A6A);
                    color: var(--cf-negative, #E86A6A);
                    padding: 6px 10px;
                    font-size: 10px;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    cursor: pointer;
                    font-family: inherit;
                    transition: all 0.1s;
                ">Ⓧ +${totalScrapValue} ⚙</button>
                <button id="item-bulk-cancel-btn" style="
                    margin-left: 6px;
                    background: transparent;
                    border: 1px solid var(--cf-text-tertiary, rgba(255,255,255,0.4));
                    color: var(--cf-text-tertiary, rgba(255,255,255,0.4));
                    width: 28px;
                    height: 28px;
                    padding: 0;
                    font-size: 14px;
                    cursor: pointer;
                    font-family: inherit;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    vertical-align: middle;
                ">✕</button>
            `;
        } else {
            bulkInfo.textContent = 'INVENTORY';
        }
        bulkScrapSection.appendChild(bulkInfo);
        header.appendChild(bulkScrapSection);

        // Right side: sort dropdown
        header.appendChild(this._buildSortDropdown(container, parentOverlay));
        section.appendChild(header);

        // Attach bulk action handlers
        setTimeout(() => {
            const bulkScrapBtn = document.getElementById('item-bulk-scrap-btn');
            const bulkCancelBtn = document.getElementById('item-bulk-cancel-btn');
            if (bulkScrapBtn) {
                bulkScrapBtn.addEventListener('click', () => {
                    this._bulkScrapItems(container, parentOverlay);
                });
            }
            if (bulkCancelBtn) {
                bulkCancelBtn.addEventListener('click', () => {
                    this.selectedItems.clear();
                    this._rebuildInventory(container, parentOverlay);
                });
            }
        }, 0);

        // Sort items
        const rarityOrder = { 'vectorized': 5, 'golden': 4, 'pristine': 3, 'standard': 2, 'faded': 1 };
        const slotOrder = { 'dashboard': 1, 'keychain': 2, 'rearview': 3 };

        let sortedItems = [...unequippedItems];
        if (this.inventorySortBy === 'recent') {
            sortedItems.reverse();
        } else if (this.inventorySortBy === 'quality') {
            sortedItems.sort((a, b) => (rarityOrder[b.rarity] || 0) - (rarityOrder[a.rarity] || 0));
        } else if (this.inventorySortBy === 'slot') {
            sortedItems.sort((a, b) => (slotOrder[a.slotType] || 0) - (slotOrder[b.slotType] || 0));
        }

        // Items list - cassette future style
        const list = document.createElement('div');
        list.style.cssText = `display: flex; flex-direction: column;`;

        if (sortedItems.length === 0) {
            const emptyState = document.createElement('div');
            emptyState.style.cssText = `
                text-align: center;
                padding: var(--cf-space-2xl, 48px) var(--cf-space-lg, 24px);
                color: var(--cf-text-tertiary, #5C6170);
                font-size: var(--cf-font-size-sm, 12px);
            `;
            emptyState.textContent = 'No items in inventory';
            list.appendChild(emptyState);
        } else {
            sortedItems.forEach(item => {
                if (!item) return;
                list.appendChild(this._buildInventoryItemRow(item, itemsData, equippedSlots, container, parentOverlay));
            });
        }

        section.appendChild(list);
        return section;
    }

    /**
     * Toggle all items selected/deselected
     */
    _toggleAllItemsSelected(items, container, parentOverlay) {
        const allSelected = items.every(i => this.selectedItems.has(i.id));
        if (allSelected) {
            this.selectedItems.clear();
        } else {
            items.forEach(i => this.selectedItems.add(i.id));
        }
        this._rebuildInventory(container, parentOverlay);
    }

    /**
     * Bulk scrap selected items
     */
    _bulkScrapItems(container, parentOverlay) {
        if (this.selectedItems.size === 0) return;

        // Calculate total value
        let totalValue = 0;
        for (const itemId of this.selectedItems) {
            totalValue += this.gameState.getItemScrapValue(itemId);
        }

        // Show confirmation
        this.gameUI.modals.createConfirmation({
            title: 'Bulk Scrap Items',
            message: `Scrap ${this.selectedItems.size} item(s) for ${totalValue} Salvage? This cannot be undone.`,
            confirmText: `Ⓧ SCRAP +${totalValue} ⚙`,
            cancelText: 'Cancel',
            confirmStyle: 'warning',
            onConfirm: () => {
                // Scrap all selected items (use inventorySystem if available, fallback to gameState)
                const itemIds = [...this.selectedItems];
                let totalEarned = 0;
                for (const itemId of itemIds) {
                    const result = this.inventorySystem
                        ? this.inventorySystem.scrapItem(itemId)
                        : this.gameState.scrapItem(itemId);
                    if (result.success) {
                        totalEarned += result.total;
                    }
                }

                // Update wavedust display with animation
                this.wavedustAnimator.targetValue = this.gameState.getWavedust();
                this._startWavedustAnimation();

                // Clear selection and rebuild
                this.selectedItems.clear();
                this._rebuildInventory(container, parentOverlay);
                this.sfxManager?.playSound('coin');

                // Show popup
                const bonusPercent = (this.gameState.getScrapBonusMultiplier() - 1) * 100;
                this.gameUI.popups.showScrapWavedust(window.innerWidth / 2, window.innerHeight / 2 - 50, totalEarned, bonusPercent);
            }
        });
    }

    /**
     * Build sort dropdown - cassette future style
     */
    _buildSortDropdown(container, parentOverlay) {
        const sortContainer = document.createElement('div');
        sortContainer.style.cssText = `position: relative;`;

        const sortButton = document.createElement('button');
        sortButton.textContent = `SORT: ${this.inventorySortBy.toUpperCase()}`;
        sortButton.style.cssText = `
            font-family: inherit;
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            padding: 6px 12px;
            background: transparent;
            color: var(--cf-text-tertiary, #5C6170);
            border: 1px solid var(--cf-divider, #2A2F3C);
            cursor: pointer;
            transition: all 0.1s;
        `;

        const dropdown = document.createElement('div');
        dropdown.style.cssText = `
            position: absolute;
            top: 100%;
            right: 0;
            margin-top: 4px;
            background: var(--cf-bg-primary, #11131A);
            border: 1px solid var(--cf-divider, #2A2F3C);
            padding: 4px;
            display: none;
            flex-direction: column;
            gap: 2px;
            z-index: 1000;
            min-width: 100px;
        `;

        const options = [
            { value: 'recent', label: 'Recent' },
            { value: 'quality', label: 'Quality' },
            { value: 'slot', label: 'Slot' }
        ];

        options.forEach(option => {
            const btn = document.createElement('button');
            btn.textContent = option.label.toUpperCase();
            const isActive = this.inventorySortBy === option.value;
            btn.style.cssText = `
                font-family: inherit;
                font-size: 10px;
                text-transform: uppercase;
                letter-spacing: 0.08em;
                padding: 6px 10px;
                background: ${isActive ? 'var(--cf-bg-raised, #181B24)' : 'transparent'};
                color: ${isActive ? 'var(--cf-text-primary, #E8E9ED)' : 'var(--cf-text-tertiary, #5C6170)'};
                border: none;
                cursor: pointer;
                text-align: left;
                transition: all 0.1s;
            `;
            btn.addEventListener('click', () => {
                this.inventorySortBy = option.value;
                localStorage.setItem('inventorySortBy', option.value);
                dropdown.style.display = 'none';
                this._rebuildInventory(container, parentOverlay);
            });
            dropdown.appendChild(btn);
        });

        sortButton.addEventListener('click', (e) => {
            e.stopPropagation();
            dropdown.style.display = dropdown.style.display === 'none' ? 'flex' : 'none';
        });

        // Remove any existing global listener before adding new one
        if (this.sortDropdownClickHandler) {
            document.removeEventListener('click', this.sortDropdownClickHandler);
        }
        this.sortDropdownClickHandler = (e) => {
            if (!sortContainer.contains(e.target)) {
                dropdown.style.display = 'none';
            }
        };
        document.addEventListener('click', this.sortDropdownClickHandler);

        sortContainer.appendChild(sortButton);
        sortContainer.appendChild(dropdown);
        return sortContainer;
    }

    /**
     * Build inventory item row - cassette future style with selection
     */
    _buildInventoryItemRow(item, itemsData, equippedSlots, container, parentOverlay) {
        const isUnseen = this.gameState.isItemUnseen(item.id);
        const isSelected = this.selectedItems.has(item.id);
        const rarityColor = itemsData.rarityColors[item.rarity] || '#888';

        const row = document.createElement('div');
        row.style.cssText = `
            position: relative;
            display: flex;
            align-items: center;
            gap: 12px;
            padding: var(--cf-space-md, 16px) 0;
            border-bottom: 1px solid var(--cf-divider, #2A2F3C);
        `;

        // Selection circle (like waves)
        const selectCircle = document.createElement('div');
        selectCircle.className = 'item-select-circle';
        selectCircle.style.cssText = `
            width: 18px;
            height: 18px;
            min-width: 18px;
            border: 1px solid ${isSelected ? 'var(--cf-lavender, #8B6F9C)' : 'var(--cf-divider-light, #363C4D)'};
            background: ${isSelected ? 'var(--cf-lavender-dim, #5E4A6B)' : 'transparent'};
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            font-size: 11px;
            color: var(--cf-lavender, #8B6F9C);
            transition: all 0.1s;
            flex-shrink: 0;
        `;
        selectCircle.textContent = isSelected ? '✓' : '';
        selectCircle.addEventListener('click', (e) => {
            e.stopPropagation();
            if (this.selectedItems.has(item.id)) {
                this.selectedItems.delete(item.id);
            } else {
                this.selectedItems.add(item.id);
            }
            this._rebuildInventory(container, parentOverlay);
        });
        row.appendChild(selectCircle);

        // Item sprite with tier-colored background
        const slotIcons = { 'rearview': '🪞', 'dashboard': '📊', 'keychain': '🔑' };
        const spriteContainer = document.createElement('div');
        spriteContainer.style.cssText = `
            width: 32px;
            height: 32px;
            min-width: 32px;
            background: ${rarityColor}33;
            border: 2px solid ${rarityColor};
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
            flex-shrink: 0;
        `;
        const itemSlot = item.slotType || item.slot;
        spriteContainer.textContent = slotIcons[itemSlot] || '📦';
        row.appendChild(spriteContainer);

        // NEW badge for unseen items or newly fused items (top-right, above dividers)
        const isNewlyFused = this._newlyFusedItemId === item.id;
        if (isUnseen || isNewlyFused) {
            const badge = document.createElement('div');
            badge.className = 'cf-new-badge cf-new-badge--item';
            badge.textContent = isNewlyFused ? 'FUSED' : 'NEW';
            if (isNewlyFused) {
                badge.style.background = 'var(--cf-cyan, #5FB3B3)';
                badge.style.color = '#000';
            }
            row.appendChild(badge);
        }

        // Item info section (main content)
        const infoSection = document.createElement('div');
        infoSection.style.cssText = `flex: 1; min-width: 0;`;

        // Item name row with slot type
        const nameRow = document.createElement('div');
        nameRow.style.cssText = `
            display: flex;
            align-items: baseline;
            gap: 8px;
            margin-bottom: 2px;
        `;

        const itemName = document.createElement('span');
        if (item.isLegacy) {
            const levelDisplay = item.level >= 10 ? ' (MAX)' : ` (Lv ${item.level})`;
            itemName.textContent = item.name + levelDisplay;
        } else {
            itemName.textContent = item.name;
        }
        itemName.dataset.itemName = item.id;
        itemName.style.cssText = `
            font-size: var(--cf-font-size-sm, 12px);
            color: ${rarityColor};
        `;
        nameRow.appendChild(itemName);

        // Legacy badge inline
        if (item.isLegacy) {
            const legacyBadge = document.createElement('span');
            legacyBadge.textContent = 'LEGACY';
            legacyBadge.style.cssText = `
                font-size: 8px;
                color: var(--cf-warning, #E8B84A);
                letter-spacing: 0.05em;
            `;
            nameRow.appendChild(legacyBadge);
        }

        infoSection.appendChild(nameRow);

        // Meta row (slot + rarity) - no stars for v3+ bespoke items
        const metaRow = document.createElement('div');
        metaRow.style.cssText = `
            font-size: var(--cf-font-size-xs, 10px);
            color: var(--cf-text-tertiary, #5C6170);
            text-transform: uppercase;
            letter-spacing: 0.08em;
            margin-bottom: 4px;
            display: flex;
            align-items: center;
            gap: 6px;
        `;

        const metaText = document.createElement('span');
        metaText.textContent = item.slotType || item.slot || 'item';
        metaRow.appendChild(metaText);

        // Prestige badge (P1, P2, etc.)
        if (item.prestigeLevel > 0) {
            const prestigeBadge = document.createElement('span');
            prestigeBadge.textContent = `P${item.prestigeLevel}`;
            prestigeBadge.style.cssText = `
                color: var(--cf-gold, #FFD700);
                font-size: 9px;
                letter-spacing: 0.05em;
            `;
            metaRow.appendChild(prestigeBadge);
        }

        infoSection.appendChild(metaRow);

        // Effects row
        const itemEffects = document.createElement('div');
        itemEffects.innerHTML = this._formatItemEffects(item, { size: 'xs', noMargin: true });
        itemEffects.dataset.itemEffects = item.id;
        infoSection.appendChild(itemEffects);

        // Make info section clickable to show comparison tooltip (like hovering SWAP)
        infoSection.style.cursor = 'pointer';
        infoSection.addEventListener('click', (e) => {
            e.stopPropagation();
            // If there's an equipped item in this slot, show comparison
            if (currentSlotEquipped) {
                // Check if this slot's comparison is already showing
                const slot = document.querySelector(`[data-equip-slot="${item.slotType}"]`);
                const isShowing = slot?._tooltip?.style.display === 'block';

                // Hide any currently shown comparison first
                if (this._activeComparisonSlot && this._activeComparisonSlot !== item.slotType) {
                    this._highlightEquipSlot(this._activeComparisonSlot, false);
                }

                if (isShowing) {
                    // Toggle off
                    this._highlightEquipSlot(item.slotType, false);
                    this._activeComparisonSlot = null;
                } else {
                    // Toggle on
                    this._highlightEquipSlot(item.slotType, true);
                    this._activeComparisonSlot = item.slotType;
                }
            }
        });

        row.appendChild(infoSection);

        // Right side - action buttons
        const actionsSection = document.createElement('div');
        actionsSection.className = 'cf-inventory-actions';

        // Equip button
        const currentSlotEquipped = equippedSlots[item.slotType];
        const equipBtn = document.createElement('button');
        equipBtn.textContent = currentSlotEquipped ? 'SWAP' : 'EQUIP';
        equipBtn.dataset.targetSlot = item.slotType;
        equipBtn.style.cssText = `
            background: transparent;
            border: 1px solid var(--cf-cyan-dim, #3D7A7A);
            color: var(--cf-cyan, #5FB3B3);
            padding: 6px 10px;
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            cursor: pointer;
            font-family: inherit;
            transition: all 0.1s;
        `;

        // Highlight target equipment slot on hover
        equipBtn.addEventListener('mouseenter', () => {
            this._highlightEquipSlot(item.slotType, true);
        });
        equipBtn.addEventListener('mouseleave', () => {
            this._highlightEquipSlot(item.slotType, false);
            if (this._activeComparisonSlot === item.slotType) {
                this._activeComparisonSlot = null;
            }
        });

        equipBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this._highlightEquipSlot(item.slotType, false); // Clear highlight
            // Use inventorySystem if available, fallback to gameState
            if (this.inventorySystem) {
                this.inventorySystem.equipItem(item.id, item.slotType);
            } else {
                this.gameState.equipItem(item.id, item.slotType);
            }
            this._rebuildInventory(container, parentOverlay);
            if (this.app.equipmentManager) {
                this.app.equipmentManager.updateEquippedItems();
            }
            this.sfxManager?.playSound('upgrade');
        });
        actionsSection.appendChild(equipBtn);

        // Fusion button (for v2 items) or legacy upgrade button
        if (item.isLegacy) {
            const upgradeCost = this.gameState.getItemUpgradeCost(item.id);
            if (upgradeCost !== null) {
                actionsSection.appendChild(this._createItemUpgradeButton(item, itemsData, itemName, itemEffects));
            }
        } else {
            const fusionBtn = this._createFusionButton(item, itemsData, container, parentOverlay);
            if (fusionBtn) {
                actionsSection.appendChild(fusionBtn);
            }
        }

        // Scrap button - individual item scrapping
        const scrapValue = this.gameState.getItemScrapValue(item.id);
        const scrapBtn = document.createElement('button');
        scrapBtn.textContent = `Ⓧ +${scrapValue} ⚙`;
        scrapBtn.title = 'Scrap item';
        scrapBtn.style.cssText = `
            background: transparent;
            border: 1px solid var(--cf-negative, #E86A6A);
            color: var(--cf-negative, #E86A6A);
            padding: 6px 8px;
            font-size: 10px;
            cursor: pointer;
            font-family: inherit;
            transition: all 0.1s;
        `;
        scrapBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            // Capture button position BEFORE showing confirmation modal
            const rect = scrapBtn.getBoundingClientRect();
            const btnPos = { x: rect.left + rect.width / 2, y: rect.top };
            this._showItemScrapConfirmation(item, scrapValue, btnPos, container, parentOverlay);
        });
        actionsSection.appendChild(scrapBtn);

        row.appendChild(actionsSection);
        return row;
    }

    /**
     * Build waves content tab
     */
    _buildWavesContent(container, parentOverlay) {
        let allPacks = this.gameState.getAvailablePacks();

        // Sort packs based on current sort preference
        if (this.wavesSortBy === 'quality') {
            allPacks = [...allPacks].sort((a, b) => b.packQuality - a.packQuality);
        } else {
            // 'recent' - sort by milestone descending (most recent first)
            allPacks = [...allPacks].sort((a, b) => b.milestone - a.milestone);
        }

        // Header row with scrap actions and sort button
        const headerRow = document.createElement('div');
        headerRow.style.cssText = `
            display: flex;
            position: sticky;
            top: 0;
            z-index: 10;
            background: var(--cf-bg-primary, #0D0D12);
            border-bottom: 1px solid var(--cf-divider, rgba(255,255,255,0.08));
            padding: var(--cf-space-sm, 8px) var(--cf-space-md, 12px);
            margin-bottom: var(--cf-space-sm, 8px);
            min-height: 40px;
            align-items: center;
            justify-content: space-between;
            gap: var(--cf-space-sm, 8px);
            backdrop-filter: blur(5px);
            box-sizing: border-box;
        `;

        // Left side: scrap info/actions
        const bulkScrapSection = document.createElement('div');
        bulkScrapSection.id = 'bulk-scrap-header';
        bulkScrapSection.style.cssText = `display: flex; align-items: center; gap: 8px; flex: 1;`;

        // Select-all square (matches dash selection style)
        const selectAllCircle = document.createElement('div');
        selectAllCircle.id = 'select-all-circle';
        selectAllCircle.title = 'Select all';
        selectAllCircle.style.cssText = `
            width: 18px;
            height: 18px;
            min-width: 18px;
            border: 1px solid var(--cf-text-tertiary, rgba(255,255,255,0.4));
            background: transparent;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            -webkit-tap-highlight-color: transparent;
            touch-action: manipulation;
            user-select: none;
            transition: all 0.1s;
            font-size: 11px;
            color: var(--cf-lavender, #a78bfa);
        `;
        selectAllCircle.textContent = '';
        selectAllCircle._checkmark = null; // Will set text directly

        selectAllCircle.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            e.preventDefault();
            this._toggleSelectAllWaves();
        });
        bulkScrapSection.appendChild(selectAllCircle);

        // Bulk scrap info/actions - unified styling matching DASH tab
        const bulkScrapInfo = document.createElement('div');
        bulkScrapInfo.id = 'bulk-scrap-info';
        bulkScrapInfo.style.cssText = `
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            color: var(--cf-text-tertiary, #5C6170);
            display: flex;
            align-items: center;
        `;
        bulkScrapInfo.textContent = 'PICKUPS';
        bulkScrapSection.appendChild(bulkScrapInfo);

        // Attach bulk action handlers after render
        this._pendingBulkScrapHandlers = parentOverlay;
        headerRow.appendChild(bulkScrapSection);

        // Right side: sort dropdown
        const sortContainer = document.createElement('div');
        sortContainer.style.cssText = `position: relative;`;

        const sortButton = document.createElement('button');
        sortButton.textContent = `SORT: ${this.wavesSortBy.toUpperCase()}`;
        sortButton.style.cssText = `
            font-family: inherit;
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            padding: 6px 12px;
            background: transparent;
            color: var(--cf-text-tertiary, #5C6170);
            border: 1px solid var(--cf-divider, #2A2F3C);
            cursor: pointer;
            transition: all 0.1s;
        `;

        const dropdown = document.createElement('div');
        dropdown.style.cssText = `
            position: absolute;
            top: 100%;
            right: 0;
            margin-top: 4px;
            background: var(--cf-bg-primary, #11131A);
            border: 1px solid var(--cf-divider, #2A2F3C);
            padding: 4px;
            display: none;
            flex-direction: column;
            gap: 2px;
            z-index: 1000;
            min-width: 100px;
        `;

        const sortOptions = [
            { value: 'recent', label: 'Recent' },
            { value: 'quality', label: 'Quality' }
        ];

        sortOptions.forEach(option => {
            const btn = document.createElement('button');
            btn.textContent = option.label.toUpperCase();
            const isActive = this.wavesSortBy === option.value;
            btn.style.cssText = `
                font-family: inherit;
                font-size: 10px;
                text-transform: uppercase;
                letter-spacing: 0.08em;
                padding: 6px 10px;
                background: ${isActive ? 'var(--cf-bg-raised, #181B24)' : 'transparent'};
                color: ${isActive ? 'var(--cf-text-primary, #E8E9ED)' : 'var(--cf-text-tertiary, #5C6170)'};
                border: none;
                cursor: pointer;
                text-align: left;
                transition: all 0.1s;
            `;
            btn.addEventListener('click', () => {
                this.wavesSortBy = option.value;
                localStorage.setItem('wavesSortBy', option.value);
                dropdown.style.display = 'none';
                this._rebuildWaves(container, parentOverlay);
            });
            dropdown.appendChild(btn);
        });

        sortButton.addEventListener('click', (e) => {
            e.stopPropagation();
            dropdown.style.display = dropdown.style.display === 'none' ? 'flex' : 'none';
        });

        // Remove any existing global listener before adding new one
        if (this.wavesSortDropdownClickHandler) {
            document.removeEventListener('click', this.wavesSortDropdownClickHandler);
        }
        this.wavesSortDropdownClickHandler = (e) => {
            if (!sortContainer.contains(e.target)) {
                dropdown.style.display = 'none';
            }
        };
        document.addEventListener('click', this.wavesSortDropdownClickHandler);

        sortContainer.appendChild(sortButton);
        sortContainer.appendChild(dropdown);
        headerRow.appendChild(sortContainer);

        container.appendChild(headerRow);
        this.wavesHeaderRowEl = headerRow;
        this.bulkScrapHeaderEl = bulkScrapSection;

        const list = document.createElement('div');
        list.className = 'cf-waves-list';

        if (allPacks.length > 0) {
            allPacks.forEach(packData => {
                list.appendChild(this._buildPackRow(packData, parentOverlay));
            });
        } else {
            const emptyState = document.createElement('div');
            emptyState.className = 'cf-muted';
            emptyState.style.cssText = 'text-align: center; padding: var(--cf-space-2xl) var(--cf-space-lg);';
            const emptyTitle = document.createElement('div');
            emptyTitle.className = 'cf-value-large';
            emptyTitle.style.marginBottom = 'var(--cf-space-md)';
            emptyTitle.textContent = 'NO PICKUPS';
            const emptySubtitle = document.createElement('div');
            emptySubtitle.className = 'cf-label';
            emptySubtitle.textContent = 'KEEP GOING';
            emptyState.appendChild(emptyTitle);
            emptyState.appendChild(emptySubtitle);
            list.appendChild(emptyState);
        }

        container.appendChild(list);

        // Update bulk scrap header visibility
        this._updateBulkScrapHeader();
    }

    /**
     * Rebuild waves list (for sorting)
     */
    _rebuildWaves(container, parentOverlay) {
        this._clearChildren(container);
        this._bulkScrapDomCache = null; // Clear stale DOM cache
        this._buildWavesContent(container, parentOverlay);
    }

    // ========== Collection Tab ==========

    /**
     * Build collection content tab - shows milestone progress bar and item grid
     */
    _buildCollectionContent(container, parentOverlay) {
        const collectionManager = this.gameState.collectionStateManager;
        if (!collectionManager) {
            const errorMsg = this._createElement('div', null, 'Collection not available');
            errorMsg.style.cssText = 'color: var(--cf-text-tertiary); padding: 20px; text-align: center;';
            container.appendChild(errorMsg);
            return;
        }

        const foundItems = collectionManager.getFoundItems();
        const foundCount = collectionManager.getFoundCount();
        const totalItems = collectionManager.getTotalItems();

        // Milestone tracker (integrated progress bar with milestone markers)
        const milestoneTracker = this._buildMilestoneTracker(collectionManager, parentOverlay, foundCount, totalItems);
        container.appendChild(milestoneTracker);

        // Item grid
        const gridContainer = document.createElement('div');
        gridContainer.className = 'cf-collection-grid';

        // Get all items from registry and sort by slot then by id
        const allItems = itemRegistry.getAll();
        const sortedItems = [...allItems].sort((a, b) => {
            // Sort by slot: rearview, dashboard, keychain
            const slotOrder = { 'rearview': 0, 'dashboard': 1, 'keychain': 2 };
            const slotA = slotOrder[a.slot || a.slotType] ?? 3;
            const slotB = slotOrder[b.slot || b.slotType] ?? 3;
            if (slotA !== slotB) return slotA - slotB;
            // Then by id
            return a.id.localeCompare(b.id);
        });

        // Build item cards using DocumentFragment for efficiency
        const fragment = document.createDocumentFragment();
        for (const item of sortedItems) {
            const isFound = foundItems.has(item.id);
            const card = this._buildCollectionItemCard(item, isFound);
            fragment.appendChild(card);
        }
        gridContainer.appendChild(fragment);

        // Hide tooltips on scroll (grid is recreated each rebuild, so listener is fresh)
        gridContainer.addEventListener('scroll', () => {
            document.querySelectorAll('.cf-collection-tooltip').forEach(t => t.style.display = 'none');
            document.querySelectorAll('.cf-milestone-tooltip').forEach(t => t.style.display = 'none');
        });

        container.appendChild(gridContainer);
    }

    /**
     * Build horizontal milestone tracker with integrated progress bar
     */
    _buildMilestoneTracker(collectionManager, parentOverlay, foundCount, totalItems) {
        const tracker = document.createElement('div');
        tracker.className = 'cf-milestone-tracker';

        // Stylized count display - centered
        const countDisplay = document.createElement('div');
        countDisplay.className = 'cf-milestone-tracker-count';

        const foundLabel = document.createElement('span');
        foundLabel.className = 'cf-milestone-count-label';
        foundLabel.textContent = 'FOUND';
        countDisplay.appendChild(foundLabel);

        const countValue = document.createElement('span');
        countValue.className = 'cf-milestone-count-value';
        countValue.textContent = `${foundCount}`;
        countDisplay.appendChild(countValue);

        const countSeparator = document.createElement('span');
        countSeparator.className = 'cf-milestone-count-separator';
        countSeparator.textContent = '/';
        countDisplay.appendChild(countSeparator);

        const totalValue = document.createElement('span');
        totalValue.className = 'cf-milestone-count-total';
        totalValue.textContent = `${totalItems}`;
        countDisplay.appendChild(totalValue);

        tracker.appendChild(countDisplay);

        // Progress bar track
        const track = document.createElement('div');
        track.className = 'cf-milestone-progress-track';

        // Progress fill
        const progress = totalItems > 0 ? (foundCount / totalItems) : 0;
        let progressColor = 'var(--cf-text-tertiary, #666)';
        if (progress >= 0.2) progressColor = 'var(--cf-cyan, #44aaff)';
        if (progress >= 0.5) progressColor = 'var(--cf-lavender, #a78bfa)';
        if (progress >= 0.8) progressColor = '#c8a030';
        if (progress >= 1) progressColor = '#ffd700';

        const fill = document.createElement('div');
        fill.className = 'cf-milestone-progress-fill';
        fill.style.cssText = `width: ${Math.round(progress * 100)}%; background: ${progressColor};`;
        track.appendChild(fill);

        // Milestone markers with tooltips
        const milestones = collectionManager.getAllMilestones();
        const claimedThresholds = collectionManager.getClaimedMilestones();
        let claimableMilestone = null;

        for (const milestone of milestones) {
            const isClaimed = claimedThresholds.includes(milestone.threshold);
            const canClaim = !isClaimed && foundCount >= milestone.threshold;
            const position = (milestone.threshold / totalItems) * 100;

            if (canClaim && !claimableMilestone) {
                claimableMilestone = milestone;
            }

            const marker = document.createElement('div');
            marker.className = `cf-milestone-marker${isClaimed ? ' cf-milestone-marker--claimed' : ''}${canClaim ? ' cf-milestone-marker--claimable' : ''}`;
            marker.style.left = `${position}%`;
            marker.textContent = isClaimed ? '✓' : milestone.threshold;

            // Create tooltip for milestone
            const tooltip = this._createMilestoneTooltip(milestone, isClaimed, canClaim);
            marker._tooltip = tooltip;

            // Mouse events for tooltip
            marker.addEventListener('mouseenter', () => {
                tooltip.style.display = 'block';
                this.gameUI.positionTooltip(tooltip, marker);
            });
            marker.addEventListener('mouseleave', () => {
                tooltip.style.display = 'none';
            });

            // Touch support: tap to toggle tooltip (and claim if claimable)
            marker.addEventListener('touchstart', (e) => {
                e.preventDefault();
                const isVisible = tooltip.style.display === 'block';
                // Hide all other milestone tooltips first
                document.querySelectorAll('.cf-milestone-tooltip').forEach(t => t.style.display = 'none');
                if (!isVisible) {
                    tooltip.style.display = 'block';
                    this.gameUI.positionTooltip(tooltip, marker);
                }
            });

            // Click to claim if claimable
            if (canClaim) {
                marker.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this._claimMilestone(milestone, collectionManager, parentOverlay, tracker);
                });
            }

            track.appendChild(marker);
        }

        tracker.appendChild(track);

        // Labels below progress bar
        const labels = document.createElement('div');
        labels.className = 'cf-milestone-labels';

        for (const milestone of milestones) {
            const isClaimed = claimedThresholds.includes(milestone.threshold);
            const canClaim = !isClaimed && foundCount >= milestone.threshold;
            const position = (milestone.threshold / totalItems) * 100;

            const label = document.createElement('div');
            label.className = `cf-milestone-label${isClaimed ? ' cf-milestone-label--claimed' : ''}${canClaim ? ' cf-milestone-label--claimable' : ''}`;
            label.style.left = `${position}%`;
            label.textContent = milestone.threshold;
            labels.appendChild(label);
        }

        tracker.appendChild(labels);

        // Show active bonuses below labels
        const bonuses = collectionManager.getMilestoneRewards();
        if (Object.keys(bonuses).length > 0) {
            const bonusContainer = document.createElement('div');
            bonusContainer.className = 'cf-milestone-bonus-row';
            for (const [stat, value] of Object.entries(bonuses)) {
                const bonusTag = document.createElement('span');
                bonusTag.className = 'cf-milestone-tracker-bonus';
                bonusTag.textContent = this._formatStatBonus(stat, value);
                bonusContainer.appendChild(bonusTag);
            }
            tracker.appendChild(bonusContainer);
        }

        // Claim button row (only if there's a claimable milestone)
        if (claimableMilestone) {
            const claimRow = document.createElement('div');
            claimRow.className = 'cf-milestone-claim-row';

            const claimBtn = document.createElement('button');
            claimBtn.className = 'cf-milestone-claim-btn';
            claimBtn.textContent = `CLAIM "${claimableMilestone.title}"`;

            claimBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                this._claimMilestone(claimableMilestone, collectionManager, parentOverlay, tracker);
            });

            claimRow.appendChild(claimBtn);
            tracker.appendChild(claimRow);
        }

        return tracker;
    }

    /**
     * Create tooltip for a milestone marker
     */
    _createMilestoneTooltip(milestone, isClaimed, canClaim) {
        const tooltip = document.createElement('div');
        tooltip.className = 'cf-milestone-tooltip cf-car-tooltip';
        tooltip.style.display = 'none';

        // Title
        const titleEl = document.createElement('div');
        titleEl.className = 'cf-car-tooltip-title';
        titleEl.style.color = isClaimed ? 'var(--cf-positive, #50ff50)' : canClaim ? '#c8a030' : 'var(--cf-text-primary)';
        titleEl.textContent = milestone.title;
        tooltip.appendChild(titleEl);

        // Threshold
        const thresholdEl = document.createElement('div');
        thresholdEl.style.cssText = 'font-size: 10px; color: var(--cf-text-tertiary); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;';
        thresholdEl.textContent = `${milestone.threshold} ITEMS COLLECTED`;
        tooltip.appendChild(thresholdEl);

        // Reward
        const rewardSection = document.createElement('div');
        rewardSection.style.cssText = 'border-top: 1px solid var(--cf-divider, rgba(255,255,255,0.1)); padding-top: 8px;';

        const rewardLabel = document.createElement('div');
        rewardLabel.className = 'cf-car-tooltip-label';
        rewardLabel.style.marginBottom = '4px';
        rewardLabel.textContent = 'Reward';
        rewardSection.appendChild(rewardLabel);

        const rewardValue = document.createElement('div');
        rewardValue.innerHTML = this._getMilestoneRewardDescription(milestone, { size: 'compact', align: 'left', noMargin: true });
        rewardSection.appendChild(rewardValue);

        // Status
        const statusRow = document.createElement('div');
        statusRow.className = 'cf-car-tooltip-row';
        statusRow.style.marginTop = '4px';

        const statusLabel = document.createElement('span');
        statusLabel.className = 'cf-car-tooltip-label';
        statusLabel.textContent = 'Status';
        statusRow.appendChild(statusLabel);

        const statusValue = document.createElement('span');
        statusValue.className = 'cf-car-tooltip-value';
        if (isClaimed) {
            statusValue.textContent = 'CLAIMED';
            statusValue.style.color = 'var(--cf-positive, #50ff50)';
        } else if (canClaim) {
            statusValue.textContent = 'READY TO CLAIM';
            statusValue.style.color = '#c8a030';
        } else {
            statusValue.textContent = 'LOCKED';
            statusValue.style.color = 'var(--cf-text-tertiary)';
        }
        statusRow.appendChild(statusValue);

        rewardSection.appendChild(statusRow);
        tooltip.appendChild(rewardSection);

        document.body.appendChild(tooltip);
        return tooltip;
    }

    /**
     * Build a single collection item card (compact: sprite + name only, tooltip for details)
     */
    _buildCollectionItemCard(item, isFound) {
        const card = document.createElement('div');
        const slot = item.slot || item.slotType;
        card.className = `cf-collection-item${isFound ? ` cf-collection-item--found tier-${slot}` : ' cf-collection-item--locked'}`;
        card.style.position = 'relative';

        // Item sprite/icon (or silhouette)
        const spriteContainer = document.createElement('div');
        spriteContainer.className = 'cf-collection-sprite';

        // Show slot icon for found items, question mark for locked
        const slotIcons = { 'rearview': '🪞', 'dashboard': '📊', 'keychain': '🔑' };
        spriteContainer.textContent = isFound ? (slotIcons[slot] || '📦') : '?';
        card.appendChild(spriteContainer);

        // Item name
        const nameEl = document.createElement('div');
        nameEl.className = 'cf-collection-name';
        nameEl.textContent = isFound ? item.name : '???';
        card.appendChild(nameEl);

        // NEW badge for unseen discoveries (top-right corner)
        if (isFound && !this.gameState.hasSeenCollectionItem(item.id)) {
            const newBadge = document.createElement('div');
            newBadge.className = 'collection-new-badge cf-new-badge';
            newBadge.textContent = 'NEW';
            newBadge.style.cssText = `
                position: absolute;
                top: 2px;
                right: 2px;
                font-size: 7px;
                padding: 1px 3px;
            `;
            card.appendChild(newBadge);
        }

        // Add tooltip for found items
        if (isFound) {
            const tooltip = this._createCollectionItemTooltip(item);
            card._tooltip = tooltip;

            // Mouse events for tooltip
            card.addEventListener('mouseenter', () => {
                tooltip.style.display = 'block';
                this.gameUI.positionTooltip(tooltip, card);
            });
            card.addEventListener('mouseleave', () => {
                tooltip.style.display = 'none';
            });

            // Touch support: tap to toggle tooltip
            card.addEventListener('touchstart', (e) => {
                e.preventDefault();
                const isVisible = tooltip.style.display === 'block';
                // Hide all other collection tooltips first
                document.querySelectorAll('.cf-collection-tooltip').forEach(t => t.style.display = 'none');
                if (!isVisible) {
                    tooltip.style.display = 'block';
                    this.gameUI.positionTooltip(tooltip, card);
                }
            });
        }

        return card;
    }

    /**
     * Create tooltip for a collection item (matches cf-car-tooltip pattern)
     */
    _createCollectionItemTooltip(item) {
        const slot = item.slot || item.slotType;
        const slotColors = {
            'rearview': 'var(--cf-cyan, #44aaff)',
            'dashboard': 'var(--cf-lavender, #a78bfa)',
            'keychain': '#c8a030'
        };
        const slotColor = slotColors[slot] || 'var(--cf-text-primary)';

        const tooltip = document.createElement('div');
        tooltip.className = 'cf-collection-tooltip cf-car-tooltip';
        tooltip.style.display = 'none';

        // Title (item name)
        const titleEl = document.createElement('div');
        titleEl.className = 'cf-car-tooltip-title';
        titleEl.style.color = slotColor;
        titleEl.textContent = item.name;
        tooltip.appendChild(titleEl);

        // Slot type
        const slotEl = document.createElement('div');
        slotEl.className = 'cf-collection-tooltip-slot';
        slotEl.textContent = slot.toUpperCase();
        tooltip.appendChild(slotEl);

        // Description (flavor text)
        if (item.description) {
            const descEl = document.createElement('div');
            descEl.className = 'cf-collection-tooltip-desc';
            descEl.textContent = `"${item.description}"`;
            tooltip.appendChild(descEl);
        }

        // Stats section
        if (item.effects && item.effects.length > 0) {
            const statsSection = document.createElement('div');
            statsSection.className = 'cf-collection-tooltip-stats';

            // Show stat ranges (faded to vectorized)
            for (const effect of item.effects) {
                const statRow = document.createElement('div');
                statRow.className = 'cf-car-tooltip-row';

                const statName = StatsFormatter.getStatDisplayName(effect.type);
                const unit = StatsFormatter.getStatUnit(effect.type);
                const isMultiplier = effect.operation === 'multiply' ||
                    (effect.operation !== 'add' && (effect.type === 'acceleration' || effect.type === 'timeScale'));
                const isPercentageStat = StatsFormatter.isPercentageStat(effect.type);
                const isMultiplierStat = StatsFormatter.isMultiplierStat(effect.type);
                const isDurationStat = StatsFormatter.isDurationStat(effect.type);

                // Get min and max values from stat_values
                const statValues = item.stat_values?.[effect.type];
                let rangeText = '';
                if (statValues) {
                    const minVal = statValues.faded;
                    const maxVal = statValues.vectorized;
                    if (isMultiplier) {
                        const minPct = Math.round((minVal - 1) * 100);
                        const maxPct = Math.round((maxVal - 1) * 100);
                        rangeText = `${minPct >= 0 ? '+' : ''}${minPct}% → ${maxPct >= 0 ? '+' : ''}${maxPct}%`;
                    } else if (isMultiplierStat) {
                        // Display as multiplier (0.04 -> 1.04x)
                        const minMult = 1 + minVal;
                        const maxMult = 1 + maxVal;
                        rangeText = `${minMult.toFixed(2)}x → ${maxMult.toFixed(2)}x`;
                    } else if (isPercentageStat) {
                        // Display as percentage (0.04 -> +4%)
                        const minPct = Math.round(minVal * 100);
                        const maxPct = Math.round(maxVal * 100);
                        rangeText = `${minPct >= 0 ? '+' : ''}${minPct}% → ${maxPct >= 0 ? '+' : ''}${maxPct}%`;
                    } else if (isDurationStat) {
                        // Display as duration (1m 30s format)
                        const minSign = minVal >= 0 ? '+' : '';
                        const maxSign = maxVal >= 0 ? '+' : '';
                        rangeText = `${minSign}${StatsFormatter.formatDurationValue(Math.abs(minVal))} → ${maxSign}${StatsFormatter.formatDurationValue(Math.abs(maxVal))}`;
                    } else {
                        const decimals = effect.type === 'momentumRate' ? 3 : 1;
                        const minFormatted = Number(minVal).toFixed(decimals);
                        const maxFormatted = Number(maxVal).toFixed(decimals);
                        const unitSuffix = unit ? ' ' + unit : '';
                        rangeText = `${minVal >= 0 ? '+' : ''}${minFormatted}${unitSuffix} → ${maxVal >= 0 ? '+' : ''}${maxFormatted}${unitSuffix}`;
                    }
                }

                const labelSpan = document.createElement('span');
                labelSpan.className = 'cf-car-tooltip-label';
                labelSpan.textContent = statName;
                statRow.appendChild(labelSpan);

                const valueSpan = document.createElement('span');
                valueSpan.className = 'cf-car-tooltip-value';
                valueSpan.style.color = 'var(--cf-positive, #50ff50)';
                valueSpan.textContent = rangeText;
                statRow.appendChild(valueSpan);

                statsSection.appendChild(statRow);
            }

            tooltip.appendChild(statsSection);
        }

        document.body.appendChild(tooltip);
        return tooltip;
    }

    /**
     * Claim a milestone and show celebration
     */
    _claimMilestone(milestone, collectionManager, parentOverlay, sectionEl) {
        const result = collectionManager.claimMilestone(milestone.threshold);
        if (!result) return;

        // Play sound effect
        this.sfxManager?.playSound('reward');

        // Show celebration popup
        this._showMilestoneCelebration(milestone);

        // Rebuild collection content to reflect new state
        if (this.collectionContentEl) {
            this._clearChildren(this.collectionContentEl);
            this._buildCollectionContent(this.collectionContentEl, parentOverlay);
        }

        // Also rebuild upgrades tab so stat values and tooltips reflect new bonuses
        // (claimMilestone already invalidated StatCalculator cache)
        this._rebuildUpgradesTab();
    }

    /**
     * Show milestone claimed celebration popup
     */
    _showMilestoneCelebration(milestone) {
        const overlay = document.createElement('div');
        overlay.style.cssText = `
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.8);
            z-index: 10001;
            display: flex;
            align-items: center;
            justify-content: center;
            animation: cf-fade 200ms ease-out forwards;
            cursor: pointer;
        `;

        const modal = document.createElement('div');
        modal.style.cssText = `
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: var(--cf-bg-primary, #0D0D12);
            border: 3px solid #c8a030;
            border-radius: 12px;
            padding: 30px 40px;
            text-align: center;
            max-width: 320px;
            box-shadow: 0 0 40px rgba(200, 160, 48, 0.4);
            animation: popupItemObtained 3s ease-out forwards;
            z-index: 10002;
        `;

        // Title earned
        const titleEl = document.createElement('div');
        titleEl.style.cssText = `
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: var(--cf-text-tertiary, #888);
            margin-bottom: 8px;
        `;
        titleEl.textContent = 'MILESTONE UNLOCKED';
        modal.appendChild(titleEl);

        // Milestone title
        const nameEl = document.createElement('div');
        nameEl.style.cssText = `
            font-size: 24px;
            font-weight: bold;
            color: #c8a030;
            margin-bottom: 12px;
        `;
        nameEl.textContent = `"${milestone.title}"`;
        modal.appendChild(nameEl);

        // Threshold
        const thresholdEl = document.createElement('div');
        thresholdEl.style.cssText = `
            font-size: 14px;
            color: var(--cf-text-secondary, #aaa);
            margin-bottom: 16px;
        `;
        thresholdEl.textContent = `${milestone.threshold} items collected`;
        modal.appendChild(thresholdEl);

        // Reward
        const rewardEl = document.createElement('div');
        rewardEl.innerHTML = this._getMilestoneRewardDescription(milestone, { size: 'normal', align: 'centered', noMargin: true });
        modal.appendChild(rewardEl);

        overlay.appendChild(modal);

        const dismiss = () => overlay.remove();
        overlay.addEventListener('click', dismiss);
        setTimeout(dismiss, 3000);

        document.body.appendChild(overlay);
    }

    /**
     * Get human-readable description of a milestone reward as bonus boxes HTML
     * @param {Object} milestone - The milestone object
     * @param {Object} options - Display options (size, align, noMargin)
     * @returns {string} HTML string with bonus boxes
     */
    _getMilestoneRewardDescription(milestone, options = {}) {
        const { size = 'normal', align = 'centered', noMargin = false } = options;
        if (milestone.reward_type === 'stat_bonus') {
            // Support both new format (rewards array) and legacy format (reward object)
            const rewards = milestone.rewards || (milestone.reward ? [milestone.reward] : []);
            if (rewards.length === 0) {
                return this._bonusBoxHTML('Reward', 'positive', size);
            }
            const bonuses = rewards.map(r => ({
                text: this._formatStatBonus(r.stat, r.value),
                variant: 'positive'
            }));
            return this._bonusRowHTML(bonuses, { align, size, noMargin });
        }
        return this._bonusBoxHTML('Reward', 'positive', size);
    }

    /**
     * Format a stat bonus for display
     */
    _formatStatBonus(stat, value) {
        const name = StatsFormatter.getStatDisplayName(stat);
        // Percentage stats: salvageBonus, waveLuck, itemUpgradeBonus
        if (StatsFormatter.isPercentageStat(stat)) {
            const percent = Math.round(value * 100);
            return `+${percent}% ${name}`;
        }
        // Multiplier stats: mileMultiplier (displayed as 1.00x format)
        if (StatsFormatter.isMultiplierStat(stat)) {
            const multiplier = 1 + value;
            return `${multiplier.toFixed(2)}x ${name}`;
        }
        // Duration stats: fuelTank (displayed as time)
        if (StatsFormatter.isDurationStat(stat)) {
            return `+${StatsFormatter.formatDurationValue(value)} ${name}`;
        }
        // Flat stats with units
        const unit = StatsFormatter.getStatUnit(stat);
        // Format small decimals (like momentumRate) with more precision
        const formatted = value < 1 ? value.toFixed(2) : value;
        return `+${formatted}${unit ? ' ' + unit : ''} ${name}`;
    }

    /**
     * Create a bonus box DOM element
     * @param {string} text - The bonus text (e.g., "+15% Idle Speed")
     * @param {string} variant - Color variant: 'positive', 'gold', 'cyan', 'lavender', 'warning'
     * @param {string} size - Size variant: 'normal', 'compact', 'xs'
     * @returns {HTMLElement}
     */
    _createBonusBox(text, variant = 'positive', size = 'normal') {
        const box = document.createElement('span');
        box.className = `cf-bonus-box cf-bonus-box--${variant}`;
        if (size === 'compact') box.classList.add('cf-bonus-box--compact');
        if (size === 'xs') box.classList.add('cf-bonus-box--xs');
        box.textContent = text;
        return box;
    }

    /**
     * Create a row of bonus boxes (DOM elements)
     * @param {Array<{text: string, variant?: string}>} bonuses - Array of bonus objects
     * @param {Object} options - Container options
     * @param {string} options.align - 'centered' or 'left' (default: 'centered')
     * @param {boolean} options.compact - Use compact spacing
     * @param {string} options.size - Size for all boxes: 'normal', 'compact', 'xs'
     * @param {boolean} options.noMargin - Remove top margin
     * @returns {HTMLElement}
     */
    _createBonusRow(bonuses, options = {}) {
        const { align = 'centered', compact = false, size = 'normal', noMargin = false } = options;

        const container = document.createElement('div');
        container.className = 'cf-bonus-row';
        if (align === 'centered') container.classList.add('cf-bonus-row--centered');
        if (align === 'left') container.classList.add('cf-bonus-row--left');
        if (compact) container.classList.add('cf-bonus-row--compact');
        if (noMargin) container.classList.add('cf-bonus-row--no-margin');

        for (const bonus of bonuses) {
            const variant = bonus.variant || 'positive';
            container.appendChild(this._createBonusBox(bonus.text, variant, size));
        }

        return container;
    }

    /**
     * Generate HTML string for a bonus box
     * @param {string} text - The bonus text
     * @param {string} variant - Color variant
     * @param {string} size - Size variant
     * @returns {string}
     */
    _bonusBoxHTML(text, variant = 'positive', size = 'normal') {
        const sizeClass = size === 'compact' ? ' cf-bonus-box--compact' :
                          size === 'xs' ? ' cf-bonus-box--xs' : '';
        return `<span class="cf-bonus-box cf-bonus-box--${variant}${sizeClass}">${text}</span>`;
    }

    /**
     * Generate HTML string for a row of bonus boxes
     * @param {Array<{text: string, variant?: string}>} bonuses - Array of bonus objects
     * @param {Object} options - Container options
     * @returns {string}
     */
    _bonusRowHTML(bonuses, options = {}) {
        const { align = 'centered', compact = false, size = 'normal', noMargin = false } = options;

        const alignClass = align === 'centered' ? ' cf-bonus-row--centered' :
                           align === 'left' ? ' cf-bonus-row--left' : '';
        const compactClass = compact ? ' cf-bonus-row--compact' : '';
        const marginClass = noMargin ? ' cf-bonus-row--no-margin' : '';

        const boxesHTML = bonuses.map(b =>
            this._bonusBoxHTML(b.text, b.variant || 'positive', size)
        ).join('');

        return `<div class="cf-bonus-row${alignClass}${compactClass}${marginClass}">${boxesHTML}</div>`;
    }

    /**
     * Build pack row
     */
    _buildPackRow(packData, parentOverlay) {
        const tier = StatsFormatter.getWaveTier(packData.packQuality);
        const isUnseen = !this.gameState.hasSeenWave(packData.milestone);
        const isSelected = this.selectedWaves.has(packData.milestone);

        const row = document.createElement('div');
        row.dataset.milestone = packData.milestone;
        row.style.cssText = `
            background: var(--cf-bg-primary, #0D0D12);
            border-bottom: 1px solid var(--cf-divider, rgba(255,255,255,0.08));
            padding: var(--cf-space-sm, 8px) var(--cf-space-md, 12px);
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: var(--cf-space-sm, 8px);
            position: relative;
            transition: background 0.15s ease;
        `;

        // Hover effect
        row.addEventListener('mouseenter', () => {
            row.style.background = 'var(--cf-bg-raised, #16161E)';
        });
        row.addEventListener('mouseleave', () => {
            row.style.background = 'var(--cf-bg-primary, #0D0D12)';
        });

        // NEW badge for unseen waves
        if (isUnseen) {
            const newBadge = document.createElement('div');
            newBadge.className = 'wave-new-badge cf-new-badge cf-new-badge--item';
            newBadge.textContent = 'NEW';
            row.appendChild(newBadge);
        }

        // Selection square (far left) - matches dash item selection style
        const selectCircle = document.createElement('div');
        selectCircle.className = 'wave-select-circle';
        selectCircle.dataset.milestone = packData.milestone;
        selectCircle.style.cssText = `
            width: 18px;
            height: 18px;
            min-width: 18px;
            border: 1px solid ${isSelected ? 'var(--cf-lavender, #a78bfa)' : 'var(--cf-divider-light, #363C4D)'};
            background: ${isSelected ? 'var(--cf-lavender-dim, #5E4A6B)' : 'transparent'};
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            -webkit-tap-highlight-color: transparent;
            touch-action: manipulation;
            user-select: none;
            transition: all 0.1s;
            font-size: 11px;
            color: var(--cf-lavender, #a78bfa);
            flex-shrink: 0;
        `;
        selectCircle.textContent = isSelected ? '✓' : '';

        // Use pointerdown for immediate response (fires before click)
        selectCircle.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            e.preventDefault();
            this._toggleWaveSelection(packData.milestone, selectCircle);
        }, { passive: false });

        row.appendChild(selectCircle);

        // Left side (wave info) - tier name with color accent
        const leftSection = document.createElement('div');
        leftSection.style.cssText = `display: flex; align-items: center; gap: var(--cf-space-sm, 8px); flex: 1; min-width: 0;`;

        // Tier indicator (colored text)
        const tierLabel = document.createElement('span');
        tierLabel.textContent = tier.name.toUpperCase();
        tierLabel.style.cssText = `
            font-size: var(--cf-font-size-xs, 10px);
            font-weight: 500;
            color: ${tier.color};
            letter-spacing: 0.5px;
        `;
        leftSection.appendChild(tierLabel);

        // Type-specific lot indicator (shows slot type if applicable)
        if (packData.slotType) {
            const slotLabel = document.createElement('span');
            slotLabel.textContent = packData.slotType.toUpperCase();
            slotLabel.title = `This lot contains only ${packData.slotType} items`;
            slotLabel.style.cssText = `
                font-size: var(--cf-font-size-xs, 10px);
                font-weight: 500;
                color: var(--cf-cyan, #5FB3B3);
                letter-spacing: 0.5px;
                padding: 2px 4px;
                background: rgba(95, 179, 179, 0.15);
                border-radius: 2px;
            `;
            leftSection.appendChild(slotLabel);
        }

        // Lot label with mile number
        const waveNum = document.createElement('span');
        waveNum.textContent = packData.isMerged ? 'Merged Lot' : `Lot #${Math.round(packData.milestone)}`;
        waveNum.style.cssText = `
            font-size: var(--cf-font-size-sm, 12px);
            color: var(--cf-text-primary, #F5F5F5);
        `;
        leftSection.appendChild(waveNum);

        row.appendChild(leftSection);

        // Check if pack is already opened (items already generated and paid for)
        const isAlreadyOpened = packData.opened === true;

        // Right side - scrap and purchase buttons
        const rightSection = document.createElement('div');
        rightSection.style.cssText = `display: flex; gap: 8px; align-items: center;`;

        // Scrap button - only show for unopened, non-free waves (can't scrap tutorial or after peeking at items)
        if (!isAlreadyOpened && packData.cost > 0) {
            const scrapValue = this.gameState.getWaveScrapValue(packData);
            const scrapBtn = document.createElement('button');
            scrapBtn.textContent = `Ⓧ +${scrapValue} ⚙`;
            scrapBtn.title = 'Scrap lot';
            scrapBtn.dataset.buttonType = 'waveScrap';
            scrapBtn.dataset.packMilestone = packData.milestone;
            scrapBtn.style.cssText = `
                background: transparent;
                border: 1px solid var(--cf-negative, #E86A6A);
                color: var(--cf-negative, #E86A6A);
                padding: 6px 8px;
                font-size: 10px;
                cursor: pointer;
                font-family: inherit;
                transition: all 0.1s;
                text-transform: uppercase;
                letter-spacing: 0.05em;
            `;

            // Click handler for scrap - shows confirmation modal
            scrapBtn.addEventListener('click', () => {
                this._showWaveScrapConfirmation(packData, scrapValue, scrapBtn, parentOverlay);
            });

            rightSection.appendChild(scrapBtn);
        }

        // SCRAP BEST button - only show for opened waves with stored items
        if (isAlreadyOpened && packData.storedItems && packData.storedItems.length > 0) {
            // Find best item scrap value
            let bestScrapValue = 0;
            packData.storedItems.forEach(item => {
                const value = this.gameState.getItemScrapValueDirect(item);
                if (value > bestScrapValue) {
                    bestScrapValue = value;
                }
            });

            // Store the scrap best value on packData for bulk selection calculations
            packData.scrapBestValue = bestScrapValue;

            const scrapBestBtn = document.createElement('button');
            scrapBestBtn.textContent = `Ⓧ +${bestScrapValue} ⚙`;
            scrapBestBtn.title = 'Scrap best item from this lot';
            scrapBestBtn.dataset.buttonType = 'waveScrapBest';
            scrapBestBtn.dataset.packMilestone = packData.milestone;
            scrapBestBtn.dataset.scrapBestValue = bestScrapValue;
            scrapBestBtn.style.cssText = `
                background: transparent;
                border: 1px solid var(--cf-negative, #E86A6A);
                color: var(--cf-negative, #E86A6A);
                padding: 6px 8px;
                font-size: 10px;
                cursor: pointer;
                font-family: inherit;
                transition: all 0.1s;
                text-transform: uppercase;
                letter-spacing: 0.05em;
            `;

            // Click handler for scrap best
            scrapBestBtn.addEventListener('click', () => {
                this._scrapBestFromWave(packData, bestScrapValue, scrapBestBtn);
            });

            rightSection.appendChild(scrapBestBtn);
        }

        // Check if can afford (wavedust)
        const canAfford = isAlreadyOpened || this.gameState.getWavedust() >= packData.cost;

        const purchaseBtn = document.createElement('button');
        // Show "OPEN" for already-opened packs, otherwise show cost
        if (isAlreadyOpened) {
            purchaseBtn.textContent = 'OPEN';
        } else if (packData.cost === 0) {
            purchaseBtn.textContent = 'FREE';
        } else {
            purchaseBtn.textContent = `${packData.cost.toLocaleString()} ⚙`;
        }
        purchaseBtn.dataset.buttonType = 'wavePack';
        purchaseBtn.dataset.packCost = packData.cost;
        purchaseBtn.dataset.packMilestone = packData.milestone;
        // Use cyan for already-opened (OPEN button), lavender for wavedust cost
        const btnColor = isAlreadyOpened
            ? (canAfford ? 'var(--cf-cyan, #5FB3B3)' : 'var(--cf-text-tertiary, rgba(255,255,255,0.3))')
            : (canAfford ? 'var(--cf-lavender, #a78bfa)' : 'var(--cf-text-tertiary, rgba(255,255,255,0.3))');
        purchaseBtn.style.cssText = `
            background: transparent;
            border: 1px solid ${btnColor};
            color: ${btnColor};
            padding: 6px 10px;
            font-size: 10px;
            cursor: ${canAfford ? 'pointer' : 'not-allowed'};
            font-family: inherit;
            transition: all 0.1s;
            text-transform: uppercase;
            letter-spacing: 0.05em;
        `;
        purchaseBtn.className = 'cf-pack-purchase-btn';

        // Click handler
        purchaseBtn.addEventListener('click', () => {
            // Prevent double-clicks during celebration
            if (this.waveOpeningInProgress) return;

            let packItems;
            let openedPack;
            const isFirstOpen = !packData.opened;

            if (packData.opened && packData.storedItems) {
                // Pack already opened - use stored items, skip celebration
                packItems = packData.storedItems;
                openedPack = packData;
                console.log(`[WaveOpen] Using STORED items for pack at ${packData.milestone}:`,
                    packItems.map(i => i.rarity).join(', '));

                // Diagnostic: Check if stored items violate MAX_UPGRADES rule (pre-patch data)
                const RARITY_INDEX = { faded: 0, standard: 1, pristine: 2, golden: 3, vectorized: 4 };
                const lotTierIdx = packData.packQuality >= 0.8 ? 4 : packData.packQuality >= 0.6 ? 3 :
                    packData.packQuality >= 0.4 ? 2 : packData.packQuality >= 0.2 ? 1 : 0;
                const upgrades = packItems.filter(i => RARITY_INDEX[i.rarity] > lotTierIdx).length;
                if (upgrades > 1) {
                    console.warn(`[WaveOpen] ⚠️ PRE-PATCH DATA: Pack has ${upgrades} upgrades (max should be 1). ` +
                        `These items were generated before the upgrade limit was added.`);
                }

                // Show pack selection immediately (no celebration for re-opens)
                this.gameUI.showPackCardSelection(packItems, openedPack.milestone, openedPack.cost, () => {
                    this.gameUI.updatePackNotificationBadge();
                    this.refreshWavesContent();
                    this.refreshInventoryContent(); // Refresh equipped slots if TAKE & EQUIP was used
                });
            } else {
                // First time opening - check affordability
                const cost = parseInt(purchaseBtn.dataset.packCost);
                if (this.gameState.getWavedust() < cost) return;

                // Generate items BEFORE opening (so they get stored)
                // Pass slotType for type-specific lots (items will be slot-locked)
                packItems = this.app.packGenerator.generatePack(packData.packQuality, packData.slotType || null);
                const slotInfo = packData.slotType ? ` [${packData.slotType.toUpperCase()}]` : '';
                console.log(`[WaveOpen] GENERATED new items for pack at ${packData.milestone} (quality=${packData.packQuality?.toFixed(3)}${slotInfo}):`,
                    packItems.map(i => i.rarity).join(', '));

                // Verify upgrade limit is being enforced
                const RARITY_INDEX = { faded: 0, standard: 1, pristine: 2, golden: 3, vectorized: 4 };
                const lotTierIdx = packData.packQuality >= 0.8 ? 4 : packData.packQuality >= 0.6 ? 3 :
                    packData.packQuality >= 0.4 ? 2 : packData.packQuality >= 0.2 ? 1 : 0;
                const upgrades = packItems.filter(i => RARITY_INDEX[i.rarity] > lotTierIdx).length;
                if (upgrades > 1) {
                    console.error(`[WaveOpen] ❌ BUG: Generated pack has ${upgrades} upgrades (max should be 1)!`);
                }

                // Open pack - this charges miles and stores the items (also awards wavedust)
                openedPack = this.gameState.openPack(packData.milestone, packItems);
                if (!openedPack) return;

                // Update wavedust display after opening with animation
                this.wavedustAnimator.targetValue = this.gameState.getWavedust();
                this._startWavedustAnimation();

                // Play celebration animation, then show items
                this._playWaveOpenCelebration(row, tier.color, () => {
                    // Refresh to show updated button text ("OPEN" instead of cost)
                    this.refreshWavesContent();

                    // Show pack selection overlay (pass tutorial bonus to show in modal)
                    this.gameUI.showPackCardSelection(packItems, openedPack.milestone, openedPack.cost, () => {
                        this.gameUI.updatePackNotificationBadge();
                        this.refreshWavesContent();
                        this.refreshInventoryContent(); // Refresh equipped slots if TAKE & EQUIP was used
                    }, openedPack.tutorialBonus || 0);
                });
            }
        });

        rightSection.appendChild(purchaseBtn);
        row.appendChild(rightSection);

        return row;
    }

    /**
     * Show confirmation modal for scrapping a wave
     */
    _showWaveScrapConfirmation(packData, scrapValue, scrapBtn, parentOverlay) {
        const tier = StatsFormatter.getWaveTier(packData.packQuality);
        const slotLabel = packData.slotType ? ` ${packData.slotType.toUpperCase()}` : '';
        const waveName = packData.isMerged ? 'Merged Lot' : `${tier.name}${slotLabel} Lot ${Math.round(packData.milestone)}`;

        this.gameUI.modals.createConfirmation({
            title: 'Scrap Lot',
            message: `Scrap this ${waveName} for ${scrapValue} Salvage? This cannot be undone.`,
            confirmText: `Ⓧ SCRAP +${scrapValue} ⚙`,
            cancelText: 'Cancel',
            confirmStyle: 'warning',
            onConfirm: () => {
                // Get button position for particle origin BEFORE any DOM changes
                const btnRect = scrapBtn.getBoundingClientRect();
                const startX = btnRect.left + btnRect.width / 2;
                const startY = btnRect.top + btnRect.height / 2;

                // Capture scrap button position for bonus popup (before it might be removed)
                const scrapBtnPos = { x: startX, y: btnRect.top };

                // Get upgrade menu's wavedust display as target
                const targetWavedustDisplay = this.menuWavedustDisplay;

                // Create purple scrap particles flowing to wavedust display
                if (targetWavedustDisplay && this.particles) {
                    this.particles.createParticles({
                        preset: 'scrap',
                        startX,
                        startY,
                        target: targetWavedustDisplay,
                        color: '#a78bfa', // Purple for wavedust
                        onComplete: () => {
                            // Actually scrap the wave after animation completes
                            const result = this.gameState.scrapWave(packData.milestone);
                            if (result.success) {
                                // Show scrap popup with wavedust and optional bonus
                                const bonusPercent = (this.gameState.getScrapBonusMultiplier() - 1) * 100;
                                this.gameUI.popups.showScrapWavedust(scrapBtnPos.x, scrapBtnPos.y, result.total, bonusPercent);

                                // Update wavedust display with animation
                                this.wavedustAnimator.targetValue = this.gameState.getWavedust();
                                this._startWavedustAnimation();

                                // Refresh waves content
                                this.refreshWavesContent();
                                // Update badge
                                this.gameUI.updatePackNotificationBadge();
                            }
                        }
                    });
                } else {
                    // Fallback: scrap immediately if no particles
                    const result = this.gameState.scrapWave(packData.milestone);
                    if (result.success) {
                        // Show scrap popup with wavedust and optional bonus
                        const bonusPercent = (this.gameState.getScrapBonusMultiplier() - 1) * 100;
                        this.gameUI.popups.showScrapWavedust(scrapBtnPos.x, scrapBtnPos.y, result.total, bonusPercent);

                        // Update wavedust display with animation
                        this.wavedustAnimator.targetValue = this.gameState.getWavedust();
                        this._startWavedustAnimation();

                        this.refreshWavesContent();
                        this.gameUI.updatePackNotificationBadge();
                    }
                }
            }
        });
    }

    /**
     * Scrap the best item from an opened wave
     */
    _scrapBestFromWave(packData, bestScrapValue, scrapBtn) {
        const tier = StatsFormatter.getWaveTier(packData.packQuality);
        const slotLabel = packData.slotType ? ` ${packData.slotType.toUpperCase()}` : '';
        const waveName = packData.isMerged ? 'Merged Lot' : `${tier.name}${slotLabel} Lot ${Math.round(packData.milestone)}`;

        this.gameUI.modals.createConfirmation({
            title: 'Scrap Best Item',
            message: `Scrap the best item from this ${waveName} for ${bestScrapValue} Salvage? This cannot be undone.`,
            confirmText: `Ⓧ SCRAP +${bestScrapValue} ⚙`,
            cancelText: 'Cancel',
            confirmStyle: 'warning',
            onConfirm: () => {
                // Get button position for particle origin
                const btnRect = scrapBtn.getBoundingClientRect();
                const startX = btnRect.left + btnRect.width / 2;
                const startY = btnRect.top + btnRect.height / 2;
                const scrapBtnPos = { x: startX, y: btnRect.top };

                // Apply scrap bonus multiplier
                const multiplier = this.gameState.getScrapBonusMultiplier(true);
                const totalValue = Math.floor(bestScrapValue * multiplier);

                // Add wavedust
                this.gameState.addWavedust(totalValue);

                // Confirm pack purchase (removes the wave)
                this.gameState.confirmPackPurchase(packData.milestone);

                // Play sound
                this.sfxManager?.playSound('upgrade');

                // Show scrap popup
                const bonusPercent = (this.gameState.getScrapBonusMultiplier() - 1) * 100;
                this.gameUI.popups.showScrapWavedust(scrapBtnPos.x, scrapBtnPos.y, totalValue, bonusPercent);

                // Update wavedust display with animation
                this.wavedustAnimator.targetValue = this.gameState.getWavedust();
                this._startWavedustAnimation();

                // Refresh waves content
                this.refreshWavesContent();
                this.gameUI.updatePackNotificationBadge();
            }
        });
    }

    /**
     * Toggle wave selection state - optimized for rapid clicking
     */
    _toggleWaveSelection(milestone, circleEl) {
        // Immediate visual feedback (no waiting for expensive calculations)
        if (this.selectedWaves.has(milestone)) {
            this.selectedWaves.delete(milestone);
            circleEl.style.background = 'transparent';
            circleEl.style.borderColor = 'var(--cf-divider-light, #363C4D)';
            circleEl.textContent = '';
        } else {
            this.selectedWaves.add(milestone);
            circleEl.style.background = 'var(--cf-lavender-dim, #5E4A6B)';
            circleEl.style.borderColor = 'var(--cf-lavender, #a78bfa)';
            circleEl.textContent = '✓';
        }

        // Defer expensive header update to next frame to keep UI responsive
        if (this._pendingHeaderUpdate) {
            cancelAnimationFrame(this._pendingHeaderUpdate);
        }
        this._pendingHeaderUpdate = requestAnimationFrame(() => {
            this._pendingHeaderUpdate = null;
            this._updateBulkScrapHeader();
        });
    }

    /**
     * Update bulk scrap header visibility and content - optimized version
     */
    _updateBulkScrapHeader() {
        if (!this.bulkScrapHeaderEl) return;

        const count = this.selectedWaves.size;

        // Cache DOM references on first call for faster subsequent updates
        if (!this._bulkScrapDomCache) {
            this._bulkScrapDomCache = {
                infoEl: this.bulkScrapHeaderEl.querySelector('#bulk-scrap-info'),
                selectAllEl: this.bulkScrapHeaderEl.querySelector('#select-all-circle')
            };
        }
        const { infoEl, selectAllEl } = this._bulkScrapDomCache;
        const headerRow = this.wavesHeaderRowEl;

        // Get all packs for comparison
        const allPacks = this.gameState.getAvailablePacks();
        const totalPacks = allPacks.length;

        // Update select-all square state
        if (selectAllEl) {
            const allSelected = totalPacks > 0 && count === totalPacks;
            selectAllEl.style.background = allSelected ? 'var(--cf-lavender-dim, #5E4A6B)' : 'transparent';
            selectAllEl.style.borderColor = allSelected ? 'var(--cf-lavender, #a78bfa)' : 'var(--cf-text-tertiary, rgba(255,255,255,0.4))';
            selectAllEl.textContent = allSelected ? '✓' : '';
            selectAllEl.title = allSelected ? 'Deselect all' : 'Select all';
        }

        if (count === 0) {
            // Inactive state - show placeholder text, hide buttons
            if (headerRow) {
                headerRow.style.background = 'var(--cf-bg-primary, #0D0D12)';
            }
            if (infoEl) {
                infoEl.textContent = 'PICKUPS';
                infoEl.style.color = 'var(--cf-text-tertiary, #5C6170)';
            }
            return;
        }

        // Active state - show selection count and buttons
        if (headerRow) {
            headerRow.style.background = 'var(--cf-bg-raised, #16161E)';
        }

        // Calculate total scrap value using Map for O(1) lookup
        // For opened waves, use SCRAP BEST value (best item); for unopened, use wave scrap value
        let totalValue = 0;
        // Build a Map for fast lookup (only if we have selections)
        const packMap = new Map(allPacks.map(p => [p.milestone, p]));
        for (const milestone of this.selectedWaves) {
            const pack = packMap.get(milestone);
            if (pack) {
                if (pack.opened && pack.storedItems && pack.storedItems.length > 0) {
                    // For opened waves, calculate best item scrap value
                    let bestScrapValue = 0;
                    pack.storedItems.forEach(item => {
                        const value = this.gameState.getItemScrapValueDirect(item);
                        if (value > bestScrapValue) {
                            bestScrapValue = value;
                        }
                    });
                    totalValue += bestScrapValue;
                } else {
                    // For unopened waves, use wave scrap value
                    totalValue += this.gameState.getWaveScrapValue(pack);
                }
            }
        }

        // Update header content with inline buttons (matches DASH tab styling)
        if (infoEl) {
            this._clearChildren(infoEl);
            const countSpan = document.createElement('span');
            countSpan.style.color = 'var(--cf-text-secondary, #9BA1B0)';
            countSpan.textContent = `${count} selected`;
            infoEl.appendChild(countSpan);

            const scrapBtn = document.createElement('button');
            scrapBtn.id = 'wave-bulk-scrap-btn';
            scrapBtn.style.cssText = `
                margin-left: 12px;
                background: transparent;
                border: 1px solid var(--cf-negative, #E86A6A);
                color: var(--cf-negative, #E86A6A);
                padding: 6px 10px;
                font-size: 10px;
                text-transform: uppercase;
                letter-spacing: 0.05em;
                cursor: pointer;
                font-family: inherit;
                transition: all 0.1s;
            `;
            scrapBtn.textContent = `Ⓧ +${totalValue.toLocaleString()} ⚙`;
            scrapBtn.addEventListener('click', () => {
                this._showBulkScrapConfirmation(this._pendingBulkScrapHandlers);
            });
            infoEl.appendChild(scrapBtn);

            const cancelBtn = document.createElement('button');
            cancelBtn.id = 'wave-bulk-cancel-btn';
            cancelBtn.style.cssText = `
                margin-left: 6px;
                background: transparent;
                border: 1px solid var(--cf-text-tertiary, rgba(255,255,255,0.4));
                color: var(--cf-text-tertiary, rgba(255,255,255,0.4));
                width: 28px;
                height: 28px;
                padding: 0;
                font-size: 14px;
                cursor: pointer;
                font-family: inherit;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                vertical-align: middle;
            `;
            cancelBtn.textContent = '✕';
            cancelBtn.addEventListener('click', () => {
                this._deselectAllWaves();
            });
            infoEl.appendChild(cancelBtn);
        }
    }

    /**
     * Deselect all waves - optimized version
     */
    _deselectAllWaves() {
        this.selectedWaves.clear();

        // Update all selection squares
        const circles = document.querySelectorAll('.wave-select-circle');
        circles.forEach(circle => {
            circle.style.background = 'transparent';
            circle.style.borderColor = 'var(--cf-divider-light, #363C4D)';
            circle.textContent = '';
        });

        this._updateBulkScrapHeader();
    }

    /**
     * Toggle select all waves - if all selected, deselect all; otherwise select all
     */
    _toggleSelectAllWaves() {
        const allPacks = this.gameState.getAvailablePacks();
        const allMilestones = allPacks.map(p => p.milestone);
        const allSelected = allMilestones.length > 0 &&
            allMilestones.every(m => this.selectedWaves.has(m));

        if (allSelected) {
            // All selected - deselect all
            this._deselectAllWaves();
        } else {
            // Not all selected - select all
            this._selectAllWaves(allMilestones);
        }
    }

    /**
     * Select all waves
     * @param {number[]} milestones - Array of milestones to select
     */
    _selectAllWaves(milestones) {
        // Add all milestones to selection
        for (const milestone of milestones) {
            this.selectedWaves.add(milestone);
        }

        // Update all selection squares
        const circles = document.querySelectorAll('.wave-select-circle');
        circles.forEach(circle => {
            circle.style.background = 'var(--cf-lavender-dim, #5E4A6B)';
            circle.style.borderColor = 'var(--cf-lavender, #a78bfa)';
            circle.textContent = '✓';
        });

        this._updateBulkScrapHeader();
    }

    /**
     * Play celebration animation when opening a wave pack
     * Includes shake buildup, particle explosion, and sound
     * @param {HTMLElement} rowElement - The wave row element to animate
     * @param {string} tierColor - Color for the particles
     * @param {function} onComplete - Callback when animation completes
     */
    _playWaveOpenCelebration(rowElement, tierColor, onComplete) {
        this.waveOpeningInProgress = true;

        // Get the button for center of explosion
        const purchaseBtn = rowElement.querySelector('.cf-pack-purchase-btn');
        const btnRect = purchaseBtn ? purchaseBtn.getBoundingClientRect() : rowElement.getBoundingClientRect();
        const centerX = btnRect.left + btnRect.width / 2;
        const centerY = btnRect.top + btnRect.height / 2;

        // Phase 1: Shake buildup (intensifying shake for 400ms)
        const shakeDuration = 400;
        const shakeStartTime = performance.now();

        const shakeRow = () => {
            const elapsed = performance.now() - shakeStartTime;
            const progress = Math.min(elapsed / shakeDuration, 1);

            // Intensity ramps up from 0 to max
            const intensity = progress * 6; // Max 6px shake
            const shakeX = (Math.random() - 0.5) * 2 * intensity;
            const shakeY = (Math.random() - 0.5) * 2 * intensity * 0.5;

            rowElement.style.transform = `translate(${shakeX}px, ${shakeY}px)`;

            if (progress < 1) {
                requestAnimationFrame(shakeRow);
            } else {
                // Reset transform
                rowElement.style.transform = '';

                // Phase 2: Pop effect (scale up and back)
                rowElement.style.transition = 'transform 0.15s ease-out';
                rowElement.style.transform = 'scale(1.05)';

                setTimeout(() => {
                    rowElement.style.transform = 'scale(1)';

                    setTimeout(() => {
                        rowElement.style.transition = '';
                        rowElement.style.transform = '';

                        // Phase 3: Particle explosion
                        if (this.particles) {
                            this.particles.createParticles({
                                preset: 'celebration',
                                startX: centerX,
                                startY: centerY,
                                color: tierColor,
                                particleCount: 20,
                                particleSizeMin: 4,
                                particleSizeMax: 10,
                                initialSpeedMin: 100,
                                initialSpeedMax: 250,
                                flightDuration: 500,
                            });
                        }

                        // Play sound effect
                        this.sfxManager?.playSound('upgrade');

                        // Short delay then complete
                        setTimeout(() => {
                            this.waveOpeningInProgress = false;
                            if (onComplete) onComplete();
                        }, 300);
                    }, 150);
                }, 150);
            }
        };

        requestAnimationFrame(shakeRow);
    }

    /**
     * Show confirmation modal for bulk scrapping waves
     */
    _showBulkScrapConfirmation(parentOverlay) {
        const count = this.selectedWaves.size;
        if (count === 0) return;

        // Calculate total scrap value (wavedust)
        // For opened waves, use SCRAP BEST value (best item); for unopened, use wave scrap value
        let totalValue = 0;
        const allPacks = this.gameState.getAvailablePacks();
        const packMap = new Map(allPacks.map(p => [p.milestone, p]));

        for (const milestone of this.selectedWaves) {
            const pack = packMap.get(milestone);
            if (pack) {
                if (pack.opened && pack.storedItems && pack.storedItems.length > 0) {
                    // For opened waves, calculate best item scrap value
                    let bestScrapValue = 0;
                    pack.storedItems.forEach(item => {
                        const value = this.gameState.getItemScrapValueDirect(item);
                        if (value > bestScrapValue) {
                            bestScrapValue = value;
                        }
                    });
                    totalValue += bestScrapValue;
                } else {
                    // For unopened waves, use wave scrap value
                    totalValue += this.gameState.getWaveScrapValue(pack);
                }
            }
        }

        this.gameUI.modals.createConfirmation({
            title: 'Scrap Lots',
            message: `Scrap ${count} Lot${count > 1 ? 's' : ''} for ${totalValue.toLocaleString()} Salvage? This cannot be undone.`,
            confirmText: `Ⓧ SCRAP +${totalValue.toLocaleString()} ⚙`,
            cancelText: 'Cancel',
            confirmStyle: 'warning',
            onConfirm: () => {
                // Get bulk scrap button position BEFORE any DOM changes
                const bulkBtn = document.getElementById('wave-bulk-scrap-btn');
                const bulkBtnPos = bulkBtn
                    ? { x: bulkBtn.getBoundingClientRect().left + bulkBtn.getBoundingClientRect().width / 2, y: bulkBtn.getBoundingClientRect().top }
                    : null;

                // Scrap all selected waves
                const milestonesToScrap = [...this.selectedWaves];
                let successCount = 0;
                let totalWavedust = 0;

                for (const milestone of milestonesToScrap) {
                    const pack = packMap.get(milestone);
                    if (!pack) continue;

                    if (pack.opened && pack.storedItems && pack.storedItems.length > 0) {
                        // For opened waves, scrap the best item
                        let bestScrapValue = 0;
                        pack.storedItems.forEach(item => {
                            const value = this.gameState.getItemScrapValueDirect(item);
                            if (value > bestScrapValue) {
                                bestScrapValue = value;
                            }
                        });

                        // Apply scrap bonus multiplier
                        const multiplier = this.gameState.getScrapBonusMultiplier(true);
                        const value = Math.floor(bestScrapValue * multiplier);

                        // Add wavedust and remove wave
                        this.gameState.addWavedust(value);
                        this.gameState.confirmPackPurchase(milestone);

                        successCount++;
                        totalWavedust += value;
                    } else {
                        // For unopened waves, use regular scrap
                        const result = this.gameState.scrapWave(milestone);
                        if (result.success) {
                            successCount++;
                            totalWavedust += result.total;
                        }
                    }
                }

                // Show scrap popup and particles at button position
                if (successCount > 0 && bulkBtnPos) {
                    this.gameUI.sfxManager?.playSound('upgrade');

                    const bonusPercent = (this.gameState.getScrapBonusMultiplier() - 1) * 100;
                    this.gameUI.popups.showScrapWavedust(bulkBtnPos.x, bulkBtnPos.y, totalWavedust, bonusPercent);

                    // Purple scrap particles to wavedust display
                    const targetWavedustDisplay = this.menuWavedustDisplay;
                    if (targetWavedustDisplay && this.particles) {
                        this.particles.createParticles({
                            preset: 'scrap',
                            startX: bulkBtnPos.x,
                            startY: bulkBtnPos.y,
                            target: targetWavedustDisplay,
                            color: '#a78bfa', // Purple for wavedust
                        });
                    }

                    // Update wavedust display with animation
                    this.wavedustAnimator.targetValue = this.gameState.getWavedust();
                    this._startWavedustAnimation();
                }

                // Clear selection
                this.selectedWaves.clear();

                // Refresh waves content
                this.refreshWavesContent();

                // Update badge
                this.gameUI.updatePackNotificationBadge();
            }
        });
    }

    /**
     * Show confirmation dialog for scrapping a single item
     */
    _showItemScrapConfirmation(item, scrapValue, btnPos, container, parentOverlay) {
        this.gameUI.modals.createConfirmation({
            title: 'Scrap Item',
            message: `Scrap ${item.name} for ${scrapValue} Salvage? This cannot be undone.`,
            confirmText: `Ⓧ SCRAP +${scrapValue} ⚙`,
            cancelText: 'Cancel',
            confirmStyle: 'warning',
            onConfirm: () => {
                // Scrap the item (use inventorySystem if available, fallback to gameState)
                const result = this.inventorySystem
                    ? this.inventorySystem.scrapItem(item.id)
                    : this.gameState.scrapItem(item.id);
                if (result.success) {
                    this.gameUI.sfxManager?.playSound('upgrade');

                    // Show scrap popup
                    const bonusPercent = (this.gameState.getScrapBonusMultiplier() - 1) * 100;
                    this.gameUI.popups.showScrapWavedust(btnPos.x, btnPos.y, result.total, bonusPercent);

                    // Purple scrap particles to wavedust display
                    const targetWavedustDisplay = this.menuWavedustDisplay;
                    if (targetWavedustDisplay && this.particles) {
                        this.particles.createParticles({
                            preset: 'scrap',
                            startX: btnPos.x,
                            startY: btnPos.y,
                            target: targetWavedustDisplay,
                            color: '#a78bfa',
                        });
                    }

                    // Update wavedust display with animation
                    this.wavedustAnimator.targetValue = this.gameState.getWavedust();
                    this._startWavedustAnimation();

                    // Rebuild inventory
                    this._rebuildInventory(container, parentOverlay);
                }
            }
        });
    }

    /**
     * Create close button
     */
    _createCloseButton(onClose) {
        const btn = document.createElement('button');
        btn.textContent = '×';
        btn.className = 'cf-car-close';
        btn.addEventListener('click', onClose);
        return btn;
    }

    /**
     * Set up dynamic updates for miles and button states
     * Uses UIUpdateScheduler for centralized timer management (Phase 6 TMR-02)
     */
    _setupDynamicUpdates(milesDisplay, upgradeButtons) {
        const scheduler = getUIUpdateScheduler();
        scheduler.register(this._upgradeMenuUpdateId, () => {
            // Update miles display via animator
            const currentMiles = Math.floor(this.gameState.getMiles());
            if (currentMiles !== this.mileAnimator.targetValue) {
                this.mileAnimator.targetValue = currentMiles;
                this._startMileAnimation();
            }

            // Update upgrade button states
            upgradeButtons.forEach(({ button, type, carId, cost }) => {
                if (!button) return; // Skip if button doesn't exist

                let canAfford;
                if (type === 'ascend') {
                    canAfford = this.gameState.canAffordCar(carId);
                } else if (type === 'fuelTank') {
                    const tankCost = this.gameState.getFuelTankCost();
                    canAfford = tankCost !== null && this.gameState.state.miles >= tankCost;
                } else if (type === 'fuel') {
                    const maxAffordable = this.gameState.getMaxAffordableFuel();
                    canAfford = maxAffordable > this.gameState.getFuel();
                } else if (type === 'scrapBonus') {
                    const skillCost = this.gameState.getScrapBonusCost();
                    canAfford = skillCost !== null && this.gameState.state.miles >= skillCost;
                } else if (type === 'itemUpgradeBonus') {
                    const skillCost = this.gameState.getItemUpgradeBonusCost();
                    canAfford = skillCost !== null && this.gameState.state.miles >= skillCost;
                } else if (type === 'waveLuck') {
                    const skillCost = this.gameState.getWaveLuckCost();
                    canAfford = skillCost !== null && this.gameState.state.miles >= skillCost;
                } else {
                    canAfford = this.gameState.canAffordUpgrade(type);
                }

                // All buttons use CSS classes consistently
                const wasAffordable = button.classList.contains('can-afford');
                if (canAfford && !wasAffordable) {
                    button.classList.remove('cannot-afford');
                    button.classList.add('can-afford');
                    button.disabled = false;
                } else if (!canAfford && wasAffordable) {
                    button.classList.remove('can-afford');
                    button.classList.add('cannot-afford');
                    button.disabled = true;
                }
            });

            // Update item upgrade button states
            document.querySelectorAll('button[data-button-type="itemUpgrade"]').forEach(button => {
                const itemId = button.dataset.itemId;
                if (!itemId) return;

                const canAfford = this.gameState.canAffordItemUpgrade(itemId);
                const isCurrentlyDisabled = button.classList.contains('cannot-afford');

                if (canAfford && isCurrentlyDisabled) {
                    button.classList.remove('cannot-afford');
                    button.classList.add('can-afford');
                } else if (!canAfford && !isCurrentlyDisabled) {
                    button.classList.remove('can-afford');
                    button.classList.add('cannot-afford');
                }
            });

            // Update wave pack purchase button states
            document.querySelectorAll('button[data-button-type="wavePack"]').forEach(button => {
                const cost = parseInt(button.dataset.packCost);
                const canAfford = currentWavedust >= cost;
                const isCurrentlyDisabled = button.classList.contains('cannot-afford');

                if (canAfford && isCurrentlyDisabled) {
                    button.classList.remove('cannot-afford');
                    button.classList.add('can-afford');
                } else if (!canAfford && !isCurrentlyDisabled) {
                    button.classList.remove('can-afford');
                    button.classList.add('cannot-afford');
                }
            });
        }, { interval: 100 });
    }

    // ========== Tier Upgrade Modal ==========

    /**
     * Calculate speed stats at a given tier multiplier (for modal comparison)
     * Only speed-based stats are affected by tier bonuses
     * Uses same calculation as StatCalculator for consistency
     */
    _calculateStatsAtTier(tierMultiplier) {
        const car = this.gameState.getCurrentCar();
        const carConfig = this.gameState.carsConfig[this.gameState.state.currentCarId];

        // Helper to calculate stat value at a specific tier multiplier
        // Matches StatCalculator logic: (base + upgrades) + tierBonus
        // where tierBonus = (base + upgrades) * (tierMultiplier - 1)
        const getStatAtTier = (type) => {
            const level = car.upgradeLevels[type] || 0;
            let baseValue, upgradeBonus;

            switch (type) {
                case 'baseSpeed': {
                    const increment = (carConfig.maxBaseSpeed - carConfig.baseSpeed) / 10;
                    baseValue = carConfig.baseSpeed;
                    upgradeBonus = level * increment;
                    break;
                }
                case 'acceleration': {
                    const increment = (carConfig.maxAcceleration - carConfig.baseAcceleration) / 10;
                    baseValue = carConfig.baseAcceleration;
                    upgradeBonus = level * increment;
                    break;
                }
                case 'maxSpeed': {
                    const increment = carConfig.maxSpeed / 10;
                    baseValue = carConfig.maxSpeed;
                    upgradeBonus = level * increment;
                    break;
                }
                default:
                    return 0;
            }

            const beforeTier = baseValue + upgradeBonus;
            const tierBonus = beforeTier * (tierMultiplier - 1);
            return beforeTier + tierBonus;
        };

        return {
            baseSpeed: getStatAtTier('baseSpeed'),
            acceleration: getStatAtTier('acceleration'),
            maxSpeed: getStatAtTier('maxSpeed')
        };
    }

    /**
     * Show the tier upgrade celebration modal
     */
    _showTierUpgradeModal({ oldTier, newTier, oldMultiplier, newMultiplier }) {
        this.tierModalActive = true;

        const oldStats = this._calculateStatsAtTier(oldMultiplier);
        const newStats = this._calculateStatsAtTier(newMultiplier);
        const bonusPercent = ((newMultiplier / oldMultiplier) - 1) * 100;

        const carConfig = this.gameState.carsConfig[this.gameState.state.currentCarId];
        const carName = carConfig.name.toUpperCase();
        const oldTierColor = this.gameState.getTierColorByName(oldTier);
        const newTierColor = this.gameState.getTierColorByName(newTier);

        // Create overlay
        const overlay = document.createElement('div');
        overlay.className = 'cf-ui cf-tier-modal-overlay';
        overlay.id = 'tier-upgrade-modal';

        // Create modal
        const modal = document.createElement('div');
        modal.className = 'cf-tier-modal';

        // Header
        const header = document.createElement('div');
        header.className = 'cf-tier-modal-header';
        const headerTitle = document.createElement('span');
        headerTitle.className = 'cf-title';
        headerTitle.textContent = 'CONDITION UPGRADED';
        header.appendChild(headerTitle);

        // Car info section
        const carSection = document.createElement('div');
        carSection.className = 'cf-tier-modal-car';
        const vehicleName = document.createElement('div');
        vehicleName.className = 'cf-vehicle-name';
        vehicleName.textContent = carName;
        const tierTransition = document.createElement('div');
        tierTransition.className = 'cf-tier-transition';
        const oldTierSpan = document.createElement('span');
        oldTierSpan.className = 'cf-tier-old';
        oldTierSpan.style.color = oldTierColor;
        oldTierSpan.textContent = oldTier.toUpperCase();
        const arrowText = document.createElement('span');
        arrowText.className = 'cf-muted';
        arrowText.textContent = ' → ';
        const newTierSpan = document.createElement('span');
        newTierSpan.className = 'cf-tier-new';
        newTierSpan.style.color = newTierColor;
        newTierSpan.textContent = newTier.toUpperCase();
        tierTransition.appendChild(oldTierSpan);
        tierTransition.appendChild(arrowText);
        tierTransition.appendChild(newTierSpan);
        carSection.appendChild(vehicleName);
        carSection.appendChild(tierTransition);

        // Stats header
        const statsHeader = document.createElement('div');
        statsHeader.className = 'cf-tier-modal-stats-header';
        const statsLabel = document.createElement('span');
        statsLabel.textContent = 'STAT BONUSES';
        const tierBonus = document.createElement('span');
        tierBonus.className = 'cf-tier-bonus';
        tierBonus.textContent = `+${bonusPercent.toFixed(1)}% TIER BONUS`;
        statsHeader.appendChild(statsLabel);
        statsHeader.appendChild(tierBonus);

        // Stats section
        const statsSection = document.createElement('div');
        statsSection.className = 'cf-tier-modal-stats';

        // Helper to render a stat row
        const renderStatRow = (label, oldVal, newVal, unit, hasTierBonus) => {
            const row = document.createElement('div');
            row.className = 'cf-tier-stat-row';

            // Build stat label
            const labelSpan = document.createElement('span');
            labelSpan.className = 'cf-stat-label';
            labelSpan.textContent = label;

            // Build stat values container
            const valuesSpan = document.createElement('span');
            valuesSpan.className = 'cf-stat-values';
            const oldSpan = document.createElement('span');
            oldSpan.className = 'cf-stat-old';
            oldSpan.textContent = oldVal.toFixed(1);
            const arrowSpan = document.createElement('span');
            arrowSpan.className = 'cf-muted';
            arrowSpan.textContent = ' → ';
            const newSpan = document.createElement('span');
            newSpan.className = 'cf-stat-new';
            newSpan.textContent = newVal.toFixed(1);
            const unitSpan = document.createElement('span');
            unitSpan.className = 'cf-stat-unit cf-muted';
            unitSpan.textContent = unit;
            valuesSpan.appendChild(oldSpan);
            valuesSpan.appendChild(arrowSpan);
            valuesSpan.appendChild(newSpan);
            valuesSpan.appendChild(unitSpan);

            // Build change indicator
            const changeSpan = document.createElement('span');
            changeSpan.className = hasTierBonus ? 'cf-stat-change' : 'cf-stat-change cf-muted';
            changeSpan.textContent = hasTierBonus ? `+${bonusPercent.toFixed(1)}%` : '—';

            row.appendChild(labelSpan);
            row.appendChild(valuesSpan);
            row.appendChild(changeSpan);
            return row;
        };

        // Speed-based stats (the only stats affected by tier bonus)
        statsSection.appendChild(renderStatRow('IDLE SPEED', oldStats.baseSpeed, newStats.baseSpeed, 'MPH', true));
        statsSection.appendChild(renderStatRow('ACCELERATION', oldStats.acceleration, newStats.acceleration, 'MPH/CLICK', true));
        statsSection.appendChild(renderStatRow('MAX SPEED', oldStats.maxSpeed, newStats.maxSpeed, 'MPH', true));

        // Footer with continue button
        const footer = document.createElement('div');
        footer.className = 'cf-tier-modal-footer';

        const continueBtn = document.createElement('button');
        continueBtn.className = 'cf-button cf-tier-continue';
        continueBtn.textContent = 'CONTINUE';
        continueBtn.addEventListener('click', () => this._closeTierModal());
        footer.appendChild(continueBtn);

        // Assemble modal
        modal.appendChild(header);
        modal.appendChild(carSection);
        modal.appendChild(statsHeader);
        modal.appendChild(statsSection);
        modal.appendChild(footer);
        overlay.appendChild(modal);

        // Add keyboard handler for Enter/Space
        this._tierModalKeyHandler = (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this._closeTierModal();
            }
        };
        document.addEventListener('keydown', this._tierModalKeyHandler);

        // Add to DOM
        document.body.appendChild(overlay);

        // Focus the continue button for accessibility
        continueBtn.focus();
    }

    /**
     * Close the tier upgrade modal
     */
    _closeTierModal() {
        this.tierModalActive = false;

        // Remove keyboard handler
        if (this._tierModalKeyHandler) {
            document.removeEventListener('keydown', this._tierModalKeyHandler);
            this._tierModalKeyHandler = null;
        }

        // Remove modal from DOM
        const modal = document.getElementById('tier-upgrade-modal');
        if (modal) {
            modal.remove();
        }

        // Trigger stat recalculation to update tooltips with new tier values
        // The stat invalidation was skipped during animation, so we need to trigger it now
        if (this.gameState.statCalculator) {
            this.gameState.statCalculator.invalidateAll();
        }
    }
}
